import React, { useState, useEffect, useRef, useMemo } from "react";
import { IP } from './configuracion.js';
import { Routes, Route } from "react-router-dom";
import { PropertyProvider } from "./context/PropertyContext";
import Login from "./components/Login";
import Registro from "./components/Registro";
import KDS from "./components/KDS";
import Kioscos from "./components/Kioscos";
import GroupList from "./components/GroupList.js";
import CategoryList from "./components/CategoryList.js";
import ProductList from "./components/ProductList";
import ModifiersModal from "./components/ModifiersModal";
import OrderSummary from "./components/OrderSummary";
import { useHandlePedido } from "./utils/insertar_pedido";
import QuantityInput from "./components/QuantityInput";
import PaymentInputModal from "./components/PaymentInputModal";
import RevenueSelector from "./components/RevenueSelector";
import TransferTableSelector from "./components/TransferTableSelector";
import TransferMeseroSelector from "./components/TransferMeseroSelector";
// Botón "Promo" (catálogo lista_promos.json). Componente aislado e
// independiente de Descuento/Descuento Abierto — ver PromoSelector.js.
import PromoSelector from "./components/PromoSelector";
import ProductSearchPanel from "./components/ProductSearchPanel";
import TransferPedidoSelector from "./components/TransferPedidoSelector";
import VentaOtroRevenueSelector from "./components/VentaOtroRevenueSelector";
import ReimprimirTicket from "./components/ReimprimirTicket";
import ChequeList from "./components/ChequeList";
import VoidReasonsModal from "./components/VoidReasonsModal";
import SegmentoModal from "./components/SegmentoModal";
import DiscountModal from "./components/DiscountModal";
import OpenDiscountModal from "./components/OpenDiscountModal";
import ControlFecha from "./components/ControlFecha";
import CorteDeCaja from "./components/CorteDeCaja";
import CorteAdmin from "./components/CorteAdmin";
import BarcodeInventoryScanner from "./components/BarcodeInventoryScanner";
import ReporteVentas from "./components/ReporteVentas";
import ResetConfig from "./components/ResetConfig";
import VirtualKeyboard from "./components/VirtualKeyboard";
import KdsReadyNotifier from "./components/KdsReadyNotifier";
import "./styles/App.css"; // CSS original para escritorio/general
import "./styles/app_movil.css"; // Nuevo CSS solo para móviles
import { FaSignOutAlt } from "react-icons/fa";
import { printTicket } from "./utils/printers/PrinterManager"; // Nueva importación
import {
  removeFromOrder,
  updateQuantity,
  handlePaymentConfirm,
  handleRemovePayment,
} from "./utils/orderFunctions";
import {
  leerMemoria,
  crearNuevoCheque,
  agregarPedidoACheque,
  buscarChequeAbierto,
} from "./components/transaccion";
import OpenPriceModal from "./components/OpenPriceModal";
import OpenNoteModal from "./components/OpenNoteModal";
import { saveConfigBackup, loadConfigBackup } from "./utils/configBackup";
// ── Tiempos de Servicio ──────────────────────────────────────────────────────
import TiempoServicioPanel from "./components/TiempoServicioPanel";
import Hotel from "./components/Hotel";
import { LanguageProvider, useLanguage } from "./context/LanguageContext";
import CancelChequeModal from "./components/CancelChequeModal";
const BASE_API_URL = `${IP}/tavpos/backend`;
const BASE_API_JSON = `${IP}/tavpos/backend/data`;
const LOCAL_API_URL = `${IP}/tavpos/backend`;
const DEFAULT_BASE_DATA_URL = `${IP}/tavpos/backend`;
const dataCache = new Map();
// Referencia estable para el caso "sin acciones permitidas todavía" — evitar
// crear un [] nuevo en cada render (ver useMemo de allowedActions más abajo).
const EMPTY_ACTIONS = [];
const FACTURACION_URL = 'https://www.bts.ei-solutions.co/shopeis/app_menu/facturacion_tav.php';

// ✅ OPTIMIZACIÓN: se reutiliza una sola conexión IndexedDB en vez de abrir
// una nueva en cada llamada a getFromIndexedDB/saveToIndexedDB/deleteFromIndexedDB.
// Con 7-8 archivos por carga de propiedad, esto evitaba abrir la conexión
// una y otra vez. Misma interfaz (openDB() sigue devolviendo una Promise<db>),
// así que ningún llamador necesita cambiar. Si la conexión falla o se cierra,
// dbPromise se limpia para permitir reintentar en la siguiente llamada.
let dbPromise = null;

const openDB = () => {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open("POSDatabase", 1);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      db.createObjectStore("data", { keyPath: "key" });
    };
    request.onsuccess = (event) => {
      const db = event.target.result;
      db.onclose = () => { dbPromise = null; };
      resolve(db);
    };
    request.onerror = (event) => {
      dbPromise = null;
      reject(event.target.error);
    };
  });

  return dbPromise;
};

const saveToIndexedDB = async (key, data) => {
  try {
    const db = await openDB();
    const tx = db.transaction("data", "readwrite");
    const store = tx.objectStore("data");
    await store.put({ key, data });
    return new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (error) {
    console.error(`Error saving to IndexedDB for ${key}:`, error);
  }
};

const getFromIndexedDB = async (key) => {
  try {
    const db = await openDB();
    const tx = db.transaction("data", "readonly");
    const store = tx.objectStore("data");
    const request = await store.get(key);
    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result?.data);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error(`Error getting from IndexedDB for ${key}:`, error);
    return null;
  }
};

const fetchData = async (baseDataUrl, property, fileName, errorMessage, revenueId = null) => {
  const cacheKey = `${property}/${fileName}`;
  const isProductFile = fileName.includes("products_") && revenueId;
  const isDeviceFile  = fileName === "devices.json";

  // ── Utilidad: determina si un dato es considerado "vacío" ──────────
  const isEmpty = (d) => {
    if (d === null || d === undefined) return true;
    if (Array.isArray(d))             return d.length === 0;
    if (typeof d === 'object')        return Object.keys(d).length === 0;
    return false;
  };

  // ── VERIFICAR CACHÉ (lógica corregida por tipo de archivo) ─────────
  if (!isProductFile) {
    const cachedData = await getFromIndexedDB(cacheKey);

    if (isDeviceFile) {
      // devices.json: solo usar si realmente tiene dispositivos
      if (cachedData && cachedData.devices?.length > 0) {
        console.log(`[fetchData] ${fileName} desde IDB (${cachedData.devices.length} devices)`);
        dataCache.set(cacheKey, cachedData);
        return cachedData;
      }
      if (cachedData) await deleteFromIndexedDB(cacheKey); // vacío → borrar

    } else if (cachedData && !isEmpty(cachedData)) {
      // employees_sec.json, config.json, promos.json, payments.json, revenues.json, etc.
      // Cualquier dato no-vacío es válido para caché
      console.log(`[fetchData] ${fileName} desde IDB`);
      dataCache.set(cacheKey, cachedData);
      return cachedData;
    } else if (cachedData) {
      await deleteFromIndexedDB(cacheKey); // vacío → borrar
    }
  }

  // ── FETCH DESDE RED ────────────────────────────────────────────────
  const url = `${baseDataUrl}/${property}/${fileName}`;
  console.log(`[fetchData] Solicitando URL: ${url}`);

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "include",
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => "No response body");
      throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
    }

    const text = await response.text();
    if (!text.trim() || text.trim().startsWith("<!DOCTYPE") || text.trim().startsWith("<html")) {
      throw new Error("Respuesta inválida: HTML o vacía");
    }

    let data;
    try {
      data = JSON.parse(text);
    } catch (jsonError) {
      console.error(`[fetchData] Error al parsear JSON:`, jsonError);
      throw new Error("Formato JSON inválido");
    }

    if (isDeviceFile) {
      const allDevices = Array.isArray(data) ? data : data.devices || [];
      const filteredDevices = allDevices.filter(
        (device) => device.Propiedad && device.Propiedad.toLowerCase() === property.toLowerCase()
      );
      const result = { devices: filteredDevices };
      if (filteredDevices.length === 0) {
        console.warn(`[fetchData] No se encontraron dispositivos para ${property}`);
      }
      dataCache.set(cacheKey, result);
      await saveToIndexedDB(cacheKey, result);
      return result;

    } else if (isProductFile) {
      const products = Array.isArray(data) ? data : data[`products_${revenueId}`] || [];
      dataCache.set(cacheKey, products);
      await saveToIndexedDB(cacheKey, products);
      return products;
    }

    dataCache.set(cacheKey, data);
    await saveToIndexedDB(cacheKey, data);
    console.log(`[fetchData] ${fileName} cargado y cacheado`);
    return data;

  } catch (error) {
    console.error(`[fetchData] Error de red al cargar ${fileName}:`, error);

    // ── FALLBACK: si la red falla, intentar datos en memoria o IDB ────
    // Importante en móvil con conexión inestable
    if (dataCache.has(cacheKey)) {
      console.warn(`[fetchData] Usando datos en memoria (fallback) para ${fileName}`);
      return dataCache.get(cacheKey);
    }
    try {
      const stale = await getFromIndexedDB(cacheKey);
      if (stale && !isEmpty(stale)) {
        console.warn(`[fetchData] Usando caché IDB desactualizado (fallback) para ${fileName}`);
        dataCache.set(cacheKey, stale);
        return stale;
      }
    } catch { /* ignorar */ }

    throw new Error(errorMessage || `Error cargando ${fileName}: ${error.message}`);
  }
};

const deleteFromIndexedDB = async (key) => {
  try {
    const db = await openDB();
    const tx = db.transaction("data", "readwrite");
    const store = tx.objectStore("data");
    await store.delete(key);
    await tx.complete;
    console.log(`[deleteFromIndexedDB] Clave ${key} eliminada`);
  } catch (error) {
    console.error(`[deleteFromIndexedDB] Error eliminando ${key}:`, error);
  }
};

export const getProperty = () => {
  return localStorage.getItem("currentProperty");
};

// Versión CORREGIDA que convierte a número decimal
const getSafeValue = (value, defaultValue = 0) => {
  // Si es undefined o null, retornar valor por defecto
  if (value === undefined || value === null) return defaultValue;
  
  // Si ya es número, retornarlo directamente
  if (typeof value === 'number') return value;
  
  // Si es string, convertirlo a número respetando decimales
  if (typeof value === 'string') {
    // Reemplazar coma por punto (formato europeo)
    const normalizedValue = value.replace(',', '.');
    const parsed = parseFloat(normalizedValue);
    return isNaN(parsed) ? defaultValue : parsed;
  }
  
  // Para cualquier otro tipo, intentar convertir
  const parsed = parseFloat(value);
  return isNaN(parsed) ? defaultValue : parsed;
};

const calculateModifiersOriginalTotal = (modifiers) => {
  if (!Array.isArray(modifiers)) return 0;
  
  return modifiers.reduce((sum, modifier) => {
    // Asegurar que los valores sean números decimales
    const modifierPrice = parseFloat(modifier.price) || 0;
    const modifierQuantity = parseFloat(modifier.quantity) || 1;
    
    // Calcular total respetando decimales
    const total = modifierPrice * modifierQuantity;
    
    console.log("Modifier original:", {
      name: modifier.name,
      price: modifierPrice,
      quantity: modifierQuantity,
      total
    });
    
    return sum + total;
  }, 0);
};

const calculateModifiersDiscountedTotal = (modifiers) => {
  if (!Array.isArray(modifiers)) return 0;
  
  return modifiers.reduce((sum, modifier) => {
    // Asegurar que los valores sean números decimales
    const modifierPrice = parseFloat(modifier.price) || 0;
    const modifierQuantity = parseFloat(modifier.quantity) || 1;
    const modifierDiscount = parseFloat(modifier.monto_descuento) || 0;
    const modifierPercentageDiscount = parseFloat(modifier.descuento_porcentaje) || 0;
    
    // Calcular subtotal del modificador
    const modifierSubtotal = modifierPrice * modifierQuantity;
    
    // Calcular descuento porcentual si existe
    const percentageDiscountAmount = (modifierSubtotal * modifierPercentageDiscount) / 100;
    
    // Total de descuentos (fijo + porcentual)
    const totalDiscount = modifierDiscount + percentageDiscountAmount;
    
    // Subtotal después de descuentos
    const subtotalAfterDiscount = modifierSubtotal - totalDiscount;
    
    console.log("Modifier discounted:", {
      name: modifier.name,
      price: modifierPrice,
      quantity: modifierQuantity,
      subtotal: modifierSubtotal,
      discount: modifierDiscount,
      percentageDiscount: modifierPercentageDiscount,
      percentageAmount: percentageDiscountAmount,
      totalDiscount,
      finalTotal: subtotalAfterDiscount
    });
    
    return sum + subtotalAfterDiscount;
  }, 0);
};

// Versión CORREGIDA en App.js
const calculateItemTotal = (item) => {
  console.log("calculateItemTotal - item:", JSON.stringify(item, null, 2));
  
  // Usar valores absolutos para cálculos pero mantener el signo para el total final
  const modifiersOriginalTotal = calculateModifiersOriginalTotal(item.modifiers);
  const modifiersDiscountedTotal = calculateModifiersDiscountedTotal(item.modifiers);
  const itemPrice = Math.abs(getSafeValue(item.price));
  const itemQuantity = getSafeValue(item.quantity, 1);
  const quantitySign = Math.sign(itemQuantity); // Para mantener el signo del total

  const baseSubtotal = (itemPrice + modifiersOriginalTotal) * Math.abs(itemQuantity);

  const dbDiscount = getSafeValue(item.monto_descuento, 0);
  const manualDiscount = getSafeValue(item.descuento_manual, 0);
  const percentageDiscount = getSafeValue(item.discount?.price, 0);

  const percentageDiscountAmount = (Math.abs(baseSubtotal) * percentageDiscount) / 100;
  const modifiersDiscounts = modifiersOriginalTotal - modifiersDiscountedTotal;
  const totalDiscount = dbDiscount + manualDiscount + percentageDiscountAmount + modifiersDiscounts;

  const subtotalAfterDiscount = baseSubtotal - totalDiscount;

  // ✅ CORREGIDO: Usar el tax del producto (0 si no existe, NO 16 fijo)
  const taxRate = getSafeValue(item.tax, 0) / 100; // Cambiado de 16 a 0
  const serviceRate = getSafeValue(item.cargo, 0) / 100;

  // Si no hay impuestos, el subtotalBeforeTaxes es el mismo que subtotalAfterDiscount
  const totalTaxRate = taxRate + serviceRate;
  const subtotalBeforeTaxes = totalTaxRate > 0 
    ? subtotalAfterDiscount / (1 + totalTaxRate)
    : subtotalAfterDiscount; // Si no hay impuestos, no dividir
  
  const taxAmount = subtotalBeforeTaxes * taxRate;
  const cargoAmount = subtotalBeforeTaxes * serviceRate;

  const totalWithTaxes = (subtotalBeforeTaxes + taxAmount + cargoAmount) * quantitySign;

  console.log("calculateItemTotal - resultado:", {
    itemTax: item.tax, // Mostrar el tax original
    taxRate: taxRate * 100, // Mostrar como porcentaje
    baseSubtotal,
    totalDiscount,
    subtotalBeforeTaxes,
    taxAmount,
    cargoAmount,
    totalWithTaxes,
    quantitySign
  });

  return parseFloat(totalWithTaxes.toFixed(2));
};

const calculateTotal = (order) => order.reduce((total, item) => total + calculateItemTotal(item), 0);

const RevenueInfo = ({
  selectedRevenue,
  selectedSpace,
  clientCount,
  selectedClient,
  setSelectedClient,
  chequeSeleccionado,
  property,
  currentUser,
  setOrder,
  mostrarMensaje,
  setPaymentMethod,
  deviceConfig,
  devices,
  order,
  printTicket,
  generarQR,
  customerNamePending = null,
  nombreVendedor = null,
  isPanelMode = false, // Nueva prop para modo panel
  allowedActions = []  // Permisos del usuario actual
}) => {
  const [showClientOptions, setShowClientOptions] = useState(null);
  const longPressTimer = useRef(null);
  const longPressDuration = 800;
  const isLongPress = useRef(false);
  // ── División de cuentas ──
  const [isDividirMode, setIsDividirMode] = useState(false);
  const [selectedClientsForDivision, setSelectedClientsForDivision] = useState(new Set());
  const [roomNumber, setRoomNumber] = useState(null);
  const [roomInfo, setRoomInfo] = useState(null);
  
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (showClientOptions !== null && !e.target.closest('.client-button-container')) {
        setShowClientOptions(null);
      }
    };
    document.addEventListener('touchstart', handleOutsideClick);
    document.addEventListener('click', handleOutsideClick);
    return () => {
      document.removeEventListener('touchstart', handleOutsideClick);
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [showClientOptions]);
  
  const imprimirCuentaCliente = async (chequeId, clientIndex) => {
    try {
      console.log('[imprimirCuentaCliente] Iniciando impresión para cliente:', clientIndex + 1);
      
      if (!chequeId) {
        throw new Error('No se proporcionó un ID de cheque válido');
      }

      const clientProducts = order.filter(item => item.client === clientIndex);
      if (clientProducts.length === 0) {
        throw new Error(`No hay productos para el cliente ${clientIndex + 1}`);
      }
      
      const total = clientProducts.reduce((sum, item) => sum + (item.price * item.quantity), 0);

      const result = await printTicket(
        clientProducts,
        total,
        [],
        property,
        deviceConfig,
        selectedRevenue,
        { ...selectedSpace, name: `${selectedSpace.name} (Cliente ${clientIndex + 1})` },
        chequeId,
        currentUser,
        devices,
        {
          isCuentaCliente: true,
          cliente: clientIndex + 1,
          DeviceID: deviceConfig?.PRNTT || 'tavpos',
          USB: { Tipo: 'USB' }
        }
      );

      if (!result.success) {
        throw new Error(result.message || 'Error desconocido al imprimir');
      }

      mostrarMensaje(`✅ Ticket impreso para cliente ${clientIndex + 1}`);
    } catch (error) {
      console.error('[imprimirCuentaCliente] Error:', error);
      mostrarMensaje(`⚠️ Error al imprimir: ${error.message}`);
    }
  };

  const crearCuentaCliente = async (clientIndex) => {
    try {
      if (!chequeSeleccionado) throw new Error('No hay cheque seleccionado');
      if (!property) throw new Error('Propiedad no definida');

      const response = await fetch(`${LOCAL_API_URL}/actualizar-cheque-cliente.php?property=${encodeURIComponent(property)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chequeOriginal: chequeSeleccionado,
          cliente: clientIndex + 1,
          usuario: currentUser.nombre
        })
      });

      const data = await response.json();
      if (!data.success) throw new Error(data.message || 'Error al crear la cuenta');

      setOrder(prev => prev.map(item => 
        item.client === clientIndex ? 
        { ...item, numero_cheque: data.chequeId } : 
        item
      ));

      setPaymentMethod({
        name: `Crear Cuenta Cliente ${clientIndex + 1}`,
        customPayment: true,
        chequeId: data.chequeId,
        cliente: clientIndex,
        total: data.total
      });

      setShowClientOptions(null);
      mostrarMensaje(`✅ Cuenta creada para cliente ${clientIndex + 1}`);
    } catch (error) {
      console.error('[crearCuentaCliente] Error:', error);
      mostrarMensaje(`⚠️ Error al crear cuenta: ${error.message}`);
    }
  };

  // ── Helper interno para bulk (sin activar flujo de pago) ──
  const _crearCuentaClienteBulk = async (clientIndex) => {
    const response = await fetch(`${LOCAL_API_URL}/actualizar-cheque-cliente.php?property=${encodeURIComponent(property)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        chequeOriginal: chequeSeleccionado,
        cliente: clientIndex + 1,
        usuario: currentUser.nombre
      })
    });
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Error al crear cuenta');
    return data;
  };

  // ── Dividir cuenta: crea cheque separado para cada cliente seleccionado ──
  const dividirCuentaSeleccionados = async () => {
    if (!chequeSeleccionado) { mostrarMensaje('No hay cheque seleccionado.'); return; }
    if (selectedClientsForDivision.size === 0) { mostrarMensaje('Selecciona al menos un cliente.'); return; }
    if (selectedClientsForDivision.size >= clientCount) {
      mostrarMensaje('Debe quedar al menos un cliente en la cuenta original.');
      return;
    }
    const indices = Array.from(selectedClientsForDivision).sort((a, b) => a - b);
    let exitosos = 0;
    for (const idx of indices) {
      const clientProducts = order.filter(item => item.client === idx);
      if (clientProducts.length === 0) continue;
      try {
        const data = await _crearCuentaClienteBulk(idx);
        setOrder(prev => prev.map(item =>
          item.client === idx ? { ...item, numero_cheque: data.chequeId } : item
        ));
        exitosos++;
      } catch (error) {
        mostrarMensaje(`⚠️ Error en cliente ${idx + 1}: ${error.message}`);
      }
    }
    if (exitosos > 0) mostrarMensaje(`✅ ${exitosos} cuenta(s) dividida(s). Selecciona más clientes para continuar.`);
    // Mantiene el modo dividir activo para permitir divisiones consecutivas
    setSelectedClientsForDivision(new Set());
  };

  // ── Cuentas Separadas: crea un cheque individual para CADA cliente ──
  const cuentasSeparadasTodas = async () => {
    if (!chequeSeleccionado) { mostrarMensaje('No hay cheque seleccionado.'); return; }
    if (clientCount < 2) { mostrarMensaje('Se necesitan al menos 2 clientes.'); return; }
    let exitosos = 0;
    for (let i = 0; i < clientCount; i++) {
      const clientProducts = order.filter(item => item.client === i);
      if (clientProducts.length === 0) continue;
      try {
        const data = await _crearCuentaClienteBulk(i);
        setOrder(prev => prev.map(item =>
          item.client === i ? { ...item, numero_cheque: data.chequeId } : item
        ));
        exitosos++;
      } catch (error) {
        mostrarMensaje(`⚠️ Error en cliente ${i + 1}: ${error.message}`);
      }
    }
    if (exitosos > 0) mostrarMensaje(`✅ ${exitosos} cuenta(s) separada(s) creadas.`);
  };

  const handleClientClick = (index, e) => {
    if (e.target.closest('.client-options-btn') || isLongPress.current) {
      isLongPress.current = false;
      return;
    }
    setSelectedClient(index);
    setShowClientOptions(null);
    mostrarMensaje(`Seleccionado Asiento ${index + 1}`);
  };

  const handleLongPressStart = (index, e) => {
    if (!chequeSeleccionado) {
      mostrarMensaje('No hay cheque seleccionado para abrir opciones.');
      return;
    }

    // Solo prevenir en touch, no en mouse
    if (e.type === "touchstart") {
      e.preventDefault();
    }

    setShowClientOptions(null);

    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
    }

    isLongPress.current = false;

    longPressTimer.current = setTimeout(() => {
      isLongPress.current = true;
      setShowClientOptions(index);
      setSelectedClient(index);
      mostrarMensaje(`Opciones abiertas para Asiento ${index + 1}`);
    }, longPressDuration || 500);
  };

  const handleLongPressEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  // ===== FUNCIÓN PARA OBTENER LA ETIQUETA DEL ESPACIO SEGÚN EL TIPO =====
  // Hacemos la comparación insensible a mayúsculas/minúsculas
  const getSpaceLabel = () => {
    const tipoRevenue = (selectedRevenue?.Tipo || "").toUpperCase();
    if (tipoRevenue === "ROOM") {
      return "Habitación";
    } else if (tipoRevenue === "TABLE") {
      return "Mesa";
    } else {
      return "Salón";
    }
  };

  // MODO PANEL - Renderiza los botones de clientes para el panel central
  if (isPanelMode) {
    return (
      <div className="clientes-buttons-container">

        {/* ── Botones de clientes ── */}
        {Array.from({ length: clientCount }, (_, index) => (
          <div key={index} className="client-button-container">
            <button
              className={`client-btn${selectedClient === index ? ' selected' : ''}`}
              onClick={() => {
                setSelectedClient(index);
                mostrarMensaje(`Seleccionado Asiento ${index + 1}`);
              }}
              style={{ touchAction: 'manipulation', userSelect: 'none' }}
            >
              Cte {index + 1}
            </button>
          </div>
        ))}

      </div>
    );
  }

  // MODO HEADER - Renderiza la vista original del header con etiqueta dinámica
  return (
    <div className="revenue-info">
      <div className="revenue-details">
        <p><strong>Revenue:</strong> {selectedRevenue?.name || 'No seleccionado'}</p>
        {selectedRevenue?.Tipo !== 'Retail' && (
          <>
            <p><strong>{getSpaceLabel()}:</strong> {selectedSpace?.name || 'No especificado'}</p>
            <p><strong>Clientes:</strong> {clientCount || 'No especificado'}</p>
            {customerNamePending && (
              <p><strong>Cliente:</strong> {customerNamePending}</p>
            )}
          </>
        )}
        <p><strong>Cheque:</strong> {chequeSeleccionado || 'Ninguno'}</p>
        {nombreVendedor && (
          <p><strong>Vendedor:</strong> {nombreVendedor}</p>
        )}
        {!chequeSeleccionado && (
          <p className="no-check-warning">.</p>
        )}
      </div>
      {/* LOS BOTONES DE CLIENTES SE RENDERIZAN EN EL MODO PANEL */}
    </div>
  );
};

// ═══════════════════════════════════════════════════════
// MODAL: SEPARAR CUENTAS
// ═══════════════════════════════════════════════════════
const SepararCuentasModal = ({
  clientCount,
  order,
  chequeSeleccionado,
  revenueId,
  property,
  currentUser,
  setOrder,
  mostrarMensaje,
  onClose,
  onSuccess
}) => {
  const [step, setStep] = useState(1);
  const [numCuentas, setNumCuentas] = useState(2);
  const [asignaciones, setAsignaciones] = useState([]); // asignaciones[clientIdx] = accountIdx, -1 = sin asignar
  const [clientePickeado, setClientePickeado] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleContinuar = () => {
    const n = parseInt(numCuentas);
    if (n < 2 || n > clientCount) return;
    setAsignaciones(Array(clientCount).fill(-1));
    setStep(2);
  };

  const handleClienteClick = (ci) => {
    if (loading) return;
    setClientePickeado(prev => prev === ci ? null : ci);
  };

  const handleCuentaClick = (accountIdx) => {
    if (loading || clientePickeado === null) return;
    const newAsig = [...asignaciones];
    newAsig[clientePickeado] = accountIdx;
    setAsignaciones(newAsig);
    setClientePickeado(null);
  };

  const handleQuitarCliente = (ci, e) => {
    if (e) e.stopPropagation();
    if (loading) return;
    const newAsig = [...asignaciones];
    newAsig[ci] = -1;
    setAsignaciones(newAsig);
    setClientePickeado(null);
  };

  const allAssigned = asignaciones.length > 0 && asignaciones.every(a => a >= 0);

  // Validación: ninguna cuenta resultante (incluida la Cuenta Actual) puede
  // quedar sin productos, para evitar generar cheques vacíos. Devuelve el
  // índice de la primera cuenta vacía encontrada, o -1 si todas tienen
  // al menos un producto.
  const getCuentaSinProductos = () => {
    const n = parseInt(numCuentas);
    for (let accountIdx = 0; accountIdx < n; accountIdx++) {
      const clientsInAccount = asignaciones
        .map((acct, ci) => ({ acct, ci }))
        .filter(({ acct }) => acct === accountIdx)
        .map(({ ci }) => ci);
      const totalProductos = clientsInAccount.reduce(
        (sum, ci) => sum + order.filter(item => item.client === ci).length,
        0
      );
      if (totalProductos === 0) return accountIdx;
    }
    return -1;
  };

  const cuentaVaciaIdx = allAssigned ? getCuentaSinProductos() : -1;

  // Separar Cuentas: cada cliente/asiento se mueve COMPLETO con todo lo que
  // ya tiene (sin preguntar cómo repartir cantidades) — así funcionaba
  // originalmente. El reparto parcial de cantidades entre asientos se hace
  // aparte con el botón "Transferir Asiento" (T. Cliente), que ya lo soporta.
  const handleConfirmar = async () => {
    if (!allAssigned) { mostrarMensaje('Asigna todos los clientes antes de confirmar.'); return; }
    if (getCuentaSinProductos() !== -1) {
      mostrarMensaje('No es posible separar las cuentas. Cada cuenta debe contener al menos un producto asignado.');
      return;
    }
    setLoading(true);
    let exitosos = 0;
    try {
      for (let accountIdx = 1; accountIdx < parseInt(numCuentas); accountIdx++) {
        const clientsForAccount = asignaciones
          .map((acct, ci) => ({ acct, ci }))
          .filter(({ acct }) => acct === accountIdx)
          .map(({ ci }) => ci);

        // targetChequeId: el cheque ya creado para esta cuenta nueva.
        // Si hay varios clientes en la misma cuenta nueva, solo el primero
        // crea el cheque; los demás se agregan a ese mismo cheque (targetCheque).
        let targetChequeId = null;

        for (const ci of clientsForAccount) {
          const clientProducts = order.filter(item => item.client === ci);
          if (!clientProducts.length) continue;

          const bodyPayload = {
            chequeOriginal: chequeSeleccionado,
            cliente: ci + 1,
            usuario: currentUser.nombre,
          };
          // Si ya se creó el cheque para esta cuenta, agregar a él en vez de crear uno nuevo
          if (targetChequeId) {
            bodyPayload.targetCheque = targetChequeId;
          }

          const response = await fetch(
            `${LOCAL_API_URL}/actualizar-cheque-cliente.php?property=${encodeURIComponent(property)}`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(bodyPayload)
            }
          );
          const data = await response.json();
          if (!data.success) throw new Error(data.message || 'Error en cliente ' + (ci + 1));

          // Bug fix: el PHP retorna data.data.nuevoCheque, no data.chequeId
          const nuevoChequeId = data.data?.nuevoCheque;
          if (!targetChequeId && nuevoChequeId) {
            targetChequeId = nuevoChequeId; // guardar para clientes siguientes del mismo grupo
          }
          setOrder(prev => prev.map(item =>
            item.client === ci ? { ...item, numero_cheque: nuevoChequeId || targetChequeId } : item
          ));
          exitosos++;
        }
      }
      mostrarMensaje(`✅ ${exitosos} cliente(s) separado(s) en nuevas cuentas.`);
      onClose();
      if (onSuccess) onSuccess();
    } catch (error) {
      mostrarMensaje(`⚠️ Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const cuentaLabel = (idx) => idx === 0 ? 'Cuenta Actual' : `Cuenta ${idx + 1}`;

  return (
    <div className="sep-overlay" onClick={(e) => { if (e.target === e.currentTarget && !loading) onClose(); }}>
      <div className="sep-modal">
        <div className="sep-header">
          <h3>Separar Cuentas</h3>
          <button className="sep-close" onClick={onClose} disabled={loading}>✕</button>
        </div>

        {/* PASO 1: ¿Cuántas cuentas? */}
        {step === 1 && (
          <div className="sep-step1">
            <p className="sep-instruccion">¿Cuántas cuentas desea generar?</p>
            <div className="sep-num-row">
              <button className="sep-num-btn" onClick={() => setNumCuentas(v => Math.max(2, v - 1))}>−</button>
              <span className="sep-num-display">{numCuentas}</span>
              <button className="sep-num-btn" onClick={() => setNumCuentas(v => Math.min(clientCount, v + 1))}>+</button>
            </div>
            <p className="sep-nota">{clientCount} cliente{clientCount !== 1 ? 's' : ''} disponible{clientCount !== 1 ? 's' : ''}</p>
            <button className="sep-continuar-btn" onClick={handleContinuar}>
              Continuar →
            </button>
          </div>
        )}

        {/* PASO 2: Asignación */}
        {step === 2 && (
          <div className="sep-step2">
            {clientePickeado !== null && (
              <div className="sep-pickup-hint">
                <i className="bi bi-hand-index-thumb"></i> Cte {clientePickeado + 1} seleccionado — toca una cuenta para asignar
              </div>
            )}

            <div className="sep-columns">
              {/* Izquierda: clientes sin asignar */}
              <div className="sep-col-clientes">
                <div className="sep-col-title">Sin asignar</div>
                {Array.from({ length: clientCount }, (_, ci) => {
                  if (asignaciones[ci] !== -1) return null;
                  return (
                    <button
                      key={ci}
                      className={`sep-cliente-btn${clientePickeado === ci ? ' sep-cliente-btn--picked' : ''}`}
                      onClick={() => handleClienteClick(ci)}
                    >
                      Cte {ci + 1}
                    </button>
                  );
                })}
                {asignaciones.every(a => a !== -1) && (
                  <p className="sep-todos-ok">✓ Todos asignados</p>
                )}
              </div>

              {/* Derecha: cuentas */}
              <div className="sep-col-cuentas">
                {Array.from({ length: parseInt(numCuentas) }, (_, accountIdx) => {
                  const clientsInAccount = asignaciones
                    .map((acct, ci) => ({ acct, ci }))
                    .filter(({ acct }) => acct === accountIdx)
                    .map(({ ci }) => ci);
                  return (
                    <div
                      key={accountIdx}
                      className={`sep-cuenta-box${clientePickeado !== null ? ' sep-cuenta-box--target' : ''}`}
                      onClick={() => handleCuentaClick(accountIdx)}
                    >
                      <div className="sep-cuenta-title">{cuentaLabel(accountIdx)}</div>
                      <div className="sep-cuenta-clients">
                        {clientsInAccount.length === 0
                          ? <span className="sep-cuenta-empty">Vacía — toca para asignar</span>
                          : clientsInAccount.map(ci => (
                              <span
                                key={ci}
                                className="sep-cuenta-tag"
                                onClick={(e) => handleQuitarCliente(ci, e)}
                              >
                                Cte {ci + 1} ✕
                              </span>
                            ))
                        }
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="sep-footer">
              <button className="sep-back-btn" onClick={() => { setStep(1); setAsignaciones([]); setClientePickeado(null); }} disabled={loading}>
                ← Volver
              </button>
              <button
                className="sep-confirmar-btn"
                onClick={handleConfirmar}
                disabled={!allAssigned || loading || cuentaVaciaIdx !== -1}
                title={cuentaVaciaIdx !== -1 ? 'Cada cuenta debe contener al menos un producto asignado.' : undefined}
              >
                {loading ? 'Procesando...' : `✓ Confirmar (${numCuentas} cuentas)`}
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

const HeaderButton = ({
  action,
  label,
  currentActive, 
  onClick, 
  isSectionVisible 
}) => {
  return (
    <button
      onClick={() => onClick(action)}
      className={`header-button ${currentActive === action ? 'active' : ''} ${
        isSectionVisible ? 'section-visible' : ''
      }`}
      aria-current={currentActive === action ? 'page' : undefined}
    >
      {label}
      {isSectionVisible && <span className="active-indicator" />}
    </button>
  );
};

const Mensaje = ({ mensaje }) => {
  if (!mensaje) return null;
  return <div className="mensaje">{mensaje}</div>;
};

const App = () => {
  const { t, formatCurrency, currentLanguage, changeLanguage } = useLanguage();
  const calculateMaxDiscount = (itemIndex) => {
    if (itemIndex === null || itemIndex === undefined || !order[itemIndex]) {
      return 0;
    }
    
    const item = order[itemIndex];
    const itemPrice = item.price || 0;
    const modifiersTotal = item.modifiers 
      ? item.modifiers.reduce((total, mod) => total + (mod.price * (mod.quantity || 1)), 0)
      : 0;
    
    return (itemPrice + modifiersTotal) * (item.quantity || 1);
  };

  // En la sección de estados (alrededor de línea 300), agrega:
const [showCancelChequeModal, setShowCancelChequeModal] = useState(false);
const [isCancellingCheque, setIsCancellingCheque] = useState(false);
// Requerimiento: indicador de procesamiento en "Confirmar Pago" — solo UI,
// no toca la lógica de cobro. true mientras confirmOrder() está en curso.
const [isConfirmingPayment, setIsConfirmingPayment] = useState(false);

  const handlePedido = useHandlePedido();
  const [currentProperty, setCurrentProperty] = useState(() =>
    localStorage.getItem("currentProperty")
  );
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(() => {
    const savedUser = localStorage.getItem("currentUser");
    if (savedUser) {
      try {
        const user = JSON.parse(savedUser);
        console.log('Usuario cargado de localStorage:', user);
        return { ...user, seguridad: Number(user.seguridad) || 4 };
      } catch (error) {
        console.error('Error al parsear currentUser:', error);
        return null;
      }
    }
    return null;
  });
  const [deviceConfig, setDeviceConfig] = useState(() => {
    const savedConfig = localStorage.getItem("currentDeviceConfig");
    try {
      return savedConfig ? JSON.parse(savedConfig) : null;
    } catch (error) {
      console.error("Error parsing currentDeviceConfig:", error);
      return null;
    }
  });
  const [isConfigLoaded, setIsConfigLoaded] = useState(() => {
    const savedConfig = localStorage.getItem("currentDeviceConfig");
    return !!savedConfig && savedConfig !== "null" && savedConfig !== "undefined";
  });
  const [securityLevels, setSecurityLevels] = useState({});
  const [calculatedTotals, setCalculatedTotals] = useState({ totalCalculado: 0 });
  const [order, setOrder] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showModifiersModal, setShowModifiersModal] = useState(false);
  const [showProductSearchPanel, setShowProductSearchPanel] = useState(false);
  const [currentGroup, setCurrentGroup] = useState(null);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [showQuantityInput, setShowQuantityInput] = useState(false);
  const [selectedOrderIndex, setSelectedOrderIndex] = useState(null);
  const [selectedOrderIndices, setSelectedOrderIndices] = useState([]); // multi-selección para Eliminar
  const [appliedPayments, setAppliedPayments] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [selectedPaymentIndex, setSelectedPaymentIndex] = useState(null);
  const [selectedRevenue, setSelectedRevenue] = useState(null);
  const [showRevenueSelector, setShowRevenueSelector] = useState(true);
  const [numeroCheque, setNumeroCheque] = useState(null);
  const [revenueInfo, setRevenueInfo] = useState(null);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedSpace, setSelectedSpace] = useState(null);
  const [clientCount, setClientCount] = useState(null);
  const [selectedClient, setSelectedClient] = useState(0);
  const [chequeSeleccionado, setChequeSeleccionado] = useState(null);
  const [nombreVendedor, setNombreVendedor] = useState(null);
  const [showChequeList, setShowChequeList] = useState(false);
  const [showPOS, setShowPOS] = useState(false);
  const [mensaje, setMensaje] = useState(null);
  const [showVoidReasonsModal, setShowVoidReasonsModal] = useState(false);
  // ── Segmento (funcionalidad opcional activada por config.json "segmento":"Y") ──
  const [showSegmentoModal, setShowSegmentoModal]   = useState(false);
  const [segmentos, setSegmentos]                   = useState([]);
  const [segmentoLoading, setSegmentoLoading]       = useState(false);
  const [segmentoError, setSegmentoError]           = useState(null);
  const [selectedVoidReason, setSelectedVoidReason] = useState(null);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [showOrderSection, setShowOrderSection] = useState(true);
  const [showPaymentsSection, setShowPaymentsSection] = useState(false);
  const [totalPedido, setTotalPedido] = useState(0);
  const [selectedDiscount, setSelectedDiscount] = useState(null);
  const [pendingDiscounts, setPendingDiscounts] = useState([]);
  const [showAdminSection, setShowAdminSection] = useState(false);
  const [showReportesSection, setShowReportesSection] = useState(false);
  const [showFechaControl, setShowFechaControl] = useState(false);
  const [showCorteDeCaja, setShowCorteDeCaja] = useState(false);
  const [showCorteAdmin, setShowCorteAdmin] = useState(false);
  const [activeTab, setActiveTab] = useState("menu");
  const [showExpressSection, setShowExpressSection] = useState(false);
  const [cheques, setCheques] = useState([]);
  const [showOrderSummary, setShowOrderSummary] = useState(false);
  const [showOpenDiscountModal, setShowOpenDiscountModal] = useState(false);
  const [resetGroupList, setResetGroupList] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [activePanel, setActivePanel] = useState('menu'); // Agrega esta línea
  const [property, setProperty] = useState(() => {
    const savedProperty = localStorage.getItem("currentProperty");
    if (!savedProperty) {
      console.warn("No hay propiedad guardada en localStorage.");
      return null;
    }
    return savedProperty;
  });
  const [propertyConfig, setPropertyConfig] = useState(null);
  const [promos, setPromos] = useState([]);
  // Catálogo del botón "Promo" (lista_promos.json). Estado aislado, no
  // se mezcla con "promos" (que alimenta los descuentos rápidos existentes).
  const [listaPromos, setListaPromos] = useState([]);
  const [payments, setPayments] = useState([]);
  const [revenues, setRevenues] = useState([]);
  const [voidsReasons, setVoidsReasons] = useState([]);
  const [devices, setDevices] = useState([]);

  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState("");
  const [selectedDevice, setSelectedDevice] = useState("");
  const [propertySelectorError, setPropertySelectorError] = useState(null);

  const [API_URL, setApiUrl] = useState("");
  const [API_PAGO, setApiPago] = useState("");
  const [API_DESCUENTO, setApiDescuento] = useState("");

  const [isLoadingDevices, setIsLoadingDevices] = useState(false);
  const [registroMode, setRegistroMode] = useState('manual'); // 'manual' | 'auto'
  const [autoConfigFound, setAutoConfigFound] = useState(null);
  const [autoConfigLoading, setAutoConfigLoading] = useState(false);

  const [activeButton, setActiveButton] = useState('pedidos'); // Valor inicial
  const [isFullscreen, setIsFullscreen] = useState(false);
  const footerRef = useRef(null); // Ref para resetear scroll del footer al cambiar sección
  const autoFileInputRef = useRef(null); // Ref para el input file del modo automático
  const [isOrdering, setIsOrdering] = useState(false);
  const isOrderingRef = useRef(false); // bloqueo inmediato antes de que React re-renderice
  const [showMobileClientPicker, setShowMobileClientPicker] = useState(false);

  const [showTransferClientModal, setShowTransferClientModal] = useState(false);
  const [showSepararCuentasModal, setShowSepararCuentasModal] = useState(false);
  const [newClient, setNewClient] = useState(null);
  const [validClients, setValidClients] = useState([]);
  const [transferClientData, setTransferClientData] = useState(null);
  const revenueId = selectedRevenue?.id; // Define revenueId
  const [orderSummaryKey, setOrderSummaryKey] = useState(0); // Para forzar re-render de OrderSummary
  // Requerimiento "Order Summary": señal { index, name, ts } del último
  // producto agregado/incrementado, para que OrderSummary se desplace hasta
  // él y lo resalte. No sustituye a orderSummaryKey (ese fuerza remount
  // completo); esto solo dispara el efecto de scroll/resaltado.
  const [lastAddedSignal, setLastAddedSignal] = useState(null);
  const [showClientCountModal, setShowClientCountModal] = useState(false); // Modal para teclado numérico
  const [tempClientCount, setTempClientCount] = useState(''); // Valor temporal del teclado

  const [showOpenPriceModal, setShowOpenPriceModal] = useState(false);

  // ── Tiempos de Servicio ────────────────────────────────────────────────────
  /** Controla la visibilidad del panel de asignación de tiempos. */
  const [showTiempoPanel, setShowTiempoPanel] = useState(false);
  // ──────────────────────────────────────────────────────────────────────────

  const handleAddOpenPrice = (productWithPrice) => {
    handleAddProductToClient(productWithPrice); // Usar la función existente
    setShowOpenPriceModal(false);
    setSelectedProduct(null);
  };

  const [showTransferTableSelector, setShowTransferTableSelector] = useState(false);
  const [showTransferPedidoSelector, setShowTransferPedidoSelector] = useState(false);
  const [showTransferMeseroSelector, setShowTransferMeseroSelector] = useState(false);
  // ✅ Punto 2+3 — Venta de Otro Revenue
  const [showVentaOtroRevenueSelector, setShowVentaOtroRevenueSelector] = useState(false);
  const [showPromoSelector, setShowPromoSelector] = useState(false);
  const [showReimprimirTicket, setShowReimprimirTicket] = useState(false);

  const [showHotelModal, setShowHotelModal] = useState(false);
  const [showOpenNoteModal, setShowOpenNoteModal] = useState(false);
const [roomNumber, setRoomNumber] = useState(null);
const [roomInfo, setRoomInfo] = useState(null);
const [customerNamePending, setCustomerNamePending] = useState(null);

  // ── Notas del pedido ──────────────────────────────────────────────────────
  // Requerimiento "Notas": estado independiente para saber a qué producto(s)
  // del pedido se les va a aplicar la nota desde los NUEVOS accesos (botón
  // "Nota" del panel de acciones y el ícono por producto en el resumen).
  // Es deliberadamente independiente de selectedOrderIndex/selectedOrderIndices
  // (que ya usan Cantidad/Eliminar/Descuentos/etc.) para no alterar esos flujos.
  // Si queda vacío, handleAddOpenNote cae de vuelta a selectedOrderIndex, que
  // es exactamente el comportamiento que ya tenía el acceso original (botón
  // "Nota" del catálogo/menú vía handleProductSelection).
  const [noteTargetIndices, setNoteTargetIndices] = useState([]);

  // Un producto ya no se puede anotar si ya fue guardado en BD (enviado a
  // cocina/bar/KDS/impresora) o si está cancelado/anulado. Mismo criterio
  // que ya usa OrderSummary.js para separar "sin ordenar" de "confirmados".
  const isOrderItemSent = (item) =>
    !!(item && item.id_producto && typeof item.id_producto === "number" && item.id_producto > 0);
  const isOrderItemVoidedOrCancelled = (item) => {
    if (!item) return true;
    const qty = parseFloat(item.quantity) || 0;
    const estado = (item.estado || "").toLowerCase();
    return qty < 0 || estado === "void";
  };

  // Requerimiento "Notas": punto de entrada único para abrir el panel de
  // notas desde cualquier acceso nuevo (botón del panel de acciones, ícono
  // por producto en el resumen). Valida ANTES de abrir el panel: si algún
  // producto de la selección ya fue enviado o está cancelado, NO abre el
  // panel y avisa — nunca aplica la nota parcialmente.
  const openNoteFor = (indices) => {
    const idxs = (Array.isArray(indices) ? indices : [indices]).filter(
      (i) => i !== null && i !== undefined && order[i]
    );

    if (idxs.length === 0) {
      mostrarMensaje("Selecciona uno o varios productos del pedido para agregar una nota.");
      return;
    }

    const noEditable = idxs.filter(
      (i) => isOrderItemSent(order[i]) || isOrderItemVoidedOrCancelled(order[i])
    );
    if (noEditable.length > 0) {
      if (idxs.length > 1) {
        // Requerimiento: identificar cuáles productos no se pueden
        // modificar (no solo avisar "algo falló") para que el usuario
        // pueda corregir su selección con esa información.
        const nombres = noEditable.map((i) => order[i]?.name).filter(Boolean);
        const preview = nombres.slice(0, 3).join(", ") + (nombres.length > 3 ? ` y ${nombres.length - 3} más` : "");
        mostrarMensaje(
          `No se aplicó la nota: ${noEditable.length} de ${idxs.length} productos ya fueron enviados (${preview}). Corrige tu selección.`
        );
      } else {
        mostrarMensaje("No es posible modificar la nota porque este producto ya fue enviado.");
      }
      return;
    }

    setNoteTargetIndices(idxs);
    setSelectedProduct(null);
    setShowOpenNoteModal(true);
  };

  const handleAddOpenNote = (note) => {
    const idxs = noteTargetIndices.length > 0
      ? noteTargetIndices
      : (selectedOrderIndex !== null ? [selectedOrderIndex] : []);

    if (idxs.length === 0) {
      mostrarMensaje("Selecciona un producto en el pedido para agregar una nota.");
      setShowOpenNoteModal(false);
      setSelectedProduct(null);
      setNoteTargetIndices([]);
      return;
    }

    setOrder((prevOrder) => {
      const updatedOrder = [...prevOrder];
      idxs.forEach((idx) => {
        const item = updatedOrder[idx];
        if (!item) return;
        // Estructura como modificador estándar para el backend (mismo
        // formato que ya se usaba antes — el backend/KDS/impresión no
        // cambian: siguen leyendo item.modifiers igual que siempre).
        updatedOrder[idx] = {
          ...item,
          modifiers: [
            ...(Array.isArray(item.modifiers) ? item.modifiers : []),
            {
              id: `mod_note_${Date.now()}_${idx}`, // ID único por producto
              productid: item.id,
              name: note,
              price: 0,
              quantity: 1,
            },
          ],
        };
      });
      console.log("Nota agregada como modificador:", note, "Índices:", idxs, "Estado de order:", updatedOrder);
      return updatedOrder;
    });

    mostrarMensaje(
      idxs.length > 1 ? `Nota agregada a ${idxs.length} productos.` : "Nota agregada al producto."
    );
    setShowOpenNoteModal(false);
    setSelectedProduct(null);
    setNoteTargetIndices([]);
  };

  // ── Tiempos de Servicio ────────────────────────────────────────────────────
  /**
   * Asigna un tiempo de servicio a los productos indicados por índice.
   * El tiempo se guarda como:
   *  - item.tiempo          → propiedad para mostrar la etiqueta visual.
   *  - modifier "Tiempo X"  → viaja al KDS como nota (mismo mecanismo que handleAddOpenNote).
   *
   * @param {string}   tiempoLabel   - Ej. "Tiempo 1", "Tiempo 2" …
   * @param {number[]} indices       - Índices dentro del array `order`.
   */
  const handleAssignTiempo = (tiempoLabel, indices) => {
    if (!indices || indices.length === 0) {
      mostrarMensaje("Selecciona al menos un producto para asignar el tiempo.");
      return;
    }

    setOrder((prevOrder) => {
      const updatedOrder = [...prevOrder];

      indices.forEach((idx) => {
        if (!updatedOrder[idx]) return;

        const item = { ...updatedOrder[idx] };

        // Eliminar modificador de tiempo anterior si existe
        // m.id puede ser número (modificadores de producto) o string (notas/tiempos)
        const modifiersSinTiempo = (item.modifiers || []).filter(
          (m) => !String(m.id).startsWith("mod_tiempo_")
        );

        // Agregar nuevo modificador de tiempo (misma estructura que notas → viaja al KDS)
        modifiersSinTiempo.push({
          id: `mod_tiempo_${Date.now()}_${idx}`,
          productid: item.id,
          name: tiempoLabel,
          price: 0,
          quantity: 1,
        });

        updatedOrder[idx] = {
          ...item,
          tiempo: tiempoLabel,         // Para la etiqueta visual
          modifiers: modifiersSinTiempo,
        };
      });

      return updatedOrder;
    });

    setShowTiempoPanel(false);
    mostrarMensaje(`${tiempoLabel} asignado a ${indices.length} producto(s).`);
  };
  // ──────────────────────────────────────────────────────────────────────────

  // If renderOrder is in App.js, modify to use modifier.note
  const renderOrder = () => {
    console.log("Renderizando order:", order);
    return order.map((item, index) => (
      <div
        key={index}
        className={`order-item ${selectedOrderIndex === index ? 'selected' : ''}`}
        onClick={() => setSelectedOrderIndex(index)}
      >
        <div>{item.quantity}x {item.name}</div>
        <div>${(item.price * item.quantity).toFixed(2)}</div>
        {item.modifiers && item.modifiers.length > 0 && (
          <div className="modifiers">
            {item.modifiers.map((modifier, modIndex) => (
              <div key={modIndex} className="modifier-item">
                1x {modifier.note || "Nota vacía"} (Gratis)
              </div>
            ))}
          </div>
        )}
      </div>
    ));
  };

  // Pass propertyConfig to GroupList
  <GroupList
    key={resetGroupList}
    products={filteredProducts}
    onSelectProduct={handleProductSelection}
    propertyConfig={propertyConfig}
  />

  // ✅ FIX doble carga en "Selecciona un Centro de Consumo": allowedActions y
  // filteredRevenues se recalculaban (referencia nueva) en CADA render de
  // App.js — incluso sin que revenues ni los permisos cambiaran realmente.
  // Como RevenueSelector recibe filteredRevenues como prop `revenues` y su
  // useEffect de carga depende de [propertyConfig, revenues] (ver
  // RevenueSelector.js), cada nuevo render de App.js (por cualquier motivo:
  // un mensaje, un timer, cualquier otro estado) volvía a disparar esa carga
  // — la lista se pintaba y, al menor render adicional del padre, se
  // recargaba de nuevo, percibido como "doble carga". useMemo conserva la
  // MISMA referencia mientras el contenido de las dependencias no cambie,
  // sin alterar el resultado del filtro.
  const allowedActions = useMemo(
    () => (currentUser && securityLevels[currentUser.seguridad]
      ? securityLevels[currentUser.seguridad]
      : EMPTY_ACTIONS),
    [currentUser, securityLevels]
  );

  const filteredRevenues = useMemo(
    () => revenues.filter((revenue) => allowedActions.includes(revenue.name)),
    [revenues, allowedActions]
  );

  // ✅ Restricción de acceso a mesas de otros vendedores.
  // Reutiliza el sistema de permisos existente (allowedActions) sin crear
  // ningún mecanismo nuevo: permite el acceso si (a) el cheque no tiene
  // vendedor registrado (fail-open, datos legados), (b) el usuario actual
  // ES el vendedor del cheque (comparación case-insensitive, mismo patrón
  // que ya usa "Mis Mesas" en RevenueSelector.js), o (c) el usuario tiene
  // el permiso "Acceder a Mesas de Otros Vendedores" o "Capitán".
  const PERMISOS_MESA_OTRO_VENDEDOR = ["Acceder a Mesas de Otros Vendedores", "Capitán"];

  const puedeAbrirMesaDeOtroVendedor = (nombreVendedorCheque) => {
    const vendedor = (nombreVendedorCheque || "").trim();
    if (!vendedor) return true;
    if (currentUser?.nombre && vendedor.toUpperCase() === currentUser.nombre.toUpperCase()) {
      return true;
    }
    return PERMISOS_MESA_OTRO_VENDEDOR.some((permiso) => allowedActions.includes(permiso));
  };

  const MENSAJE_MESA_OTRO_VENDEDOR =
    "No puede abrir esta mesa porque pertenece a otro vendedor. Solicite apoyo a un supervisor o a un usuario con permisos para administrar mesas de otros vendedores.";

  // En App.jsx, añadir antes de handleSaveOrder si no existe:
  const fetchCheques = async () => {
    try {
      const response = await fetch(`${LOCAL_API_URL}/lectura_cheques.php?property=${encodeURIComponent(property)}`);
      if (!response.ok) throw new Error(`Error ${response.status}: ${await response.text()}`);
      const data = await response.json();
      if (!data.success) throw new Error(data.message || "Error al cargar cheques");
      setCheques(data.data || []);
      return data.data || []; // Return the fetched cheques
    } catch (error) {
      console.error("[fetchCheques] Error:", error);
      mostrarMensaje(`⚠️ Error al cargar cheques: ${error.message}`);
      throw error;
    }
  };

  const handleHeaderButtonClick = (buttonName) => {
    setActiveButton(buttonName);
    switch(buttonName) {
      case 'pedidos': return handleShowPedidos();
      case 'pagos': return handleShowPagos();
      case 'express': return handleShowExpress();
      case 'admin': return handleShowAdmin();
      case 'reportes': return handleShowReportes();
      default: return handleShowPedidos();
    }
  };

  const handleReloadData = async () => {
    if (!property) {
      mostrarMensaje("No hay propiedad seleccionada.");
      return;
    }

    try {
      // Archivos a limpiar y recargar
      const filesToReload = [
        "employees_sec.json",
        "promos.json",
        "lista_promos.json",
        "payments.json",
        "revenues.json",
        "voidsReasons.json"
      ];

      if (selectedRevenue?.id) {
        filesToReload.push(`products_${selectedRevenue.id}.json`);
      }

      // Borrar caché de IndexedDB
      for (const file of filesToReload) {
        await deleteFromIndexedDB(`${property}/${file}`);
      }

      // Recargar empleados_seguridad
      const employeesSecData = await fetchData(
        BASE_API_JSON,
        property,
        "employees_sec.json",
        "Error cargando niveles de seguridad"
      );
      const securityLevelsMap = {};
      employeesSecData.forEach((item) => {
        securityLevelsMap[item.level] = item.actions;
      });
      setSecurityLevels(securityLevelsMap);

      // Recargar promociones
      const promosData = await fetchData(
        BASE_API_JSON,
        property,
        "promos.json",
        "Error cargando promociones"
      );
      setPromos(Array.isArray(promosData) ? promosData : []);

      // Recargar catálogo del botón "Promo" (independiente de promos.json)
      try {
        const listaPromosData = await fetchData(
          BASE_API_JSON,
          property,
          "lista_promos.json",
          "Error cargando catálogo de promociones"
        );
        setListaPromos(Array.isArray(listaPromosData?.promociones) ? listaPromosData.promociones : []);
      } catch (listaPromosError) {
        console.warn("[Promo] lista_promos.json no disponible para esta propiedad:", listaPromosError.message);
      }

      // Recargar formas de pago
      const paymentsData = await fetchData(
        BASE_API_JSON,
        property,
        "payments.json",
        "Error cargando formas de pago"
      );
      setPayments(Array.isArray(paymentsData) ? paymentsData : []);

      // Recargar centros de consumo
      const revenuesData = await fetchData(
        BASE_API_JSON,
        property,
        "revenues.json",
        "Error cargando centros de consumo"
      );
      setRevenues(revenuesData);

      // Recargar razones de cancelación
      const voidsReasonsData = await fetchData(
        BASE_API_JSON,
        property,
        "voidsReasons.json",
        "Error cargando razones de cancelación"
      );
      setVoidsReasons(Array.isArray(voidsReasonsData) ? voidsReasonsData : []);

      // Si hay revenue seleccionado, recargar productos
      if (selectedRevenue?.id) {
        const productsData = await fetchData(
          BASE_API_JSON,
          property,
          `products_${selectedRevenue.id}.json`,
          "Error cargando productos",
          selectedRevenue.id
        );
        const newProducts = Array.isArray(productsData) ? productsData : [];
        setFilteredProducts((prev) =>
          JSON.stringify(prev) === JSON.stringify(newProducts) ? prev : newProducts
        );
      }

      // Mostrar mensaje de éxito
      mostrarMensaje("✅ Datos recargados correctamente.", 5000);

      // Opcional: Recargar la página para asegurar que los cambios se vean visualmente
      window.location.reload();

    } catch (error) {
      console.error("Error al recargar datos:", error);
      mostrarMensaje(`⚠️ Error al recargar datos: ${error.message}`, 5000);
    }
  };

  // Función para actualizar el número de clientes
  const updateClientCount = async () => {
    if (!allowedActions.includes("Clientes")) {
      mostrarMensaje("No tienes permiso para actualizar clientes.", "error");
      return;
    }
    if (!selectedRevenue || selectedRevenue.Tipo === "Retail") {
      setClientCount(0);
      setOrderSummaryKey((prev) => prev + 1);
      mostrarMensaje("No se pueden actualizar clientes en un revenue Retail.", "error");
      return;
    }
    try {
      console.log("[updateClientCount] Fetching client count for revenue:", selectedRevenue.id);
      const response = await fetch(
        `${LOCAL_API_URL}/update_clientes.php?property=${encodeURIComponent(property)}&revenue_id=${selectedRevenue.id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'get_client_count',
            numero_cheque: chequeSeleccionado,
          }),
        }
      );
      if (!response.ok) {
        throw new Error(`Error HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || "Error al obtener el número de clientes");
      }
      const currentClientCount = parseInt(data.data.client_count, 10) || 0;
      console.log("[updateClientCount] Current client count:", currentClientCount);
      setClientCount(currentClientCount);
      setTempClientCount(''); // Iniciar vacío para que el usuario seleccione directamente
      setShowClientCountModal(true);
      console.log("[updateClientCount] Setting showClientCountModal to true");
    } catch (error) {
      console.error("[updateClientCount] Error:", error);
      mostrarMensaje(
        `No se pudo conectar con el servidor: ${error.message}. Usando valor actual: ${clientCount || 0}.`,
        "error"
      );
      setTempClientCount(''); // Iniciar vacío incluso en modo fallback
      setShowClientCountModal(true);
    }
  };

  const confirmClientCount = async () => {
    // Validación de permisos
    if (!allowedActions.includes("Clientes")) {
      mostrarMensaje("No tienes permiso para actualizar clientes.", "error");
      return;
    }

    // Validación del input
    const newCount = parseInt(tempClientCount, 10);
    if (isNaN(newCount) || newCount < 0) {
      mostrarMensaje("Ingresa un número de clientes válido (mayor o igual a 0).", "error");
      return;
    }

    // Validación de requisitos previos
    if (!property) {
      mostrarMensaje("Error: No hay propiedad seleccionada.", "error");
      return;
    }

    if (!selectedRevenue?.id) {
      mostrarMensaje("Error: No hay revenue seleccionado.", "error");
      return;
    }

    if (!chequeSeleccionado) {
      mostrarMensaje("Error: No hay cheque seleccionado.", "error");
      return;
    }

    try {
      console.log("[confirmClientCount] Confirmando nuevo conteo de clientes:", {
        newCount,
        property,
        revenueId: selectedRevenue.id,
        chequeSeleccionado,
        usuario: currentUser?.nombre || 'unknown'
      });

      // 1. Primero validamos el conteo actual de clientes
      const validationResponse = await fetch(
        `${LOCAL_API_URL}/update_clientes.php?property=${encodeURIComponent(property)}&revenue_id=${selectedRevenue.id}`,
        {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
          },
          body: JSON.stringify({
            action: 'get_client_count',
            numero_cheque: chequeSeleccionado.toString(), // Asegurar que es string
          }),
        }
      );

      if (!validationResponse.ok) {
        const errorText = await validationResponse.text();
        console.error("[confirmClientCount] Error en validación:", {
          status: validationResponse.status,
          statusText: validationResponse.statusText,
          errorText
        });
        throw new Error(`Error HTTP ${validationResponse.status}: ${errorText}`);
      }

      const validationData = await validationResponse.json();
      console.log("[confirmClientCount] Respuesta de validación:", validationData);

      if (!validationData.success) {
        throw new Error(validationData.message || "Error al validar el número de clientes");
      }

      const currentClientCount = parseInt(validationData.data?.client_count, 10) || 0;
      
      // Validación adicional: No permitir reducir clientes si hay pedidos
      if (newCount < currentClientCount) {
        mostrarMensaje(
          `No puedes reducir el número de clientes a ${newCount} porque actualmente hay ${currentClientCount} clientes con pedidos.`,
          "error"
        );
        return;
      }

      // 2. Si pasa la validación, procedemos con la actualización
      const updateResponse = await fetch(
        `${LOCAL_API_URL}/update_clientes.php?property=${encodeURIComponent(property)}&revenue_id=${selectedRevenue.id}`,
        {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
          },
          body: JSON.stringify({
            action: 'update_client_count',
            client_count: newCount,
            numero_cheque: chequeSeleccionado.toString(), // Asegurar que es string
            usuario: currentUser?.nombre || 'unknown',
            timestamp: new Date().toISOString(),
            // Añadir más datos de contexto para el servidor
            mesa: selectedSpace?.name || 'No especificado',
            revenue_name: selectedRevenue?.name || 'No especificado'
          }),
        }
      );

      if (!updateResponse.ok) {
        const errorText = await updateResponse.text();
        console.error("[confirmClientCount] Error en actualización:", {
          status: updateResponse.status,
          statusText: updateResponse.statusText,
          errorText
        });
        throw new Error(`Error HTTP ${updateResponse.status}: ${errorText}`);
      }

      const updateData = await updateResponse.json();
      console.log("[confirmClientCount] Respuesta de actualización:", updateData);

      if (!updateData.success) {
        throw new Error(updateData.message || "Error al actualizar el número de clientes");
      }

      // 3. Si todo sale bien, actualizamos el estado local
      setClientCount(newCount);
      setShowClientCountModal(false);
      setTempClientCount('');
      setShowChequeList(true);
      setOrderSummaryKey(prev => prev + 1); // Forzar re-render
      
      console.log("[confirmClientCount] Conteo de clientes actualizado exitosamente a:", newCount);
      mostrarMensaje(`Número de clientes actualizado a ${newCount}.`, "success");

      // Actualizar la lista de cheques después del cambio
      await fetchCheques();

    } catch (error) {
      console.error("[confirmClientCount] Error detallado:", {
        error: error.message,
        stack: error.stack,
        context: {
          newCount,
          chequeSeleccionado,
          property,
          revenueId: selectedRevenue?.id,
          user: currentUser?.nombre
        }
      });

      // Manejo de errores con mensaje más descriptivo
      let errorMessage = error.message;
      
      if (error.message.includes('400')) {
        errorMessage = "Datos inválidos enviados al servidor. Verifica:";
        errorMessage += `\n- Property: ${property}`;
        errorMessage += `\n- Revenue ID: ${selectedRevenue?.id}`;
        errorMessage += `\n- Cheque: ${chequeSeleccionado}`;
        errorMessage += `\n- Usuario: ${currentUser?.nombre || 'unknown'}`;
      }

      mostrarMensaje(
        `Error al actualizar clientes: ${errorMessage}`,
        "error"
      );

      // Fallback: Actualizar localmente pero indicar que no se sincronizó con el servidor
      setClientCount(newCount);
      setShowClientCountModal(false);
      setTempClientCount('');
      setShowChequeList(true);
      setOrderSummaryKey(prev => prev + 1);
      
      mostrarMensaje(
        `Se aplicó el cambio localmente (${newCount} clientes), pero no se pudo sincronizar con el servidor.`,
        "warning"
      );
    }
  };

  // Función para obtener clientes válidos
  const fetchValidClients = async () => {
    if (!allowedActions.includes("Transferir Cliente")) {
      mostrarMensaje("No tienes permiso para transferir clientes.", "error");
      return false;
    }
    if (!chequeSeleccionado) {
      mostrarMensaje("Selecciona un cheque antes de transferir.", "error");
      return false;
    }
    try {
      const response = await fetch(
        `${LOCAL_API_URL}/update_clientes.php?property=${encodeURIComponent(property)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'get_valid_clients',
            numero_cheque: chequeSeleccionado,
          }),
        }
      );
      if (!response.ok) {
        throw new Error(`Error HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || "Error al obtener clientes válidos");
      }
      setValidClients(data.data.valid_clients || []);
      return data.data.valid_clients.length > 0;
    } catch (error) {
      console.error("[fetchValidClients] Error:", error);
      mostrarMensaje(`Error al obtener clientes válidos: ${error.message}`, "error");
      setValidClients([]);
      return false;
    }
  };

  // Función para manejar la transferencia de cliente
  const handleTransferClient = async (clientIndex) => {
    if (!chequeSeleccionado || !allowedActions.includes("Transferir Cliente")) {
      mostrarMensaje("No hay cheque seleccionado o no tienes permisos.", "error");
      return;
    }
    if (selectedOrderIndex === null || selectedOrderIndex < 0 || !order[selectedOrderIndex]) {
      mostrarMensaje("Selecciona un artículo para transferir.", "error");
      return;
    }
    if (!selectedRevenue?.id) {
      mostrarMensaje("No hay revenue seleccionado.", "error");
      return;
    }
    try {
      const validClients = await fetchValidClientsTrans();
      console.log("Valid clients:", validClients); // Should log [{clientIndex: 0}, {clientIndex: 1}, {clientIndex: 2}]
      if (!validClients || validClients.length === 0) {
        mostrarMensaje("No hay clientes válidos para transferir.", "error");
        return;
      }
      const product = order[selectedOrderIndex];
      console.log("Selected product:", product); // Should log product with asiento: 1, item: 1562
      setTransferClientData({
        clientIndex,
        selectedOrderIndex,
        product,
        validClients,
      });
      console.log("Opening modal with transferClientData:", {
        clientIndex,
        selectedOrderIndex,
        product,
        validClients,
      });
      setShowTransferClientModal(true);
    } catch (error) {
      console.error("Error al preparar transferencia:", error);
      mostrarMensaje(`Error: ${error.message}`, "error");
    }
  };

  // Función para confirmar la transferencia de cliente
  const confirmTransferClient = async (selectedOrderIndex, newClient, cantidadATransferirInput) => {
    try {
      console.log("confirmTransferClient - Input:", { selectedOrderIndex, newClient, cantidadATransferirInput, order });
      const selectedItem = order[selectedOrderIndex];
      if (!selectedItem) {
        throw new Error("No se encontró el artículo seleccionado. Índice inválido.");
      }

      if (selectedItem.client === newClient) {
        throw new Error("No puedes transferir al mismo cliente.");
      }

      const numeroPedido = selectedItem.id_produto || selectedItem.id; // id_produto = t_pedidos.id en lectura_cheques.php
      if (!numeroPedido) {
        throw new Error("El artículo seleccionado no tiene un identificador válido (id o item).");
      }

      console.log("confirmTransferClient - selectedItem:", selectedItem);

      const cantidadTotal = Math.abs(parseFloat(selectedItem.quantity)) || 1;
      const cantidadSolicitada = parseFloat(cantidadATransferirInput);
      const esTransferenciaParcial =
        cantidadTotal > 1 && !isNaN(cantidadSolicitada) && cantidadSolicitada > 0 && cantidadSolicitada < cantidadTotal;

      if (esTransferenciaParcial) {
        // Requerimiento: si el artículo tiene varias unidades (ej. 15
        // buffets), permitir transferir solo una parte al nuevo asiento,
        // dejando el resto en el asiento original. Reutilizamos
        // dividir_cantidad.php (mismo cheque, distinto asiento destino) en
        // vez de update_clientesaclientes.php, que siempre mueve el
        // renglón completo.
        const response = await fetch(`${LOCAL_API_URL}/dividir_cantidad.php?property=${encodeURIComponent(property)}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            accion: "dividir",
            chequeOriginal: chequeSeleccionado,
            pedidoId: numeroPedido,
            cantidadMover: cantidadSolicitada,
            targetCheque: chequeSeleccionado, // mismo cheque: solo cambia el asiento
            clienteDestino: newClient + 1,    // clienteDestino es 1-based
            usuario: currentUser.nombre,
          }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Error HTTP ${response.status}: ${errorText}`);
        }

        const data = await response.json();
        console.log("confirmTransferClient (parcial) - Response:", data);
        if (!data.success) {
          throw new Error(data.message || "Error al transferir la cantidad parcial.");
        }

        setShowTransferClientModal(false);
        setSelectedOrderIndex(null);
        setOrderSummaryKey((prev) => prev + 1);
        mostrarMensaje(`✅ Se transfirieron ${cantidadSolicitada} de "${selectedItem.name}" al Asiento ${newClient + 1}.`);
      } else {
        // Transferencia completa del renglón (comportamiento sin cambios).
        const response = await fetch(
          `${LOCAL_API_URL}/update_clientesaclientes.php?property=${encodeURIComponent(property)}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
            },
            body: JSON.stringify({
              action: "transfer_client",
              numero_cheque: chequeSeleccionado,
              revenue_id: revenueId,
              new_cliente: newClient, // newClient es 0-based; el PHP suma +1 internamente
              clientes: clientCount,      // total de clientes activos → PHP no capea new_cliente incorrectamente
              pedidos: [{ numero_pedido: numeroPedido }],
              usuario: currentUser.nombre,
            }),
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Error HTTP ${response.status}: ${errorText}`);
        }

        const data = await response.json();
        console.log("confirmTransferClient - Response:", data);
        if (!data.success) {
          throw new Error(data.message || "Error al transferir el artículo.");
        }

        // newClient es 0-based → guardamos directo en estado
        setOrder((prev) =>
          prev.map((item, idx) =>
            idx === selectedOrderIndex ? { ...item, client: newClient } : item
          )
        );

        setShowTransferClientModal(false);
        setSelectedOrderIndex(null);
        setOrderSummaryKey((prev) => prev + 1);
        mostrarMensaje(`✅ Artículo transferido al Asiento ${newClient + 1}`);
      }

      // Recargar cheques y orden desde DB para garantizar consistencia
      // (en el caso parcial esto es lo único que refresca el estado local,
      // ya que un movimiento parcial afecta dos renglones, no uno solo).
      const updatedCheques = await fetchCheques();
      if (updatedCheques && chequeSeleccionado) {
        const chequeActual = updatedCheques.find(c => c.numero_cheque == chequeSeleccionado);
        if (chequeActual) {
          await cargarChequeActualizado(chequeSeleccionado, updatedCheques);
        }
      }
    } catch (error) {
      console.error("Error confirming transfer:", error);
      mostrarMensaje(`⚠️ Error al transferir: ${error.message}`, "error");
    }
  };

  const fetchValidClientsTrans = async () => {
    if (!allowedActions.includes("Transferir Cliente")) {
      mostrarMensaje("No tienes permiso para transferir clientes.", "error");
      return [];
    }
    if (!chequeSeleccionado) {
      mostrarMensaje("Selecciona un cheque antes de transferir.", "error");
      return [];
    }
    try {
      console.log("fetchValidClientsTrans - Request:", {
        url: `${LOCAL_API_URL}/update_clientesaclientes.php?property=${encodeURIComponent(property)}`,
        body: { action: "get_client_count", numero_cheque: chequeSeleccionado, revenue_id: revenueId },
      });
      const response = await fetch(
        `${LOCAL_API_URL}/update_clientesaclientes.php?property=${encodeURIComponent(property)}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
          },
          body: JSON.stringify({
            action: "get_client_count",
            numero_cheque: chequeSeleccionado,
            revenue_id: revenueId, // Ensure revenueId is defined in your App.js state
          }),
        }
      );
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error HTTP ${response.status}: ${errorText}`);
      }
      const data = await response.json();
      console.log("fetchValidClientsTrans - Response:", data);
      if (!data.success) {
        throw new Error(data.message || "Error al obtener clientes válidos");
      }
      // Convert client_count to an array of valid client indices (1 to client_count)
      const clientCount = data.data.client_count || 0;
      const validClients = Array.from({ length: clientCount }, (_, i) => ({
        clientIndex: i, // 0-based for App.js
      }));
      setValidClients(validClients);
      return validClients;
    } catch (error) {
      console.error("[fetchValidClientsTrans] Error:", error);
      mostrarMensaje(`Error al obtener clientes válidos: ${error.message}`, "error");
      setValidClients([]);
      return [];
    }
  };

  // Modal para seleccionar la cantidad de clientes en la mesa
  const ClientCountModal = ({ show, onClose, onConfirm, currentClientCount }) => {
    const [showManualClientInput, setShowManualClientInput] = React.useState(false);
    const [manualClientValue, setManualClientValue] = React.useState('');

    if (!show) return null;

    const selectedCount = tempClientCount === '' ? null : parseInt(tempClientCount, 10);

    const handleSelect = (n) => {
      if (selectedCount === n) {
        setTempClientCount('');
      } else {
        setTempClientCount(n.toString());
      }
      setShowManualClientInput(false);
      setManualClientValue('');
    };

    const handleManualClientConfirm = () => {
      const num = parseInt(manualClientValue, 10);
      if (isNaN(num) || num < 1) {
        mostrarMensaje("Ingresa un número válido.", "error");
        return;
      }
      if (num < (currentClientCount || 0)) {
        mostrarMensaje(`El mínimo permitido es ${currentClientCount}.`, "error");
        return;
      }
      setTempClientCount(num.toString());
      setShowManualClientInput(false);
      setManualClientValue('');
    };

    const handleConfirm = () => {
      const newCount = parseInt(tempClientCount, 10);
      if (isNaN(newCount) || tempClientCount === '') {
        mostrarMensaje("Selecciona el número de clientes.", "error");
        return;
      }
      if (newCount < (currentClientCount || 0)) {
        mostrarMensaje(
          `El mínimo permitido es ${currentClientCount} (ya hay pedidos registrados).`,
          "error"
        );
        return;
      }
      onConfirm();
    };

    const handleClose = () => {
      setTempClientCount('');
      onClose();
    };

    // Rango dinámico: cubrir siempre hasta al menos 12, o hasta el actual + 4
    const maxN = Math.max(12, (currentClientCount || 0) + 4);
    const numbers = Array.from({ length: maxN }, (_, i) => i + 1);

    return (
      <div
        style={{
          position: "fixed",
          top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.6)",
          backdropFilter: "blur(5px)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 10000,
          padding: "16px",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            background: "white",
            borderRadius: "24px",
            padding: "24px",
            width: "100%",
            maxWidth: "420px",
            maxHeight: "90vh",
            overflowY: "auto",
            boxSizing: "border-box",
            boxShadow: "0 25px 50px -12px rgba(0,0,0,0.35)",
            border: "1px solid rgba(255,255,255,0.3)",
            animation: "modalSlideIn 0.2s ease-out",
          }}
        >
          {/* Título */}
          <h2
            style={{
              margin: "0 0 8px 0",
              fontSize: "clamp(1.1rem, 5vw, 1.4rem)",
              color: "#0b2b5c",
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              borderBottom: "2px solid #e2e8f0",
              paddingBottom: "12px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <i className="bi bi-people-fill" style={{ fontSize: "1.2rem" }}></i>
            Clientes en Mesa
          </h2>

          {/* Estado actual → nuevo */}
          <p
            style={{
              margin: "12px 0 16px",
              fontSize: "0.9rem",
              color: "#64748b",
              textAlign: "center",
            }}
          >
            Actual:{" "}
            <strong style={{ color: "#0b2b5c" }}>
              {currentClientCount || 0}
            </strong>
            {"  →  "}
            <strong style={{ color: selectedCount !== null ? "#0a95c8" : "#94a3b8" }}>
              {selectedCount !== null ? `${selectedCount} clientes` : "Selecciona"}
            </strong>
          </p>

          {/* Grid de selección directa */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: "8px",
              background: "linear-gradient(145deg, #f1f5f9, #ffffff)",
              borderRadius: "20px",
              padding: "14px",
              marginBottom: "20px",
              boxShadow: "inset 0 2px 5px rgba(0,0,0,0.04), 0 10px 20px -8px rgba(0,0,0,0.1)",
            }}
          >
            {numbers.map((n) => {
              const isSelected = selectedCount === n;
              const isCurrent = n === (currentClientCount || 0);
              const isDisabled = n < (currentClientCount || 0);
              return (
                <button
                  key={n}
                  onClick={() => !isDisabled && handleSelect(n)}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "10px 4px",
                    minHeight: "62px",
                    border: isSelected
                      ? "2px solid #0a95c8"
                      : isCurrent
                      ? "2px solid #e2e8f0"
                      : "1px solid rgba(255,255,255,0.6)",
                    borderRadius: "14px",
                    background: isSelected
                      ? "linear-gradient(145deg, #0a95c8, #0879a8)"
                      : isCurrent
                      ? "linear-gradient(145deg, #f0f9ff, #e0f2fe)"
                      : "white",
                    color: isSelected ? "white" : isDisabled ? "#cbd5e1" : "#1f2937",
                    cursor: isDisabled ? "not-allowed" : "pointer",
                    boxShadow: isSelected
                      ? "0 5px 0 #065a7a"
                      : isDisabled
                      ? "none"
                      : "0 5px 0 #cbd5e1",
                    transition: "all 0.08s ease",
                    fontFamily: "inherit",
                    touchAction: "manipulation",
                    transform: isSelected ? "translateY(3px)" : "none",
                    opacity: isDisabled ? 0.4 : 1,
                  }}
                >
                  {isCurrent && !isSelected && (
                    <span
                      style={{
                        fontSize: "0.55rem",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.3px",
                        color: "#0a95c8",
                        marginBottom: "1px",
                      }}
                    >
                      actual
                    </span>
                  )}
                  {!(isCurrent && !isSelected) && (
                    <span style={{ marginBottom: "1px", minHeight: "10px" }}></span>
                  )}
                  <span
                    style={{
                      fontSize: "1.6rem",
                      fontWeight: 800,
                      lineHeight: 1,
                    }}
                  >
                    {n}
                  </span>
                  <span
                    style={{
                      fontSize: "0.55rem",
                      fontWeight: 600,
                      opacity: 0.6,
                      textTransform: "uppercase",
                      letterSpacing: "0.3px",
                      marginTop: "2px",
                    }}
                  >
                    {n === 1 ? "persona" : "personas"}
                  </span>
                </button>
              );
            })}

            {/* Botón para ingresar cantidad manual */}
            <button
              onClick={() => {
                setShowManualClientInput((prev) => !prev);
                setTempClientCount('');
                setManualClientValue('');
              }}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "10px 4px",
                minHeight: "62px",
                border: showManualClientInput ? "2px solid #f59e0b" : "1px dashed #94a3b8",
                borderRadius: "14px",
                background: showManualClientInput
                  ? "linear-gradient(145deg, #f59e0b, #d97706)"
                  : "white",
                color: showManualClientInput ? "white" : "#64748b",
                cursor: "pointer",
                boxShadow: showManualClientInput
                  ? "0 5px 0 #92400e"
                  : "0 5px 0 #cbd5e1",
                transition: "all 0.08s ease",
                fontFamily: "inherit",
                touchAction: "manipulation",
              }}
            >
              <span style={{ fontSize: "1.4rem", fontWeight: 800, lineHeight: 1 }}>+</span>
              <span style={{ fontSize: "0.55rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.3px", opacity: 0.85, marginTop: "2px" }}>
                Otro
              </span>
            </button>
          </div>

          {/* Input manual de cantidad */}
          {showManualClientInput && (
            <div
              style={{
                display: "flex",
                gap: "8px",
                alignItems: "center",
                marginBottom: "16px",
                background: "#fffbeb",
                border: "1px solid #fcd34d",
                borderRadius: "16px",
                padding: "12px 14px",
              }}
            >
              <span style={{ fontSize: "0.85rem", color: "#92400e", fontWeight: 600, whiteSpace: "nowrap" }}>
                Clientes:
              </span>
              <input
                type="number"
                min={currentClientCount || 1}
                value={manualClientValue}
                onChange={(e) => setManualClientValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleManualClientConfirm(); }}
                placeholder="Ej: 100"
                autoFocus
                style={{
                  flex: 1,
                  padding: "8px 12px",
                  border: "1px solid #fcd34d",
                  borderRadius: "10px",
                  fontSize: "1.1rem",
                  fontWeight: 700,
                  color: "#1f2937",
                  outline: "none",
                  fontFamily: "inherit",
                  background: "white",
                  minWidth: 0,
                }}
              />
              <button
                onClick={handleManualClientConfirm}
                style={{
                  padding: "8px 16px",
                  background: "linear-gradient(145deg, #f59e0b, #d97706)",
                  border: "none",
                  borderRadius: "10px",
                  color: "white",
                  fontWeight: 800,
                  fontSize: "0.9rem",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  whiteSpace: "nowrap",
                  touchAction: "manipulation",
                }}
              >
                OK
              </button>
            </div>
          )}

          {/* Botones de acción */}
          <div style={{ display: "flex", gap: "10px" }}>
            <button
              onClick={handleClose}
              style={{
                flex: 1,
                padding: "14px 16px",
                border: "none",
                borderRadius: "40px",
                background: "linear-gradient(145deg, #dc2626, #b91c1c)",
                color: "white",
                fontWeight: 800,
                fontSize: "clamp(0.9rem, 4vw, 1rem)",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                cursor: "pointer",
                boxShadow: "0 6px 0 #7f1d1d",
                minHeight: "50px",
                touchAction: "manipulation",
                fontFamily: "inherit",
              }}
            >
              ✕ Cancelar
            </button>
            <button
              onClick={handleConfirm}
              disabled={selectedCount === null}
              style={{
                flex: 1,
                padding: "14px 16px",
                border: "none",
                borderRadius: "40px",
                background:
                  selectedCount !== null
                    ? "linear-gradient(145deg, #0a95c8, #0879a8)"
                    : "#94a3b8",
                color: "white",
                fontWeight: 800,
                fontSize: "clamp(0.9rem, 4vw, 1rem)",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                cursor: selectedCount !== null ? "pointer" : "not-allowed",
                boxShadow:
                  selectedCount !== null ? "0 6px 0 #065a7a" : "0 4px 0 #64748b",
                minHeight: "50px",
                touchAction: "manipulation",
                opacity: selectedCount === null ? 0.6 : 1,
                fontFamily: "inherit",
              }}
            >
              {selectedCount !== null
                ? `✓ Confirmar ${selectedCount} ${selectedCount === 1 ? "Cliente" : "Clientes"}`
                : "Confirmar"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Modal para seleccionar nuevo asiento destino al transferir cliente
  const TransferClientNumericModal = ({ show, onClose, transferClientData, validClients, onConfirm }) => {
    const [selectedSeat, setSelectedSeat] = useState(null); // clientIndex 0-based seleccionado
    const [isLoading, setIsLoading] = useState(false);
    const [showManualInput, setShowManualInput] = useState(false);
    const [manualSeatValue, setManualSeatValue] = useState("");
    // Requerimiento: si el artículo tiene cantidad > 1 (ej. 15 buffets),
    // preguntar cuántas unidades transferir en vez de mover siempre todo.
    const [cantidadATransferir, setCantidadATransferir] = useState("");

    // product.client está normalizado a 0-based en el estado → usarlo directamente como clientIndex
    const currentClientIndex = transferClientData?.product?.client ?? -1;
    const currentClient1Based = currentClientIndex >= 0 ? currentClientIndex + 1 : -1;
    const cantidadTotalProducto = Math.abs(parseFloat(transferClientData?.product?.quantity)) || 1;
    const esCantidadMultiple = cantidadTotalProducto > 1;

    // Filtrar asientos disponibles excluyendo el asiento actual (comparación en 0-based)
    const availableClients = validClients.filter((c) => c.clientIndex !== currentClientIndex);

    useEffect(() => {
      // Por defecto se propone transferir TODA la cantidad (comportamiento
      // igual al de siempre); el usuario puede reducirla si solo quiere
      // mover una parte (ej. 5 de 15 buffets).
      if (show) {
        setCantidadATransferir(String(cantidadTotalProducto));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, transferClientData?.selectedOrderIndex]);

    const handleSeatClick = (clientIndex) => {
      setSelectedSeat((prev) => (prev === clientIndex ? null : clientIndex));
      setShowManualInput(false);
      setManualSeatValue("");
    };

    const handleManualConfirm = () => {
      const num = parseInt(manualSeatValue, 10);
      if (isNaN(num) || num < 1) {
        mostrarMensaje("Ingresa un número de asiento válido.", "error");
        return;
      }
      const idx = num - 1; // convertir a 0-based
      if (idx === currentClientIndex) {
        mostrarMensaje("No puedes transferir al mismo asiento.", "error");
        return;
      }
      setSelectedSeat(idx);
      setShowManualInput(false);
    };

    const handleConfirm = async () => {
      if (selectedSeat === null) {
        mostrarMensaje("Selecciona un asiento de destino.", "error");
        return;
      }
      if (esCantidadMultiple) {
        // Validación defensiva (la UI ya solo permite elegir por botones
        // valores enteros de 1 al máximo disponible, pero se valida de
        // nuevo aquí antes de ejecutar la transferencia, por requerimiento).
        const cant = Number(cantidadATransferir);
        if (
          cantidadATransferir === "" ||
          isNaN(cant) ||
          !Number.isInteger(cant) ||
          cant < 1 ||
          cant > cantidadTotalProducto
        ) {
          mostrarMensaje(`Selecciona una cantidad entre 1 y ${cantidadTotalProducto}.`, "error");
          return;
        }
      }
      setIsLoading(true);
      try {
        await onConfirm(transferClientData.selectedOrderIndex, selectedSeat, cantidadATransferir);
      } finally {
        setIsLoading(false);
        setSelectedSeat(null);
        onClose();
      }
    };

    const handleClose = () => {
      setSelectedSeat(null);
      setShowManualInput(false);
      setManualSeatValue("");
      onClose();
    };

    if (!show) return null;

    // Calcular columnas según cantidad de asientos disponibles (mínimo 1 para evitar grid vacío)
    const cols = Math.max(1, availableClients.length <= 3 ? availableClients.length : Math.min(3, Math.ceil(Math.sqrt(availableClients.length))));

    return (
      <div
        style={{
          position: "fixed",
          top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.6)",
          backdropFilter: "blur(5px)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 10000,
          padding: "16px",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            background: "white",
            borderRadius: "24px",
            padding: "24px",
            width: "100%",
            maxWidth: "400px",
            maxHeight: "90vh",
            overflowY: "auto",
            boxSizing: "border-box",
            boxShadow: "0 25px 50px -12px rgba(0,0,0,0.35)",
            border: "1px solid rgba(255,255,255,0.3)",
            animation: "modalSlideIn 0.2s ease-out",
          }}
        >
          {/* Título */}
          <h2
            style={{
              margin: "0 0 8px 0",
              fontSize: "clamp(1.1rem, 5vw, 1.4rem)",
              color: "#0b2b5c",
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              borderBottom: "2px solid #e2e8f0",
              paddingBottom: "12px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <i className="bi bi-person-arrow-right" style={{ fontSize: "1.2rem" }}></i>
            Transferir Asiento
          </h2>

          {/* Info del asiento actual */}
          <p
            style={{
              margin: "12px 0 16px",
              fontSize: "0.9rem",
              color: "#64748b",
              textAlign: "center",
            }}
          >
            Asiento actual:{" "}
            <strong style={{ color: "#0b2b5c" }}>
              {currentClient1Based > 0 ? currentClient1Based : "—"}
            </strong>
            {"  →  "}
            <strong style={{ color: selectedSeat !== null ? "#0a95c8" : "#94a3b8" }}>
              {selectedSeat !== null ? `Asiento ${selectedSeat + 1}` : "Selecciona destino"}
            </strong>
          </p>

          {/* Grid de asientos disponibles */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${cols}, 1fr)`,
              gap: "10px",
              background: "linear-gradient(145deg, #f1f5f9, #ffffff)",
              borderRadius: "20px",
              padding: "16px",
              marginBottom: "20px",
              boxShadow: "inset 0 2px 5px rgba(0,0,0,0.04), 0 10px 20px -8px rgba(0,0,0,0.1)",
            }}
          >
            {availableClients.map((c) => {
              const isSelected = selectedSeat === c.clientIndex;
              return (
                <button
                  key={c.clientIndex}
                  onClick={() => handleSeatClick(c.clientIndex)}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "14px 8px",
                    minHeight: "72px",
                    border: isSelected ? "2px solid #0a95c8" : "1px solid rgba(255,255,255,0.6)",
                    borderRadius: "16px",
                    background: isSelected
                      ? "linear-gradient(145deg, #0a95c8, #0879a8)"
                      : "white",
                    color: isSelected ? "white" : "#1f2937",
                    cursor: "pointer",
                    boxShadow: isSelected
                      ? "0 6px 0 #065a7a, 0 8px 20px -8px rgba(10,149,200,0.4)"
                      : "0 6px 0 #cbd5e1, 0 8px 20px -8px rgba(0,0,0,0.1)",
                    transition: "all 0.08s ease",
                    fontFamily: "inherit",
                    touchAction: "manipulation",
                    transform: isSelected ? "translateY(4px)" : "none",
                  }}
                >
                  <span
                    style={{
                      fontSize: "0.65rem",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      opacity: 0.75,
                      marginBottom: "2px",
                    }}
                  >
                    Asiento
                  </span>
                  <span style={{ fontSize: "1.8rem", fontWeight: 800, lineHeight: 1 }}>
                    {c.clientIndex + 1}
                  </span>
                </button>
              );
            })}

            {/* Botón para ingresar asiento manual (mesas grandes) */}
            <button
              onClick={() => {
                setShowManualInput((prev) => !prev);
                setSelectedSeat(null);
                setManualSeatValue("");
              }}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "14px 8px",
                minHeight: "72px",
                border: showManualInput ? "2px solid #f59e0b" : "1px dashed #94a3b8",
                borderRadius: "16px",
                background: showManualInput
                  ? "linear-gradient(145deg, #f59e0b, #d97706)"
                  : "white",
                color: showManualInput ? "white" : "#64748b",
                cursor: "pointer",
                boxShadow: showManualInput
                  ? "0 6px 0 #92400e, 0 8px 20px -8px rgba(245,158,11,0.4)"
                  : "0 6px 0 #cbd5e1, 0 8px 20px -8px rgba(0,0,0,0.1)",
                transition: "all 0.08s ease",
                fontFamily: "inherit",
                touchAction: "manipulation",
              }}
            >
              <span style={{ fontSize: "1.5rem", lineHeight: 1, fontWeight: 800 }}>+</span>
              <span style={{ fontSize: "0.6rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", opacity: 0.85, marginTop: "2px" }}>
                Otro
              </span>
            </button>
          </div>

          {/* Cantidad a transferir — Paso 2: solo aparece DESPUÉS de elegir
              el asiento destino, y solo si el producto tiene más de 1
              unidad (ej. 15 buffets). Captura por BOTONES (1..N + "Todo"),
              nunca por texto libre, para que sea imposible teclear una
              cantidad inválida (mayor al disponible, negativa, decimal,
              no numérica). "Transferir todo" sigue siendo la opción por
              defecto y usa exactamente la misma ruta de siempre. */}
          {esCantidadMultiple && selectedSeat !== null && (
            <div
              style={{
                marginBottom: "16px",
                background: "#eff6ff",
                border: "1px solid #bfdbfe",
                borderRadius: "16px",
                padding: "12px 14px",
              }}
            >
              <p style={{ margin: "0 0 10px", fontSize: "0.85rem", color: "#1e40af", fontWeight: 600 }}>
                Cantidad a transferir (producto tiene {cantidadTotalProducto} unidades)
              </p>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "8px",
                  marginBottom: "10px",
                  maxHeight: "160px",
                  overflowY: "auto",
                }}
              >
                {Array.from({ length: cantidadTotalProducto }, (_, i) => i + 1).map((n) => {
                  const activo = cantidadATransferir === String(n);
                  return (
                    <button
                      key={n}
                      onClick={() => setCantidadATransferir(String(n))}
                      style={{
                        minWidth: "44px",
                        minHeight: "44px",
                        padding: "8px 10px",
                        background: activo ? "#1e40af" : "white",
                        color: activo ? "white" : "#1e40af",
                        border: "1.5px solid #1e40af",
                        borderRadius: "10px",
                        fontWeight: 700,
                        fontSize: "0.95rem",
                        cursor: "pointer",
                        touchAction: "manipulation",
                      }}
                    >
                      {n}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => setCantidadATransferir(String(cantidadTotalProducto))}
                style={{
                  width: "100%",
                  padding: "10px 14px",
                  background: cantidadATransferir === String(cantidadTotalProducto) ? "#1e40af" : "white",
                  color: cantidadATransferir === String(cantidadTotalProducto) ? "white" : "#1e40af",
                  border: "1.5px solid #1e40af",
                  borderRadius: "10px",
                  fontWeight: 800,
                  fontSize: "0.85rem",
                  cursor: "pointer",
                  touchAction: "manipulation",
                }}
              >
                Transferir todo ({cantidadTotalProducto})
              </button>
              <p style={{ margin: "8px 0 0", fontSize: "0.75rem", color: "#64748b" }}>
                {parseFloat(cantidadATransferir) < cantidadTotalProducto
                  ? `El asiento actual se queda con ${cantidadTotalProducto - (parseFloat(cantidadATransferir) || 0)}.`
                  : "Se transferirá el artículo completo (igual que antes)."}
              </p>
            </div>
          )}

          {/* Input manual de asiento */}
          {showManualInput && (
            <div
              style={{
                display: "flex",
                gap: "8px",
                alignItems: "center",
                marginBottom: "16px",
                background: "#fffbeb",
                border: "1px solid #fcd34d",
                borderRadius: "16px",
                padding: "12px 14px",
              }}
            >
              <span style={{ fontSize: "0.85rem", color: "#92400e", fontWeight: 600, whiteSpace: "nowrap" }}>
                Asiento #
              </span>
              <input
                type="number"
                min="1"
                value={manualSeatValue}
                onChange={(e) => setManualSeatValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleManualConfirm(); }}
                placeholder="Ej: 47"
                autoFocus
                style={{
                  flex: 1,
                  padding: "8px 12px",
                  border: "1px solid #fcd34d",
                  borderRadius: "10px",
                  fontSize: "1.1rem",
                  fontWeight: 700,
                  color: "#1f2937",
                  outline: "none",
                  fontFamily: "inherit",
                  background: "white",
                  minWidth: 0,
                }}
              />
              <button
                onClick={handleManualConfirm}
                style={{
                  padding: "8px 16px",
                  background: "linear-gradient(145deg, #f59e0b, #d97706)",
                  border: "none",
                  borderRadius: "10px",
                  color: "white",
                  fontWeight: 800,
                  fontSize: "0.9rem",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  whiteSpace: "nowrap",
                  touchAction: "manipulation",
                }}
              >
                OK
              </button>
            </div>
          )}

          {/* Botones de acción */}
          <div style={{ display: "flex", gap: "10px" }}>
            <button
              onClick={handleClose}
              style={{
                flex: 1,
                padding: "14px 16px",
                border: "none",
                borderRadius: "40px",
                background: "linear-gradient(145deg, #dc2626, #b91c1c)",
                color: "white",
                fontWeight: 800,
                fontSize: "clamp(0.9rem, 4vw, 1rem)",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                cursor: "pointer",
                boxShadow: "0 6px 0 #7f1d1d",
                minHeight: "50px",
                touchAction: "manipulation",
                fontFamily: "inherit",
              }}
            >
              ✕ Cancelar
            </button>
            <button
              onClick={handleConfirm}
              disabled={selectedSeat === null || isLoading}
              style={{
                flex: 1,
                padding: "14px 16px",
                border: "none",
                borderRadius: "40px",
                background:
                  selectedSeat !== null && !isLoading
                    ? "linear-gradient(145deg, #0a95c8, #0879a8)"
                    : "#94a3b8",
                color: "white",
                fontWeight: 800,
                fontSize: "clamp(0.9rem, 4vw, 1rem)",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                cursor: selectedSeat !== null && !isLoading ? "pointer" : "not-allowed",
                boxShadow:
                  selectedSeat !== null && !isLoading
                    ? "0 6px 0 #065a7a"
                    : "0 4px 0 #64748b",
                minHeight: "50px",
                touchAction: "manipulation",
                opacity: selectedSeat === null || isLoading ? 0.6 : 1,
                fontFamily: "inherit",
              }}
            >
              {isLoading
                ? "Procesando..."
                : selectedSeat !== null
                ? (esCantidadMultiple && parseFloat(cantidadATransferir) < cantidadTotalProducto
                    ? `✓ Mover ${cantidadATransferir} a Asiento ${selectedSeat + 1}`
                    : `✓ Mover a Asiento ${selectedSeat + 1}`)
                : "Confirmar"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Funciones placeholder para otras acciones (ajusta según tu implementación)

  const handleApplyOpenDiscount = async (discountData, itemIndex) => {
    console.log('[handleApplyOpenDiscount] Iniciando:', discountData, 'al índice:', itemIndex);

    setIsLoading(true);
    try {
      // Validaciones básicas
      if (!chequeSeleccionado) {
        throw new Error('No hay cheque seleccionado');
      }

      const isGeneralDiscount = itemIndex === null || itemIndex === undefined;

      if (!isGeneralDiscount) {
        const index = parseInt(itemIndex);
        if (isNaN(index) || index < 0 || index >= order.length) {
          throw new Error('Índice de ítem inválido');
        }
        const selectedItem = order[index];
        if (!selectedItem) {
          throw new Error('Ítem no encontrado en el pedido');
        }
      }

      // Preparar payload para el backend
      const payload = {
        mesa: selectedRevenue?.Tipo === "Retail" ? "Retail 1" : selectedSpace?.name,
        numeroCheque: chequeSeleccionado,
        descuentoId: 1,
        nombreDescuento: discountData.nombre_descuento,
        empleado: currentUser.nombre,
        esDescuentoGeneral: isGeneralDiscount,
      };

      // Calcular orderTotal para descuento general
      let orderTotal = 0;
      if (isGeneralDiscount) {
        orderTotal = order.reduce((total, item) => {
          const itemPrice = item.price || 0;
          const modifiersTotal = item.modifiers
            ? item.modifiers.reduce((modTotal, mod) => modTotal + (mod.price * (mod.quantity || 1)), 0)
            : 0;
          return total + ((itemPrice + modifiersTotal) * (item.quantity || 1));
        }, 0);
        payload.montoTotalDescuento = discountData.tipo_descuento === 'percentage'
          ? parseFloat((orderTotal * (discountData.valor_original / 100)).toFixed(2))
          : parseFloat(Math.min(discountData.valor_original, orderTotal).toFixed(2));
      } else {
        const selectedItem = order[parseInt(itemIndex)];
        payload.articuloId = selectedItem.id_producto || selectedItem.id || selectedItem.item;
        const itemPrice = selectedItem.price || 0;
        const modifiersTotal = selectedItem.modifiers
          ? selectedItem.modifiers.reduce((total, mod) => total + (mod.price * (mod.quantity || 1)), 0)
          : 0;
        const totalAmount = (itemPrice + modifiersTotal) * (selectedItem.quantity || 1);
        payload.montoTotalDescuento = discountData.tipo_descuento === 'percentage'
          ? parseFloat((totalAmount * (discountData.valor_original / 100)).toFixed(2))
          : parseFloat(Math.min(discountData.valor_original, totalAmount).toFixed(2));
      }

      if (discountData.tipo_descuento === 'percentage') {
        payload.porcentaje = discountData.valor_original;
      } else {
        payload.monto = discountData.valor_original;
      }

      console.log('Enviando descuento al servidor. Payload:', payload);

      // Enviar solicitud al backend
      const apiUrl = `${LOCAL_API_URL}/actualizar-descuento.php?property=${encodeURIComponent(property)}`;
      console.log('Enviando descuento al servidor. URL:', apiUrl, 'Payload:', payload);
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const responseText = await response.text();
      console.log('Respuesta recibida:', responseText);

      if (!response.ok) {
        throw new Error(`Error HTTP ${response.status}: ${responseText}`);
      }

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (jsonError) {
        throw new Error(`La respuesta del servidor no es un JSON válido: ${responseText}`);
      }

      // Verificar que el descuento se aplicó correctamente
      if (!data.success) {
        const errorMessage = data.results && data.results[0]
          ? data.results[0].message
          : data.message || "Error en la respuesta del servidor";
        throw new Error(errorMessage);
      }

      // Confirmar que el cambio se realizó en la base de datos
      if (data.hasOwnProperty('descuentoConfirmado') && !data.descuentoConfirmado) {
        throw new Error("El descuento no se confirmó en la base de datos.");
      } else if (!data.hasOwnProperty('descuentoConfirmado')) {
        console.warn('[handleApplyOpenDiscount] Advertencia: El campo descuentoConfirmado no está presente en la respuesta del servidor.');
      }

      // Mostrar mensaje de éxito
      const successMessage = `✅ Descuento ${discountData.tipo_descuento === 'percentage' ? `${discountData.valor_original}%` : `$${payload.montoTotalDescuento.toFixed(2)}`} aplicado exitosamente`;
      console.log('✅ Descuento aplicado exitosamente');
      mostrarMensaje(successMessage);

      // ACTUALIZACIÓN INMEDIATA DEL ESTADO
      if (!data.hasOwnProperty('descuentoConfirmado') || data.descuentoConfirmado) {
        if (isGeneralDiscount) {
          setOrder(prevOrder => {
            const orderTotal = prevOrder.reduce((total, item) => {
              const itemPrice = item.price || 0;
              const modifiersTotal = item.modifiers
                ? item.modifiers.reduce((modTotal, mod) => modTotal + (mod.price * (mod.quantity || 1)), 0)
                : 0;
              return total + ((itemPrice + modifiersTotal) * (item.quantity || 1));
            }, 0);

            return prevOrder.map(item => {
              const itemPrice = item.price || 0;
              const modifiersTotal = item.modifiers
                ? item.modifiers.reduce((modTotal, mod) => modTotal + (mod.price * (mod.quantity || 1)), 0)
                : 0;
              const totalAmount = (itemPrice + modifiersTotal) * (item.quantity || 1);
              
              const discountAmount = discountData.tipo_descuento === 'percentage'
                ? parseFloat((totalAmount * (discountData.valor_original / 100)).toFixed(2))
                : parseFloat((totalAmount / orderTotal * discountData.valor_original).toFixed(2));
              
              return {
                ...item,
                discount: {
                  nombre_descuento: discountData.nombre_descuento,
                  price: discountData.tipo_descuento === 'percentage' ? discountData.valor_original : 0,
                  monto: discountAmount,
                },
                monto_descuento: discountAmount,
                nombre_descuento: discountData.nombre_descuento,
              };
            });
          });
        } else {
          const index = parseInt(itemIndex);
          setOrder(prevOrder => {
            const newOrder = [...prevOrder];
            const selectedItem = newOrder[index];
            
            const itemPrice = selectedItem.price || 0;
            const modifiersTotal = selectedItem.modifiers
              ? selectedItem.modifiers.reduce((total, mod) => total + (mod.price * (mod.quantity || 1)), 0)
              : 0;
            const totalAmount = (itemPrice + modifiersTotal) * (selectedItem.quantity || 1);
            
            const discountAmount = discountData.tipo_descuento === 'percentage'
              ? parseFloat((totalAmount * (discountData.valor_original / 100)).toFixed(2))
              : parseFloat(Math.min(discountData.valor_original, totalAmount).toFixed(2));
            
            newOrder[index] = {
              ...selectedItem,
              discount: {
                nombre_descuento: discountData.nombre_descuento,
                price: discountData.tipo_descuento === 'percentage' ? discountData.valor_original : 0,
                monto: discountAmount,
              },
              monto_descuento: discountAmount,
              nombre_descuento: discountData.nombre_descuento,
            };
            
            return newOrder;
          });
        }
        
        setOrderSummaryKey(prev => prev + 1);
      }

      // El descuento ya está guardado en DB (actualizar-descuento.php → 200)
      // y aplicado al state local arriba — no se necesita re-fetch.
      setShowOpenDiscountModal(false);

    } catch (error) {
      console.error('[handleApplyOpenDiscount] Error completo:', error);
      mostrarMensaje(`❌ Error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelCheque = async (numeroCheque) => {
  if (!allowedActions.includes("Cancelar Cheque")) {
    mostrarMensaje("No tienes permiso para cancelar cheques.");
    return;
  }

  if (!numeroCheque) {
    mostrarMensaje("Debes ingresar un número de cheque válido.");
    return;
  }

  // Confirmación adicional
  if (!window.confirm(`¿Estás SEGURO de que deseas cancelar el cheque #${numeroCheque}?\n\nEsta acción:\n- Generará un cheque negativo con todos los productos\n- Invertirá todos los pagos realizados\n- Marcará el cheque original como cancelado\n\nEsta operación NO se puede deshacer.`)) {
    return;
  }

  setIsCancellingCheque(true);

  try {
    const response = await fetch(`${LOCAL_API_URL}/cancelar_cheque.php?property=${encodeURIComponent(property)}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        numero_cheque: numeroCheque,
        userName: currentUser.nombre,
        userId: currentUser.id
      })
    });

    const responseText = await response.text();
    console.log('Respuesta cancelación:', responseText);

    if (!response.ok) {
      throw new Error(`Error HTTP ${response.status}: ${responseText}`);
    }

    let data;
    try {
      data = JSON.parse(responseText);
    } catch (jsonError) {
      throw new Error(`Respuesta inválida del servidor: ${responseText}`);
    }

    if (!data.success) {
      throw new Error(data.message || 'Error al cancelar el cheque');
    }

    // Éxito
    mostrarMensaje(`✅ Cheque #${numeroCheque} cancelado exitosamente. Nuevo cheque negativo: #${data.data.cheque_cancelacion}`);
    
    // Cerrar modal y limpiar
    setShowCancelChequeModal(false);
    
    // Opcional: Recargar lista de cheques
    await fetchCheques();
    
    // Si el cheque cancelado es el que está actualmente seleccionado, limpiar la orden
    if (chequeSeleccionado === numeroCheque) {
      setOrder([]);
      setChequeSeleccionado(null);
      setShowPOS(false);
      setShowRevenueSelector(true);
    }

  } catch (error) {
    console.error('[handleCancelCheque] Error:', error);
    mostrarMensaje(`❌ Error al cancelar el cheque: ${error.message}`);
  } finally {
    setIsCancellingCheque(false);
  }
};

  useEffect(() => {
    if (
      (promos.length > 0 || payments.length > 0 || revenues.length > 0) &&
      Object.keys(securityLevels).length > 0
    ) {
      const allActions = Object.values(securityLevels).flat();
      const missingPermissions = [
        ...promos.filter((promo) => !allActions.includes(promo.name)).map((promo) => ({
          type: "Promoción",
          name: promo.name
        })),
        ...payments.filter((method) => !allActions.includes(method.name)).map((method) => ({
          type: "Forma de pago",
          name: method.name
        })),
        ...revenues.filter((revenue) => !allActions.includes(revenue.name)).map((revenue) => ({
          type: "Centro de consumo",
          name: revenue.name
        }))
      ];
      if (missingPermissions.length > 0) {
        console.warn("Elementos sin permisos asignados:", missingPermissions);
        mostrarMensaje(
          `Advertencia: Algunos elementos no tienen permisos asignados: ${missingPermissions
            .map((item) => `${item.type} "${item.name}"`)
            .join(", ")}.`,
          5000
        );
      }
    }
  }, [promos, payments, revenues, securityLevels]);

  useEffect(() => {
    const loadInitialConfig = () => {
      const savedConfig = localStorage.getItem("currentDeviceConfig");
      const savedProperty = localStorage.getItem("currentProperty");

      if (savedConfig && savedProperty) {
        try {
          const config = JSON.parse(savedConfig);
          if (config && config.Propiedad && config.name && config.Rol) {
            // Config válida en localStorage → cargar sin validar contra API
            setDeviceConfig(config);
            setProperty(savedProperty);
            setCurrentProperty(savedProperty);
            setSelectedProperty(savedProperty);
            setIsConfigLoaded(true);
            return;
          }
        } catch {}
      }
      // Sin config → pantalla de registro
      setIsConfigLoaded(false);
      setSelectedProperty("");
    };
    loadInitialConfig();
  }, []);

  useEffect(() => {
    const loadSecurityLevels = async () => {
      if (!property) return;

      try {
        const data = await fetchData(
          BASE_API_JSON,
          property,
          "employees_sec.json",
          "Error cargando niveles de seguridad"
        );

        // 👇 Solo cambiamos esta parte:
        const securityLevelsMap = {};
        data.forEach(item => {
          securityLevelsMap[item.level] = item.actions;
        });
        setSecurityLevels(securityLevelsMap);

        console.log(`Niveles de seguridad cargados para ${property}:`, securityLevelsMap);
        console.log('Current User Seguridad:', currentUser?.seguridad);
        console.log('Allowed Actions:', securityLevelsMap[currentUser?.seguridad] || []);

      } catch (error) {
        console.error(`Error al cargar employees_sec.json para ${property}:`, error);
        setSecurityLevels({});
        mostrarMensaje(
          "Error crítico: No se pudieron cargar los niveles de seguridad. Contacta al administrador.",
          10000
        );
      }
    };

    loadSecurityLevels();
  }, [property, currentUser]);

  // ✅ OPTIMIZACIÓN: se eliminó la carga inicial automática de cheques aquí
  // (lectura_cheques.php). El estado `cheques` de este componente nunca se
  // lee directamente en el render — todos los flujos que lo necesitan
  // (conteo de clientes, transferir cliente, cancelar cheque, etc.) hacen
  // su propio `await fetchCheques()` justo antes de usar el resultado, así
  // que no dependen de esta carga inicial. Mientras tanto, RevenueSelector
  // ya hace esta misma consulta al montar (fetchSpaceStatus en su propio
  // loadAllData), así que este efecto solo duplicaba esa llamada a la base
  // de datos en cada login sin que nada leyera su resultado.

  // ===== FULLSCREEN AUTOMÁTICO AL INICIAR SESIÓN =====
  useEffect(() => {
    if (!isLoggedIn) return;
    const enterFullscreen = async () => {
      try {
        const el = document.documentElement;
        if (el.requestFullscreen) {
          await el.requestFullscreen();
        } else if (el.webkitRequestFullscreen) {
          await el.webkitRequestFullscreen();
        } else if (el.mozRequestFullScreen) {
          await el.mozRequestFullScreen();
        }
      } catch (err) {
        console.warn('[Fullscreen] No se pudo activar automáticamente:', err.message);
      }
    };
    enterFullscreen();
  }, [isLoggedIn]);

  // ===== RESETEAR SCROLL AL INICIO CUANDO CAMBIA LA SECCIÓN ACTIVA =====
  // Necesario en móvil donde el footer es un elemento fijo y el scroll persiste entre secciones
  useEffect(() => {
    if (footerRef.current) {
      footerRef.current.scrollLeft = 0;
    }
  }, [showOrderSection, showPaymentsSection, showExpressSection, showAdminSection]);

  // ===== SINCRONIZAR ESTADO isFullscreen CON EL NAVEGADOR =====
  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', onFullscreenChange);
    document.addEventListener('mozfullscreenchange', onFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', onFullscreenChange);
      document.removeEventListener('mozfullscreenchange', onFullscreenChange);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem("currentProperty", property);
    setCurrentProperty(property);
  }, [property]);

  useEffect(() => {
    const loadProperties = async () => {
      const url = `${BASE_API_URL}/properties.php`;
      console.log(`[App - loadProperties] Fetching properties from: ${url}`);

      try {
        const response = await fetch(url, {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
          credentials: "omit",
        });

        if (!response.ok) {
          const errorText = await response.text().catch(() => "No response body");
          throw new Error(`HTTP ${response.status}: ${errorText}`);
        }

        const text = await response.text();
        if (text.trim().startsWith("<!DOCTYPE") || text.trim().startsWith("<html")) {
          throw new Error("Invalid response: HTML received instead of JSON");
        }

        const data = JSON.parse(text);
        console.log(`[App - loadProperties] Parsed data:`, data);

        if (!data.properties || !Array.isArray(data.properties)) {
          throw new Error("Invalid response: 'properties' is not an array");
        }

        if (data.properties.length === 0) {
          setPropertySelectorError("No properties found.");
          setProperties([]);
          mostrarMensaje("No se encontraron propiedades.", 5000);
          return;
        }

        setProperties(data.properties);
        setPropertySelectorError(null);
        console.log(`[App - loadProperties] Properties loaded:`, data.properties);
      } catch (error) {
        console.error(`[App - loadProperties] Error:`, error);
        setPropertySelectorError(`Error loading properties: ${error.message}`);
        setProperties([]);
        mostrarMensaje(`Error al cargar propiedades: ${error.message}`, 5000);
      }
    };

    loadProperties();
  }, []);

  useEffect(() => {
    if (!selectedProperty) { setDevices([]); return; }
    const loadDevices = async () => {
      setIsLoadingDevices(true);
      setPropertySelectorError(null);
      try {
        const data = await fetchData(BASE_API_JSON, selectedProperty, "devices.json", "");
        const devList = data.devices || [];
        setDevices(devList);
        if (devList.length === 0) setPropertySelectorError("No hay dispositivos para esta propiedad.");
      } catch {
        setPropertySelectorError("Propiedad no encontrada o sin conexión.");
        setDevices([]);
      } finally {
        setIsLoadingDevices(false);
      }
    };
    loadDevices();
  }, [selectedProperty]);

  const mostrarMensaje = (texto, duracion = 3000) => {
    // Si el texto parece una clave (sin espacios y en minúscula/guiones)
    const isTranslationKey = typeof texto === 'string' && 
      (texto.includes('_') || (texto === texto.toLowerCase() && !texto.includes(' ')));
    
    const mensajeTexto = isTranslationKey ? t(texto) : texto;
    setMensaje(mensajeTexto);
    setTimeout(() => setMensaje(null), duracion);
  };

  const handleAutoFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAutoConfigLoading(true);
    setAutoConfigFound(null);
    setPropertySelectorError(null);
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const cfg = JSON.parse(evt.target.result);
        if (!cfg.Propiedad || !cfg.name) {
          setPropertySelectorError('El archivo no contiene Propiedad o name.');
        } else {
          setAutoConfigFound(cfg);
        }
      } catch {
        setPropertySelectorError('El archivo no es un JSON válido.');
      } finally {
        setAutoConfigLoading(false);
        e.target.value = '';
      }
    };
    reader.onerror = () => {
      setPropertySelectorError('No se pudo leer el archivo.');
      setAutoConfigLoading(false);
    };
    reader.readAsText(file);
  };

  const loadAutoConfig = () => {
    setAutoConfigFound(null);
    setPropertySelectorError(null);
    autoFileInputRef.current?.click();
  };

  // Aplica la configuración automática encontrada
  const applyAutoConfig = async (config) => {
    setAutoConfigLoading(true);
    setPropertySelectorError(null);
    try {
      const data = await fetchData(BASE_API_JSON, config.Propiedad, "devices.json", "");
      const device = (data.devices || []).find(d => d.name === config.name);
      if (!device) {
        setPropertySelectorError(`Dispositivo "${config.name}" no encontrado en ${config.Propiedad}.`);
        return;
      }
      const fullConfig = { ...device, Propiedad: config.Propiedad, timestamp: new Date().toISOString() };
      localStorage.setItem("currentDeviceConfig", JSON.stringify(fullConfig));
      localStorage.setItem("currentProperty", config.Propiedad);
      saveConfigBackup(fullConfig);
      setDeviceConfig(fullConfig);
      setProperty(config.Propiedad);
      setCurrentProperty(config.Propiedad);
      setIsConfigLoaded(true);
      mostrarMensaje(`Dispositivo registrado: ${config.Propiedad} — ${config.name}`, 3000);
    } catch (err) {
      setPropertySelectorError('Error al validar configuración: ' + err.message);
    } finally {
      setAutoConfigLoading(false);
    }
  };

  const handleSelectPropertyAndDevice = () => {
    if (!selectedProperty) {
      setPropertySelectorError("Por favor selecciona una propiedad.");
      return;
    }
    if (!selectedDevice) {
      setPropertySelectorError("Por favor selecciona un dispositivo.");
      return;
    }

    const deviceConfig = devices.find((device) => device.name === selectedDevice);
    if (!deviceConfig) {
      setPropertySelectorError("Dispositivo no válido.");
      return;
    }

    // Validar PRNTT para POS
    if (deviceConfig.Rol === "POS" && deviceConfig.PRNTT) {
      const printer = devices.find(
        (device) => device.name === deviceConfig.PRNTT && device.Rol === "PRNT"
      );
      if (!printer) {
        setPropertySelectorError(`Impresora ${deviceConfig.PRNTT} no encontrada.`);
        return;
      }
      if (!printer.Config?.[0]?.Tipo) {
        setPropertySelectorError(`Impresora ${deviceConfig.PRNTT} no tiene tipo definido.`);
        return;
      }
      console.log(`Seleccionado ${deviceConfig.name}, asociado a ${printer.name} (Tipo: ${printer.Config[0].Tipo})`);
    }

    const fullConfig = {
      ...deviceConfig,
      Propiedad: selectedProperty,
      timestamp: new Date().toISOString(),
    };

    setProperty(selectedProperty);
    setDeviceConfig(fullConfig);
    setIsConfigLoaded(true);
    localStorage.setItem("currentDeviceConfig", JSON.stringify(fullConfig));
    localStorage.setItem("currentProperty", selectedProperty);

    // ── Guardar respaldo automático en OPFS + IndexedDB ──
    saveConfigBackup(fullConfig).then(({ opfs, idb }) => {
      console.log(`[App] Respaldo de configuración guardado — OPFS: ${opfs}, IDB: ${idb}`);
    });

    mostrarMensaje(`Dispositivo registrado: ${selectedProperty} - ${selectedDevice}`, 3000);
  };

  useEffect(() => {
    // ✅ OPTIMIZACIÓN: las 6 cargas siguientes son independientes entre sí
    // (ninguna usa el resultado de otra), así que ahora viajan en paralelo
    // con Promise.allSettled en lugar de una tras otra con await secuencial.
    // Se usa allSettled (no Promise.all) para conservar el comportamiento
    // original: si UNA carga falla, las demás que sí tuvieron éxito igual
    // aplican su estado (antes, un fallo a mitad de la cadena secuencial no
    // deshacía lo ya aplicado antes de él). El mensaje de error genérico se
    // muestra si al menos una carga falló, igual que antes.
    // lista_promos.json corre en paralelo con las demás pero conserva su
    // propio try/catch (no participa en hadError): si el archivo no existe
    // todavía en alguna propiedad, no debe romper la carga del resto.
    const loadPropertyData = async () => {
      if (!property) return;

      const listaPromosPromise = (async () => {
        try {
          const listaPromosData = await fetchData(
            BASE_API_JSON,
            property,
            "lista_promos.json",
            "Error cargando catálogo de promociones"
          );
          setListaPromos(Array.isArray(listaPromosData?.promociones) ? listaPromosData.promociones : []);
        } catch (listaPromosError) {
          console.warn("[Promo] lista_promos.json no disponible para esta propiedad:", listaPromosError.message);
          setListaPromos([]);
        }
      })();

      const [
        configResult,
        promosResult,
        paymentsResult,
        revenuesResult,
        voidsReasonsResult,
        devicesResult,
      ] = await Promise.allSettled([
        fetchData(BASE_API_JSON, property, "config.json", "Error cargando configuración"),
        fetchData(BASE_API_JSON, property, "promos.json", "Error cargando promociones"),
        fetchData(BASE_API_JSON, property, "payments.json", "Error cargando formas de pago"),
        fetchData(BASE_API_JSON, property, "revenues.json", "Error cargando centros de consumo"),
        fetchData(BASE_API_JSON, property, "voidsReasons.json", "Error cargando razones de cancelación"),
        fetchData(BASE_API_JSON, property, "devices.json", "Error cargando dispositivos"),
      ]);

      let hadError = false;

      if (configResult.status === "fulfilled") {
        setPropertyConfig(configResult.value);
        setApiUrl(`${BASE_API_URL}/void.php?property=${property}`);
        setApiPago(`${BASE_API_URL}/pagos.php?property=${property}`);
        setApiDescuento(`${BASE_API_URL}/aplicar_descuento.php?property=${property}`);
      } else {
        hadError = true;
        console.error(`Error al cargar config.json de ${property}:`, configResult.reason);
      }

      if (promosResult.status === "fulfilled") {
        setPromos(Array.isArray(promosResult.value) ? promosResult.value : []);
      } else {
        hadError = true;
        console.error(`Error al cargar promos.json de ${property}:`, promosResult.reason);
      }

      if (paymentsResult.status === "fulfilled") {
        setPayments(Array.isArray(paymentsResult.value) ? paymentsResult.value : []);
      } else {
        hadError = true;
        console.error(`Error al cargar payments.json de ${property}:`, paymentsResult.reason);
      }

      if (revenuesResult.status === "fulfilled") {
        setRevenues(revenuesResult.value);
      } else {
        hadError = true;
        console.error(`Error al cargar revenues.json de ${property}:`, revenuesResult.reason);
      }

      if (voidsReasonsResult.status === "fulfilled") {
        setVoidsReasons(Array.isArray(voidsReasonsResult.value) ? voidsReasonsResult.value : []);
      } else {
        hadError = true;
        console.error(`Error al cargar voidsReasons.json de ${property}:`, voidsReasonsResult.reason);
      }

      if (devicesResult.status === "fulfilled") {
        const loadedDevices = Array.isArray(devicesResult.value.devices) ? devicesResult.value.devices : [];
        console.log("Devices cargados en loadPropertyData:", loadedDevices.map(d => ({
          name: d.name,
          rol: d.Rol,
          prntt: d.PRNTT || "N/A",
          tipo: d.Config?.[0]?.Tipo || "No definido"
        })));

        // Validar impresoras
        const printers = loadedDevices.filter(d => d.Rol === "PRNT");
        if (printers.length === 0) {
          console.warn("No se encontraron impresoras para la propiedad:", property);
          mostrarMensaje("Advertencia: No hay impresoras configuradas.", 5000);
        }
        printers.forEach(printer => {
          if (!printer.Config?.[0]?.Tipo) {
            console.warn(`Impresora ${printer.name} no tiene tipo definido`);
            mostrarMensaje(`Advertencia: La impresora ${printer.name} no tiene tipo definido.`, 5000);
          }
        });

        setDevices(loadedDevices);
      } else {
        hadError = true;
        console.error(`Error al cargar devices.json de ${property}:`, devicesResult.reason);
      }

      if (hadError) {
        mostrarMensaje(
          "No se pudo cargar la configuración. Asegúrate de haber registrado una propiedad válida.",
          5000
        );
      }

      await listaPromosPromise;
    };
    loadPropertyData();
  }, [property]);

  const handleShowPagos = () => {
    setShowOrderSection(false);
    setShowPaymentsSection(true);
    setShowAdminSection(false);
    setShowExpressSection(false);
  };

  const handleShowPedidos = () => {
    setShowOrderSection(true);
    setShowPaymentsSection(false);
    setShowAdminSection(false);
    setShowExpressSection(false);
  };

  const handleShowAdmin = () => {
    setShowAdminSection(!showAdminSection);
    setShowOrderSection(false);
    setShowPaymentsSection(false);
    setShowExpressSection(false);
  };
  
  // En el componente App, añadir después de handleShowReportes (línea 686):
  const handleShowExpress = () => {
    setShowExpressSection(true);
    setShowOrderSection(false);
    setShowPaymentsSection(false);
    setShowAdminSection(false);
    setShowReportesSection(false);
  };

  const handleShowReportes = () => {
    setShowReportesSection((prev) => !prev);
  };

const handleLoginSuccess = async (user) => {
  // 1. Validar y preparar usuario
  const validatedUser = { 
    ...user, 
    seguridad: Number(user.seguridad) || 4,
    idioma: user.idioma ? String(user.idioma).toUpperCase().trim() : 'ES'
  };
  
  console.log('🔐 LOGIN SUCCESS - Usuario:', validatedUser.nombre, 'Idioma:', validatedUser.idioma);
  
  // 2. Forzar carga inicial de idioma
  if (changeLanguage) {
    console.log('🔄 Forzando carga inicial de idioma:', validatedUser.idioma);
    changeLanguage(validatedUser.idioma);
  }
  
  // 3. Guardar usuario PRIMERO
  setIsLoggedIn(true);
  setCurrentUser(validatedUser);
  localStorage.setItem("currentUser", JSON.stringify(validatedUser));
  
  // 4. Guardar preferencia de idioma
  if (property) {
    localStorage.setItem(`language_pref_${property}`, validatedUser.idioma);
  }
  
  // 5. ✅ LIMPIAR ESTADOS DE LA SESIÓN ANTERIOR
  setOrder([]);
  setSelectedRevenue(null);
  setSelectedSpace(null);
  setClientCount(null);
  setFilteredProducts([]);
  setChequeSeleccionado(null);
  setNombreVendedor(null);
  setCurrentGroup(null);
  setCurrentCategory(null);
  setAppliedPayments([]);
  setPaymentMethod(null);
  setSelectedOrderIndex(null);
  setSelectedPaymentIndex(null);
  setSelectedClient(0);
  setShowChequeList(false);
  
  // 6. ✅ MOSTRAR REVENUE SELECTOR INMEDIATAMENTE
  setShowPOS(false);
  setShowRevenueSelector(true);
  
  console.log('✅ RevenueSelector debería mostrarse ahora - showRevenueSelector:', true);

  // ✅ OPTIMIZACIÓN: se eliminó el segundo fetch de employees.json aquí.
  // Login.js YA cargó employees.json para validar las credenciales, y el
  // "idioma" del empleado encontrado ahí viaja dentro de `user` (ver
  // userWithLanguage en Login.js) hasta `validatedUser.idioma` (paso 1
  // arriba). Volver a pedir employees.json y volver a buscar al mismo
  // empleado por id/usr/nombre solo para releer el mismo campo `idioma`
  // era una llamada de red redundante que no cambiaba el resultado.

  // 8. Mensaje de bienvenida
  mostrarMensaje(`Bienvenido ${validatedUser.nombre}`);
};

const handleLogout = () => {
  setIsLoggedIn(false);
  setCurrentUser(null);
  localStorage.removeItem("currentUser");
  
  // Limpiar todos los estados
  setOrder([]);
  setSelectedRevenue(null);
  setSelectedSpace(null);
  setClientCount(null);
  setFilteredProducts([]);
  setChequeSeleccionado(null);
  setNombreVendedor(null);
  setCurrentGroup(null);
  setCurrentCategory(null);
  setAppliedPayments([]);
  setPaymentMethod(null);
  setSelectedOrderIndex(null);
  setSelectedPaymentIndex(null);
  setSelectedClient(0);
  setShowChequeList(false);
  setShowPOS(false);
  setShowReportesSection(false);
  
  // Mostrar RevenueSelector después del logout
  setShowRevenueSelector(true);
};

  // En el componente App, añadir después de handleApplyDiscount (línea 1157):
  const handleApplyExpressDiscountToAll = (promo) => {
    if (!allowedActions.includes(promo.name)) {
      mostrarMensaje(`No tienes permiso para aplicar el descuento "${promo.name}".`);
      return;
    }

    if (!order || order.length === 0) {
      mostrarMensaje("No hay productos en el pedido para aplicar el descuento.");
      return;
    }

    let appliedCount = 0;
    const updatedOrder = order.map((item) => {
      if (!item.id && !item.id_producto) {
        console.error("Producto sin identificador válido", item);
        mostrarMensaje(`Error: Producto "${item.name}" sin ID válido`);
        return item;
      }

      // Verificar si el descuento es aplicable al producto
      const isApplicable = promo.products.some((p) => Number(p.id) === Number(item.item));
      if (!isApplicable) {
        return item; // No aplicar descuento si el producto no es elegible
      }

      appliedCount++;
      const updatedItem = {
        ...item,
        discount: promo,
        total: calculateItemTotal({ ...item, discount: promo }),
      };

      return updatedItem;
    });

    const discountsToApply = [];
    order.forEach((item) => {
      if (!item.id && !item.id_producto) return;

      const isApplicable = promo.products.some((p) => Number(p.id) === Number(item.item));
      if (!isApplicable) return;

      const mainProductId = item.id_producto || item.id;
      const mainItemId = item.item;

      discountsToApply.push({
        id: mainProductId,
        itemId: mainItemId,
        discount: promo,
        originalPrice: item.price,
        finalPrice: calculateItemTotal({ ...item, discount: promo }) / item.quantity,
        modifiers: [],
      });

      if (item.modifiers && item.modifiers.length > 0) {
        item.modifiers.forEach((modifier) => {
          const modifierId = modifier.id_producto || modifier.id;
          if (!modifierId) {
            console.error("Modificador sin ID válido", modifier);
            return;
          }

          discountsToApply.push({
            id: modifierId,
            itemId: modifier.item || modifierId,
            discount: promo,
            originalPrice: modifier.price,
            finalPrice: modifier.price * (1 - promo.price / 100),
            parentItem: mainProductId,
            isModifier: true,
          });
        });
      }
    });

    setOrder(updatedOrder);
    setPendingDiscounts((prev) => [...prev, ...discountsToApply]);
    mostrarMensaje(`Descuento "${promo.name}" aplicado a ${appliedCount} ${appliedCount === 1 ? 'elemento' : 'elementos'} del pedido.`);
  };

  // En App.jsx, reemplazar handleToggleExpressDiscount (línea ~1270):
  const handleToggleExpressDiscount = async (promo) => {
    if (!allowedActions.includes(promo.name)) {
      mostrarMensaje(`No tienes permiso para aplicar el descuento "${promo.name}".`);
      return;
    }

    if (!order || order.length === 0) {
      mostrarMensaje("No hay productos en el pedido para aplicar el descuento.");
      return;
    }

    const isDiscountApplied = order.some(
      (item) => item.discount && item.discount.id === promo.id
    );

    let updatedOrder;
    let discountsToApply = [];

    if (isDiscountApplied) {
      updatedOrder = order.map((item) => {
        if (item.discount && item.discount.id === promo.id) {
          return {
            ...item,
            discount: null,
            total: calculateItemTotal({ ...item, discount: null }),
          };
        }
        return item;
      });

      setPendingDiscounts((prev) =>
        prev.filter((d) => d.discount.id !== promo.id)
      );
      mostrarMensaje(`Descuento "${promo.name}" removido del pedido.`);
    } else {
      let appliedCount = 0;
      updatedOrder = order.map((item) => {
        if (!item.id && !item.id_producto) {
          console.error("Producto sin identificador válido", item);
          mostrarMensaje(`Error: Producto "${item.name}" sin ID válido`);
          return item;
        }

        const isApplicable = promo.products.some(
          (p) => Number(p.id) === Number(item.item)
        );
        if (!isApplicable) {
          return item;
        }

        appliedCount++;
        return {
          ...item,
          discount: promo,
          total: calculateItemTotal({ ...item, discount: promo }),
        };
      });

      order.forEach((item) => {
        if (!item.id && !item.id_producto) return;

        const isApplicable = promo.products.some(
          (p) => Number(p.id) === Number(item.item)
        );
        if (!isApplicable) return;

        const mainProductId = item.id_producto || item.id;
        const mainItemId = item.item;

        discountsToApply.push({
          id: mainProductId,
          itemId: mainItemId,
          discount: promo,
          originalPrice: item.price,
          finalPrice: calculateItemTotal({ ...item, discount: promo }) / item.quantity,
          modifiers: [],
        });

        if (item.modifiers && item.modifiers.length > 0) {
          item.modifiers.forEach((modifier) => {
            const modifierId = modifier.id_producto || modifier.id;
            if (!modifierId) {
              console.error("Modificador sin ID válido", modifier);
              return;
            }

            discountsToApply.push({
              id: modifierId,
              itemId: modifier.item || modifierId,
              discount: promo,
              originalPrice: modifier.price,
              finalPrice: modifier.price * (1 - promo.price / 100),
              parentItem: mainProductId,
              isModifier: true,
            });
          });
        }
      });

      setPendingDiscounts((prev) => [...prev, ...discountsToApply]);
      await processExpressDiscounts(); // Procesar inmediatamente en la base de datos
      mostrarMensaje(`Descuento "${promo.name}" aplicado y procesado para ${appliedCount} ${appliedCount === 1 ? 'elemento' : 'elementos'} del pedido.`);
    }

    setOrder(updatedOrder);
  };

  // En App.jsx, después de handleToggleExpressDiscount:
  const processExpressDiscounts = async () => {
    if (!allowedActions.includes("Descuentos")) {
      mostrarMensaje("No tienes permiso para procesar descuentos.");
      return;
    }
    if (!pendingDiscounts || pendingDiscounts.length === 0) {
      mostrarMensaje("No hay descuentos pendientes para procesar.");
      return;
    }
    try {
      if (!currentUser?.nombre) {
        throw new Error("No se ha identificado al usuario");
      }
      if (!chequeSeleccionado) {
        throw new Error("No hay un cheque seleccionado");
      }

      const uniqueDiscounts = [...new Map(
        pendingDiscounts.map((item) => [`${item.id}-${item.discount.id}`, item])
      ).values()];

      const results = await Promise.allSettled(
        uniqueDiscounts.map(async (pending) => {
          try {
            if (!pending.discount || !pending.id) {
              throw new Error("Datos de descuento incompletos");
            }

            const body = {
              mesa: selectedRevenue.Tipo === "Retail" ? "Retail 1" : selectedSpace?.name || "Sin especificar",
              articuloId: pending.id,
              numeroCheque: chequeSeleccionado,
              descuentoId: pending.discount.id,
              porcentaje: pending.discount.price,
              nombreDescuento: pending.discount.name,
              empleado: currentUser.nombre,
            };

            const urls = [
              API_DESCUENTO,
              `${LOCAL_API_URL}/aplicar_descuento.php?property=${property}`,
            ];

            let response;
            let responseText;
            let lastError;

            for (const url of urls) {
              try {
                response = await fetch(url, {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                    "X-Property": property,
                  },
                  body: JSON.stringify(body),
                  credentials: 'omit',
                });

                responseText = await response.text();

                if (response.ok) {
                  break;
                } else {
                  lastError = new Error(`Error ${response.status}: ${responseText}`);
                }
              } catch (fetchError) {
                lastError = new Error(`Error de red: ${fetchError.message}`);
              }
            }

            if (!response || !response.ok) {
              throw lastError || new Error("No se pudo conectar con ninguna URL.");
            }

            const responseData = JSON.parse(responseText);
            if (!responseData.success) {
              throw new Error(responseData.message || "Error en la respuesta del servidor");
            }

            return responseData;
          } catch (error) {
            console.error("Error en descuento:", pending.id, error);
            throw error;
          }
        })
      );

      const errores = results.filter((r) => r.status === "rejected");
      if (errores.length > 0) {
        throw new Error(`${errores.length} descuentos fallaron`);
      }

      setPendingDiscounts([]);
      mostrarMensaje("✅ Descuentos procesados exitosamente");
    } catch (error) {
      console.error("[processExpressDiscounts] Error:", error);
      mostrarMensaje(`⚠️ Error al procesar descuentos: ${error.message}`);
      throw error;
    }
  };

  // En App.jsx, después de processExpressDiscounts:
  const handleRemoveAllExpressDiscounts = () => {
    if (!allowedActions.includes("Quitar descuento")) {
      mostrarMensaje("No tienes permiso para quitar descuentos.");
      return;
    }
    if (!order.some((item) => item.discount)) {
      mostrarMensaje("No hay descuentos aplicados para remover.");
      return;
    }

    const updatedOrder = order.map((item) => {
      if (item.discount) {
        return {
          ...item,
          discount: null,
          total: calculateItemTotal({ ...item, discount: null }),
        };
      }
      return item;
    });

    setOrder(updatedOrder);
    setPendingDiscounts([]);
    mostrarMensaje("Todos los descuentos han sido removidos del pedido.");
  };

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const loadProducts = async () => {
      if (!selectedRevenue?.id || !property) return;

      setLoadingProducts(true);
      try {
        const data = await fetchData(
          BASE_API_JSON,
          property,
          `products_${selectedRevenue.id}.json`,
          "Error cargando productos",
          selectedRevenue.id
        );

        if (isMounted) {
          const newProducts = Array.isArray(data) ? data : [];
          console.log(`[loadProducts] Productos cargados:`, newProducts.length, 'productos');
          const product482 = newProducts.find(p => p.id === 482);
          if (product482) {
            console.log('[loadProducts] Producto id: 482:', product482);
            console.log('[loadProducts] Modifiers para id: 482:', JSON.stringify(product482.modifiers, null, 2));
          } else {
            console.warn("[loadProducts] Producto id: 482 no encontrado");
            mostrarMensaje("El producto Pechuga Empanizada no está disponible. Contacta al administrador.", 5000);
          }
          setFilteredProducts((prev) =>
            JSON.stringify(prev) === JSON.stringify(newProducts) ? prev : newProducts
          );
        }
      } catch (error) {
        if (isMounted) setFilteredProducts([]);
        console.error("Error loading products:", error);
        mostrarMensaje("Error al cargar los productos.");
      } finally {
        if (isMounted) setLoadingProducts(false);
      }
    };

    loadProducts();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [selectedRevenue, property]);

  useEffect(() => {
    const cargarPedidoExistente = async () => {
      if (selectedSpace && !chequeSeleccionado) {
        try {
          const nombreMesa = selectedRevenue.Tipo === "Retail" ? "Retail 1" : selectedSpace.name;
          const chequeAbierto = await buscarChequeAbierto(property, nombreMesa);

          if (chequeAbierto) {
            if (!puedeAbrirMesaDeOtroVendedor(chequeAbierto.nombre_vendedor)) {
              mostrarMensaje(MENSAJE_MESA_OTRO_VENDEDOR);
              setOrder([]);
              setChequeSeleccionado(null);
              setSelectedSpace(null);
              setShowRevenueSelector(true);
              setShowPOS(false);
              return;
            }
            const productosCargados = chequeAbierto.pedidos.flatMap((pedido) =>
              pedido.productos.map((producto) => ({
                ...producto,
                numero_pedido: pedido.numero_pedido,
                modifiers: producto.modifiers || [],
                client: producto.client || 0,
                quantity: producto.quantity || 1,
                total: calculateItemTotal(producto),
                isFromDB: true,
                id: producto.id_producto,
                item: producto.item || producto.id_producto,
              }))
            );

            setOrder(productosCargados);
            setChequeSeleccionado(chequeAbierto.numero_cheque);
            setNombreVendedor(chequeAbierto.nombre_vendedor || null);
            // Usar SOLO el campo de conteo real de clientes; ignorar .cliente como fallback
            // para evitar confundir "cliente seleccionado" con "total de clientes"
            const clientes = parseInt(chequeAbierto.clientes, 10) || 1;
            setClientCount(clientes);
            // Siempre arrancar en Cliente 1 (índice 0) como indica el flujo de venta
            setSelectedClient(0);
            mostrarMensaje(`Cargado el cheque abierto para ${nombreMesa}`);
          } else {
            setOrder([]);
            setSelectedClient(0);
            mostrarMensaje(`No hay cheque abierto para ${nombreMesa}. Puedes agregar nuevos productos.`);
          }
        } catch (error) {
          console.error("Error al cargar el pedido:", error);
          setOrder([]);
          setSelectedClient(0);
          setChequeSeleccionado(null);
          setClientCount(1);
          mostrarMensaje("Error al cargar el pedido. Inténtalo de nuevo.");
        }
      }
    };

    cargarPedidoExistente();
  }, [selectedSpace, selectedRevenue, chequeSeleccionado, property]);

const handleChequeSeleccionado = async (cheque) => {
  try {
    setOrder([]);
    setSelectedClient(0);
    setClientCount(1);
    setChequeSeleccionado(null);
    setCurrentGroup(null);
    setCurrentCategory(null);
    setAppliedPayments([]);
    setSelectedOrderIndex(null);
    setSelectedPaymentIndex(null);

    const revenueConfig = revenues.find((r) => r.name === cheque.revenue);
    if (!revenueConfig) {
      console.error("No se encontró el revenue correspondiente:", cheque.revenue);
      mostrarMensaje("Error: No se encontró el revenue correspondiente.");
      return;
    }
    setSelectedRevenue({
      name: revenueConfig.name,
      Tipo: revenueConfig.Tipo,
      id: revenueConfig.id,
    });

    setSelectedSpace({ name: cheque.mesa });
    // Usar SOLO el campo de conteo real; no usar .cliente como fallback de count
    const clientes = parseInt(cheque.clientes, 10) || 1;
    setClientCount(clientes);
    // Cliente 1 (índice 0) siempre por defecto al cargar
    setSelectedClient(0);
    // Solo actualizar si el backend devuelve el campo (undefined = servidor sin el campo aún)
    if (cheque.referencia_lespera !== undefined) {
      setCustomerNamePending(cheque.referencia_lespera || null);
    }

    setChequeSeleccionado(cheque.numero_cheque);
    setNombreVendedor(cheque.nombre_vendedor || null);

    const productosCargados = cheque.pedidos.flatMap((pedido) =>
      pedido.productos.map((producto) => ({
        ...producto,
        id: producto.id_producto,
        name: producto.nombre,
        price: parseFloat(producto.precio),
        modifiers: (producto.modificadores || []).map((m) => ({
          id: m.id,
          name: m.nombre,
          price: parseFloat(m.precio),
          quantity: parseInt(m.cantidad, 10) || 1,
          monto_descuento: parseFloat(m.monto_descuento) || 0,
          // ✅ LEER CARGO DEL MODIFICADOR
          cargo: parseFloat(m.cargo_servicio) || parseFloat(m.cargo) || 0,
        })),
        quantity: parseInt(producto.cantidad, 10),
        client: Math.max(0, parseInt(producto.asiento, 10) - 1),
        tax: parseFloat(producto.impuesto) || 0,
        // ✅ LEER CARGO DEL PRODUCTO
        cargo: parseFloat(producto.cargo_servicio) || parseFloat(producto.cargo) || 0,
        item: producto.item || producto.id_producto,
        discount: producto.descuento
          ? {
              id: producto.descuento.id,
              name: producto.descuento.nombre,
              price: parseFloat(producto.descuento.monto_descuento),
            }
          : null,
        isFromDB: true,
      }))
    );
    setOrder(productosCargados);

    setShowRevenueSelector(false);
    setShowPOS(true);
    mostrarMensaje(`Cargado el cheque abierto para ${cheque.mesa}`);
    setShowChequeList(false);
  } catch (error) {
    console.error("Error al cargar el cheque seleccionado:", error);
    mostrarMensaje("Hubo un error al cargar el cheque seleccionado. Inténtalo de nuevo.");
  }
};

  const handleOccupiedTableClick = async (cheque) => {
    try {
      console.log("Handling occupied table click with cheque:", cheque);

      if (!cheque || !cheque.numero_cheque || !cheque.mesa || !cheque.revenue) {
        throw new Error("Datos del cheque incompletos");
      }

      if (!puedeAbrirMesaDeOtroVendedor(cheque.nombre_vendedor)) {
        mostrarMensaje(MENSAJE_MESA_OTRO_VENDEDOR);
        return;
      }

      // Función para parsear números, inspirada en ChequeList.js
      const parseNumber = (value) => {
        if (typeof value === "number") return value;
        const normalized = String(value).replace(/\./g, "").replace(/,/g, ".");
        const parsed = parseFloat(normalized);
        return isNaN(parsed) ? 0 : parsed;
      };

      // Inicializar estados
      setOrder([]);
      setSelectedClient(0);
      setClientCount(1);
      setChequeSeleccionado(null);
      setCurrentGroup(null);
      setCurrentCategory(null);
      setAppliedPayments([]);
      setSelectedOrderIndex(null);
      setSelectedPaymentIndex(null);

      // Configurar revenue
      const revenueConfig = revenues.find((r) => r.name === cheque.revenue);
      if (!revenueConfig) {
        throw new Error(`No se encontró el revenue correspondiente: ${cheque.revenue}`);
      }

      setSelectedRevenue({
        name: revenueConfig.name,
        Tipo: revenueConfig.Tipo,
        id: revenueConfig.id,
      });

      setSelectedSpace({ name: cheque.mesa });
      const clientes = parseNumber(cheque.clientes || cheque.cliente || 1);
      setClientCount(clientes);
      const clienteSeleccionado = parseNumber(cheque.cliente || 0);
      setSelectedClient(clienteSeleccionado);
      // Solo actualizar si el backend devuelve el campo (undefined = servidor sin el campo aún)
      if (cheque.referencia_lespera !== undefined) {
        setCustomerNamePending(cheque.referencia_lespera || null);
      }

      setChequeSeleccionado(cheque.numero_cheque);
      setNombreVendedor(cheque.nombre_vendedor || null);

      // Parsear productos
      const productosCargados = cheque.pedidos?.flatMap((pedido) => {
        if (!pedido.productos || !Array.isArray(pedido.productos)) {
          console.warn("Invalid productos in pedido:", pedido);
          return [];
        }
        return pedido.productos.map((producto) => {
          if (!producto.id_producto || !producto.nombre || !producto.cantidad) {
            console.warn("Invalid producto:", producto);
            return null;
          }
          return {
            ...producto,
            id: producto.id_producto,
            name: producto.nombre,
            price: parseNumber(producto.precio),
            modifiers: (producto.modificadores || []).map((m) => ({
              id: m.id || "unknown",
              name: m.nombre || "unknown",
              price: parseNumber(m.precio),
              quantity: parseNumber(m.cantidad) || 1,
              monto_descuento: parseNumber(m.monto_descuento) || 0,
            })).filter(mod => mod.id && mod.name),
            quantity: parseNumber(producto.cantidad),
            client: Math.max(0, (parseNumber(producto.asiento) || 1) - 1),
            tax: parseNumber(producto.impuesto) || 0,
            cargo: parseNumber(producto.cargo_servicio) || 0,
            item: producto.item || producto.id_producto,
            discount: producto.descuento
              ? {
                  id: producto.descuento.id || "unknown",
                  name: producto.descuento.nombre || "unknown",
                  price: parseNumber(producto.descuento.monto_descuento),
                }
              : null,
            isFromDB: true,
          };
        }).filter(p => p !== null);
      }) || [];

      console.log("Productos cargados:", productosCargados);
      setOrder(productosCargados);

      // Mostrar el POS
      setShowRevenueSelector(false);
      setShowPOS(true);
      setShowChequeList(false);

      mostrarMensaje(`Cargado el cheque abierto para ${cheque.mesa}`);
    } catch (error) {
      console.error("Error al cargar el cheque de la mesa ocupada:", error);
      mostrarMensaje(`Error al cargar el cheque: ${error.message}`);
      setShowRevenueSelector(true);
      setShowPOS(false);
    }
  };

  const handleCalculateTotals = (data) => {
    setCalculatedTotals((prev) => {
      if (JSON.stringify(prev) !== JSON.stringify(data)) {
        return data;
      }
      return prev;
    });
  };

  const imprimirCuentaCliente = async (clientIndex) => {
    if (!chequeSeleccionado) {
      mostrarMensaje('No hay cheque seleccionado');
      return;
    }

    try {
      // Filtrar productos del cliente específico
      const productosCliente = order.filter(item => item.client === clientIndex);
      
      if (productosCliente.length === 0) {
        mostrarMensaje(`El cliente ${clientIndex + 1} no tiene productos`);
        return;
      }

      // Calcular total del cliente
      const totalCliente = productosCliente.reduce((sum, item) => sum + calculateItemTotal(item), 0);

      // Preparar datos para impresión
      const ticketData = {
        productos: productosCliente,
        total: totalCliente,
        mesa: selectedRevenue.Tipo === "Retail" 
          ? "Retail" 
          : `${selectedSpace?.name || "Mesa"} (Cliente ${clientIndex + 1})`,
        revenue: selectedRevenue?.name || "Revenue",
        usuario: currentUser.nombre,
        chequeId: chequeSeleccionado,
        esCuentaIndividual: true,
        cliente: clientIndex + 1
      };

      // Llamar a la función de impresión
      const result = await printTicket(
        ticketData,
        totalCliente,
        [], // No pagos para cuenta individual
        property,
        deviceConfig,
        selectedRevenue,
        selectedSpace,
        chequeSeleccionado,
        currentUser,
        {
          ...deviceConfig,
          isCuentaCliente: true,
          cliente: clientIndex + 1
        }
      );

      if (result.success) {
        mostrarMensaje(`✅ Ticket impreso para cliente ${clientIndex + 1}`);
      } else {
        throw new Error(result.message || 'Error al imprimir');
      }
    } catch (error) {
      console.error('Error al imprimir cuenta de cliente:', error);
      mostrarMensaje(`⚠️ Error al imprimir: ${error.message}`);
    }
  };
  const handlePartialOrder = async (newProducts) => {
  try {
    const mesa = selectedRevenue.Tipo === "Retail" ? "Retail 1" : selectedSpace.name;
    const clienteQuePidio = selectedClient + 1;
    const clientes = selectedRevenue.Tipo === "Retail" ? 1 : clientCount;

    const data = await fetchData(
      BASE_API_JSON,
      property,
      `products_${selectedRevenue.id}.json`,
      "Error cargando productos",
      selectedRevenue.id
    );
    const products = Array.isArray(data) ? data : [];

    const partialOrderData = {
      deviceName: deviceConfig.name,
      accountNumber: chequeSeleccionado,
      userId: currentUser?.id,
      userName: currentUser?.nombre,
      revenueId: selectedRevenue.id,
      revenueName: selectedRevenue.name,
      mesa,
      clientes,
      clienteQuePidio,
      order: newProducts.map((p) => {
        const productConfig = products.find((prod) => prod.id === p.id) || {};
        
        // Asegurar que quantity sea número decimal
        const quantity = parseFloat(p.quantity) || 0;
        
        // ✅ OBTENER CARGO DEL PRODUCTO (del producto o de propertyConfig)
        // Primero intentar del producto, luego de la configuración de la propiedad
        let cargoValue = parseFloat(p.cargo) || 0;
        
        // Si el producto no tiene cargo, intentar obtener de propertyConfig
        if (cargoValue === 0 && propertyConfig && propertyConfig.cargo_servicio) {
          cargoValue = parseFloat(propertyConfig.cargo_servicio) || 0;
        }
        
        // Si aún es 0, intentar de la configuración del revenue
        if (cargoValue === 0 && selectedRevenue && selectedRevenue.cargo_servicio) {
          cargoValue = parseFloat(selectedRevenue.cargo_servicio) || 0;
        }
        
        // Crear el objeto base del producto
        const productData = {
          id: p.id,
          quantity: quantity,
          modifiers: p.modifiers.map(mod => ({
            id: mod.id,
            name: mod.name,
            price: parseFloat(mod.price) || 0,
            quantity: parseFloat(mod.quantity) || 1,
            productid: mod.productid,
            // ✅ INCLUIR CARGO EN MODIFICADORES
            cargo: parseFloat(mod.cargo) || cargoValue,
          })),
          client: p.client + 1, // convertir 0-based → 1-based para PHP
          price: parseFloat(p.price) || 0,
          tax: parseFloat(p.tax) || 0,
          // ✅ INCLUIR CARGO EN EL PRODUCTO PRINCIPAL
          cargo: cargoValue,
          name: p.name,
          KDS: productConfig.KDS || [],
          void_reason: p.void_reason || null,
          void_id: p.void_id || null,
          void_name: p.void_name || null,
          void_itemid: p.void_itemid || null,
          void: p.void || 0,
        };

        // INCLUIR DATOS DE DESCUENTO PARA PRODUCTOS CANCELADOS (VOID)
        if (quantity < 0) {
          if (p.discount || p.monto_descuento || p.nombre_descuento) {
            productData.id_descuento = p.discount?.id || p.id_descuento || 0;
            productData.nombre_descuento = p.discount?.name || p.discount?.nombre_descuento || p.nombre_descuento || "";
            productData.monto_descuento = parseFloat(p.monto_descuento) || 0;
            productData.porcentaje_descuento = parseFloat(p.discount?.price || p.porcentaje_descuento) || 0;
          }
        }

        return productData;
      }),
      total: newProducts.reduce((sum, p) => {
        return sum + calculateItemTotal({
          ...p,
          quantity: parseFloat(p.quantity) || 0,
          price: parseFloat(p.price) || 0
        });
      }, 0),
    };

    console.log('Enviando datos al backend (handlePartialOrder) con cargo:', 
      partialOrderData.order.map(o => ({
        name: o.name,
        quantity: o.quantity,
        cargo: o.cargo,
        modifiers: o.modifiers.map(m => ({ name: m.name, cargo: m.cargo }))
      }))
    );
    
    await handlePedido(partialOrderData, mostrarMensaje);
  } catch (error) {
    console.error("Error al procesar el pedido parcial:", error);
    mostrarMensaje("Hubo un error al procesar el pedido parcial. Inténtalo de nuevo.");
  }
};

const handleFullOrder = async (allProducts, mesa, cliente, revenue, asiento) => {
    try {
      const clientes = selectedRevenue.Tipo === "Retail" ? 1 : clientCount;

      const data = await fetchData(
        BASE_API_JSON,
        property,
        `products_${selectedRevenue.id}.json`,
        "Error cargando productos",
        selectedRevenue.id
      );
      const products = Array.isArray(data) ? data : [];

      const fullOrderData = {
        deviceName: deviceConfig.name,
        accountNumber: 0,
        userId: currentUser?.id,
        userName: currentUser?.nombre,
        revenueId: selectedRevenue.id,
        revenueName: revenue,
        mesa,
        clientes,
        referencia_lespera: customerNamePending || null,
        clienteQuePidio: cliente,
        order: allProducts.map((p) => {
          const productConfig = products.find((prod) => prod.id === p.id) || {};
          
          // Asegurar que quantity sea número decimal
          const quantity = parseFloat(p.quantity) || 0;
          
          // ✅ OBTENER CARGO DEL PRODUCTO
          let cargoValue = parseFloat(p.cargo) || 0;
          
          if (cargoValue === 0 && propertyConfig && propertyConfig.cargo_servicio) {
            cargoValue = parseFloat(propertyConfig.cargo_servicio) || 0;
          }
          
          if (cargoValue === 0 && selectedRevenue && selectedRevenue.cargo_servicio) {
            cargoValue = parseFloat(selectedRevenue.cargo_servicio) || 0;
          }
          
          // Crear el objeto base del producto
          const productData = {
            id: p.id,
            quantity: quantity,
            modifiers: p.modifiers.map((mod) => ({
              id: mod.id,
              name: mod.name,
              price: parseFloat(mod.price) || 0,
              quantity: parseFloat(mod.quantity) || 1,
              productid: mod.productid,
              // ✅ INCLUIR CARGO EN MODIFICADORES
              cargo: parseFloat(mod.cargo) || cargoValue,
            })),
            client: typeof p.client === 'number' ? p.client + 1 : cliente, // convertir 0-based → 1-based para PHP
            price: parseFloat(p.price) || 0,
            tax: parseFloat(p.tax) || 0,
            // ✅ INCLUIR CARGO EN EL PRODUCTO PRINCIPAL
            cargo: cargoValue,
            name: p.name,
            KDS: productConfig.KDS || [],
            void_reason: p.void_reason || null,
          };

          // INCLUIR DATOS DE DESCUENTO PARA PRODUCTOS CANCELADOS (VOID)
          if (quantity < 0) {
            if (p.discount || p.monto_descuento || p.nombre_descuento) {
              productData.id_descuento = p.discount?.id || p.id_descuento || 0;
              productData.nombre_descuento = p.discount?.name || p.discount?.nombre_descuento || p.nombre_descuento || "";
              productData.monto_descuento = parseFloat(p.monto_descuento) || 0;
              productData.porcentaje_descuento = parseFloat(p.discount?.price || p.porcentaje_descuento) || 0;
            }
          }

          return productData;
        }),
        total: allProducts.reduce((sum, p) => {
          return sum + calculateItemTotal({
            ...p,
            quantity: parseFloat(p.quantity) || 0,
            price: parseFloat(p.price) || 0
          });
        }, 0),
      };

      console.log('Enviando datos al backend (handleFullOrder) con cargo:', 
        fullOrderData.order.map(o => ({
          name: o.name,
          quantity: o.quantity,
          cargo: o.cargo,
          modifiers: o.modifiers.map(m => ({ name: m.name, cargo: m.cargo }))
        }))
      );
      
      const result = await handlePedido(fullOrderData, mostrarMensaje);
      if (!result?.data?.numero_cheque) {
        throw new Error("No se recibió un número de cheque válido.");
      }

      setNumeroCheque(result.data.numero_cheque);
      setChequeSeleccionado(result.data.numero_cheque);
      // No limpiar customerNamePending — ya fue guardado en Referencia_Lespera
      // y debe seguir visible en el header mientras la mesa esté activa
      return { numeroCheque: result.data.numero_cheque };
    } catch (error) {
      console.error("[handleFullOrder] Error:", error);
      throw error;
    }
  };

const cargarChequeActualizado = async (chequeId, chequesList) => {
  try {
    const cheque = chequesList.find(c => c.numero_cheque == chequeId);
    if (!cheque) throw new Error(`No se encontró el cheque #${chequeId}`);

    setChequeSeleccionado(cheque.numero_cheque);
    setNombreVendedor(cheque.nombre_vendedor || null);

    const productosCargados = cheque.pedidos.flatMap((pedido) =>
      pedido.productos.map((producto) => ({
        ...producto,
        id: producto.id_producto,
        name: producto.nombre,
        price: parseFloat(producto.precio) || 0,
        modifiers: (producto.modificadores || []).map((m) => ({
          id: m.id,
          name: m.nombre,
          price: parseFloat(m.precio) || 0,
          quantity: parseFloat(m.cantidad) || 1,
          monto_descuento: parseFloat(m.monto_descuento) || 0,
          // ✅ LEER CARGO DEL MODIFICADOR DESDE LA DB
          cargo: parseFloat(m.cargo_servicio) || parseFloat(m.cargo) || 0,
        })),
        quantity: parseFloat(producto.cantidad) || 0,
        client: Math.max(0, parseInt(producto.asiento, 10) - 1), // normalizar 1-based DB → 0-based estado
        tax: parseFloat(producto.impuesto) || 0,
        // ✅ LEER CARGO DEL PRODUCTO DESDE LA DB
        cargo: parseFloat(producto.cargo_servicio) || parseFloat(producto.cargo) || 0,
        item: producto.item || producto.id_producto,
        discount: producto.descuento
          ? {
              id: producto.descuento.id,
              name: producto.descuento.nombre,
              price: parseFloat(producto.descuento.monto_descuento) || 0,
            }
          : null,
        isFromDB: true,
      }))
    );

    console.log("Productos cargados con cargo:", productosCargados.map(p => ({
      name: p.name,
      quantity: p.quantity,
      price: p.price,
      cargo: p.cargo,
      modifiers: p.modifiers.map(m => ({ name: m.name, cargo: m.cargo }))
    })));

    setOrder(productosCargados);
    return cheque;
  } catch (error) {
    console.error("[cargarChequeActualizado] Error:", error);
    mostrarMensaje(`Error al cargar el cheque actualizado: ${error.message}`);
    throw error;
  }
};

  // En App.jsx, reemplazar handleSaveOrder:
const handleSaveOrder = async () => {
  if (isOrderingRef.current) return; // bloqueo doble clic
  if (order.length === 0) {
    mostrarMensaje("No hay productos en el pedido.");
    return;
  }

  isOrderingRef.current = true;
  setIsOrdering(true);
  try {
    const [newProducts, existingProducts] = order.reduce(
      ([newAcc, existingAcc], product) => {
        if (product.isFromDB) {
          existingAcc.push(product);
        } else {
          newAcc.push(product);
        }
        return [newAcc, existingAcc];
      },
      [[], []]
    );

    let chequeId = chequeSeleccionado;

    if (chequeSeleccionado) {
      if (newProducts.length > 0) {
        console.log("Enviando nuevos productos con cantidades:", 
          newProducts.map(p => ({ name: p.name, quantity: p.quantity }))
        );
        
        await handlePartialOrder(newProducts);
        
        // Pequeño delay para asegurar que el backend procesó
        await new Promise(resolve => setTimeout(resolve, 500));
        
        const updatedCheques = await fetchCheques();
        const cheque = updatedCheques.find(c => c.numero_cheque == chequeSeleccionado);

        if (!cheque) throw new Error(`No se encontró el cheque #${chequeSeleccionado}`);

        await cargarChequeActualizado(chequeSeleccionado, updatedCheques);
        
        if (pendingDiscounts.length > 0) {
          await processExpressDiscounts();
        }
        
        // Forzar actualización del OrderSummary
        setOrderSummaryKey(prev => prev + 1);
        
        mostrarMensaje("Nuevos productos agregados al cheque existente.");
      } else {
        mostrarMensaje("No hay nuevos productos para agregar al cheque existente.");
      }
    } else {
      const mesa = selectedRevenue.Tipo === "Retail" ? "Retail 1" : selectedSpace?.name;
      const cliente = selectedClient + 1 || 1;
      const revenue = selectedRevenue?.name || "";
      const asiento = cliente;

      const result = await handleFullOrder(order, mesa, cliente, revenue, asiento);
      if (!result || !result.numeroCheque) {
        throw new Error("No se pudo crear un nuevo cheque.");
      }
      chequeId = result.numeroCheque;

      const updatedCheques = await fetchCheques();
      await cargarChequeActualizado(chequeId, updatedCheques);

      if (pendingDiscounts.length > 0) {
        await processExpressDiscounts();
      }
      
      setOrderSummaryKey(prev => prev + 1);
      mostrarMensaje("Nuevo cheque creado con todos los productos.");
    }
  } catch (error) {
    console.error("[handleSaveOrder] Error:", error);
    mostrarMensaje(`Hubo un error al procesar el pedido: ${error.message}`);
  } finally {
    isOrderingRef.current = false;
    setIsOrdering(false);
  }
};
  const handleVoid = async () => {
    if (!allowedActions.includes("Void")) {
      mostrarMensaje("No tienes permiso para realizar la acción Void.");
      return;
    }
    if (selectedOrderIndex === null) {
      mostrarMensaje("No hay ningún artículo seleccionado.");
      return;
    }

    const selectedItem = order[selectedOrderIndex];

    if (!selectedItem.isFromDB) {
      mostrarMensaje("Este artículo no puede ser anulado con Void. Usa Eliminar.");
      return;
    }

    setShowVoidReasonsModal(true);
  };

  const confirmVoid = async (reason) => {
    if (!reason || selectedOrderIndex === null) {
      mostrarMensaje("Error: Selecciona una razón y un producto.");
      return;
    }

    const selectedItem = order[selectedOrderIndex];
    if (!selectedItem) {
      mostrarMensaje("Error: Producto no encontrado.");
      return;
    }

    if (!selectedItem.isFromDB) {
      mostrarMensaje("Error: Usa Eliminar para este producto.");
      return;
    }

    if (!chequeSeleccionado) {
      mostrarMensaje("Error: No hay cheque seleccionado.");
      return;
    }

    // Validación final antes de proceder
    const productoId = selectedItem.id_producto || selectedItem.id;
    const yaFueCancelado = order.some(item => {
      const itemProductoId = item.id_producto || item.id;
      return itemProductoId === productoId && item.quantity < 0 && item.isFromDB === false;
    });

    if (yaFueCancelado) {
      mostrarMensaje(`❌ Error: El producto (ID: ${productoId}) ya fue cancelado. No se puede cancelar nuevamente.`);
      setShowVoidReasonsModal(false);
      setSelectedVoidReason(null);
      return;
    }

    try {
      const originalQuantity = Number(selectedItem.quantity) || 1;
      const negativeQuantity = -Math.abs(originalQuantity);

      // PRESERVAR TODOS LOS DATOS DE DESCUENTO DEL PRODUCTO ORIGINAL
      const voidedProduct = {
        ...selectedItem,
        quantity: negativeQuantity,
        price: Math.abs(Number(selectedItem.price)),
        total: calculateItemTotal({
          ...selectedItem,
          quantity: negativeQuantity,
          price: Math.abs(Number(selectedItem.price)),
          modifiers: selectedItem.modifiers
            ? selectedItem.modifiers.map((mod) => ({
                ...mod,
                quantity: 1,
                price: Math.abs(Number(mod.price)),
                // Preservar datos de descuento del modificador
                ...(mod.monto_descuento && {
                  id_descuento: mod.id_descuento || 0,
                  nombre_descuento: mod.nombre_descuento || "",
                  monto_descuento: mod.monto_descuento,
                  descuento_porcentaje: mod.descuento_porcentaje || 0
                })
              }))
            : [],
        }),
        isFromDB: false,
        void_reason: reason.name || reason.description || reason.id || "Sin razón",
        void_id: reason.id,
        void_name: reason.name || reason.description || "Sin razón",
        void_itemid: selectedItem.pedido_id || selectedItem.id,
        void: 1,
        KDS: selectedItem.KDS || [],
        client: selectedItem.client || selectedClient,
        id: selectedItem.id || selectedItem.id_producto,
        name: selectedItem.name || selectedItem.nombre,
        
        // PRESERVAR TODOS LOS DATOS DE DESCUENTO DEL PRODUCTO PRINCIPAL
        discount: selectedItem.discount || null,
        id_descuento: selectedItem.id_descuento || selectedItem.discount?.id || 0,
        nombre_descuento: selectedItem.nombre_descuento || selectedItem.discount?.name || selectedItem.discount?.nombre_descuento || "",
        monto_descuento: selectedItem.monto_descuento || 0,
        porcentaje_descuento: selectedItem.porcentaje_descuento || selectedItem.discount?.price || 0,
        
        // Preservar también datos de descuento en modificadores
        modifiers: selectedItem.modifiers ? selectedItem.modifiers.map(mod => ({
          ...mod,
          // Preservar datos de descuento del modificador
          ...(mod.monto_descuento && {
            id_descuento: mod.id_descuento || 0,
            nombre_descuento: mod.nombre_descuento || "",
            monto_descuento: mod.monto_descuento,
            descuento_porcentaje: mod.descuento_porcentaje || 0
          })
        })) : [],
        
        // Guardar referencia al producto original para validaciones futuras
        producto_original_id: productoId,
      };

      console.log('Producto cancelado con datos de descuento:', {
        nombre: voidedProduct.name,
        id_descuento: voidedProduct.id_descuento,
        nombre_descuento: voidedProduct.nombre_descuento,
        monto_descuento: voidedProduct.monto_descuento,
        porcentaje_descuento: voidedProduct.porcentaje_descuento,
        modifiers: voidedProduct.modifiers
      });

      setOrder((prevOrder) => {
        const existingIndex = prevOrder.findIndex(
          (item) =>
            item.id === voidedProduct.id &&
            item.client === voidedProduct.client &&
            item.void_reason === voidedProduct.void_reason &&
            JSON.stringify(item.modifiers) === JSON.stringify(voidedProduct.modifiers) &&
            !item.isFromDB
        );

        if (existingIndex !== -1) {
          return prevOrder.map((item, index) =>
            index === existingIndex
              ? {
                  ...item,
                  quantity: item.quantity + negativeQuantity,
                  total: calculateItemTotal({
                    ...item,
                    quantity: item.quantity + negativeQuantity,
                    price: Math.abs(Number(item.price)),
                    modifiers: item.modifiers,
                  }),
                }
              : item
          );
        }
        return [...prevOrder, voidedProduct];
      });

      setShowVoidReasonsModal(false);
      setSelectedVoidReason(null);
      setSelectedOrderIndex(null);

      mostrarMensaje(
        `Producto "${selectedItem.name}" (ID: ${productoId}) añadido como cancelación. Presiona "Ordenar" para confirmar.`
      );
    } catch (error) {
      console.error("Error en confirmVoid:", error);
      mostrarMensaje(`❌ Error: ${error.message}`);
    }
  };

  const total = calculateTotal(order);
  const totalPagado = parseFloat(
    appliedPayments.reduce((sum, p) => sum + p.amount, 0).toFixed(2)
  );
  const restante = parseFloat((calculatedTotals.totalCalculado - totalPagado).toFixed(2));
  const isPagoCompleto = restante <= 0;

const confirmOrder = async () => {
  if (!allowedActions.includes("Confirmar Pago")) {
    mostrarMensaje("No tienes permiso para confirmar pagos.");
    return;
  }

  // Evita doble clic mientras ya hay una confirmación en curso.
  if (isConfirmingPayment) return;
  setIsConfirmingPayment(true);

  try {
    if (!chequeSeleccionado) {
      throw new Error("No hay un cheque seleccionado.");
    }

    // ==============================================
    // 1. DETECTAR PAGOS DE HOTEL Y PROPINA TOTAL
    // ==============================================
    const hotelPayments = appliedPayments.filter(p => p.isHotelPayment === true);
    
    // ✅ CALCULAR PROPINA TOTAL DE TODOS LOS PAGOS
    const totalPropina = appliedPayments.reduce((sum, p) => {
      return sum + (parseFloat(p.tip || p.propinaAplicada || 0) || 0);
    }, 0);
    
    console.log("💝 Propina total a aplicar:", totalPropina);
    
    // Si hay pagos de hotel, procesarlos PRIMERO
    if (hotelPayments.length > 0) {
      console.log("🏨 Procesando pagos de hotel:", hotelPayments);
      
      // Obtener configuración de la propiedad
      const propertyConfigData = propertyConfig || await fetchData(
        BASE_API_JSON,
        property,
        "config.json",
        "Error cargando configuración"
      );
      
      // Obtener productos para mapear categorías
      const productsData = await fetchData(
        BASE_API_JSON,
        property,
        `products_${selectedRevenue.id}.json`,
        "Error cargando productos",
        selectedRevenue.id
      );
      const products = Array.isArray(productsData) ? productsData : [];
      
      // Crear mapa de productos por ID
      const productMap = {};
      products.forEach(product => {
        productMap[product.id] = product;
      });
      
      // ==============================================
      // 2. CALCULAR SUBTOTALES POR CATEGORÍA (INCLUYENDO PROPINA)
      // ==============================================
      let subtotalAlimentos = 0;
      let subtotalBebidas = 0;
      let totalImpuestos = 0;
      let subtotalPropina = totalPropina; // Propina como subtotal separado
      
      // Procesar cada producto del pedido (solo cantidades positivas)
      order.forEach(item => {
        if (item.quantity > 0) {
          const productInfo = productMap[item.id] || {};
          const categoria = (productInfo.category || item.categoria || '').toLowerCase();
          const precioUnitario = Math.abs(parseFloat(item.price) || 0);
          const cantidad = Math.abs(parseFloat(item.quantity) || 1);
          const totalProducto = precioUnitario * cantidad;
          
          // Calcular impuesto del producto
          const taxPorcentaje = parseFloat(item.tax || productInfo.tax || 16);
          const factor = 1 + (taxPorcentaje / 100);
          const subtotalSinImpuesto = totalProducto / factor;
          const impuestoProducto = totalProducto - subtotalSinImpuesto;
          
          totalImpuestos += impuestoProducto;
          
          // Clasificar por categoría
          if (categoria.includes('bebida') || categoria.includes('cerveza') || categoria.includes('vino')) {
            subtotalBebidas += subtotalSinImpuesto;
          } else {
            subtotalAlimentos += subtotalSinImpuesto;
          }
          
          // Procesar modificadores
          if (item.modifiers && item.modifiers.length > 0) {
            item.modifiers.forEach(mod => {
              if (mod.quantity > 0) {
                const modTotal = Math.abs(parseFloat(mod.price) || 0) * Math.abs(parseFloat(mod.quantity) || 1);
                const modTax = parseFloat(mod.tax || taxPorcentaje);
                const modFactor = 1 + (modTax / 100);
                const modSinImpuesto = modTotal / modFactor;
                
                if (categoria.includes('bebida')) {
                  subtotalBebidas += modSinImpuesto;
                } else {
                  subtotalAlimentos += modSinImpuesto;
                }
                totalImpuestos += modTotal - modSinImpuesto;
              }
            });
          }
        }
      });
      
      const totalGeneral = subtotalAlimentos + subtotalBebidas + totalImpuestos + subtotalPropina;
      
      // ==============================================
      // 3. OBTENER CONFIGURACIÓN DE TURNOS
      // ==============================================
      const ifac_hdepto = propertyConfigData?.ifac_hdepto || [];
      const depto = propertyConfigData?.Depto || '1';
      const now = new Date();
      const horaActual = now.toTimeString().split(' ')[0];
      
      let turnoId = '2'; // Default: COMIDA
      let turnoNombre = 'COMIDA';
      
      const configModo = ifac_hdepto.find(m => 
        m.depto === depto && 
        horaActual >= m.inicio && 
        (m.fin >= m.inicio ? horaActual <= m.fin : horaActual >= m.inicio || horaActual <= m.fin)
      );
      
      if (configModo) {
        turnoId = configModo.modoid;
        turnoNombre = configModo.modo || (turnoId === '1' ? 'DESAYUNO' : turnoId === '2' ? 'COMIDA' : 'CENA');
      }
      
      // ==============================================
      // 4. PROCESAR CADA PAGO DE HOTEL (INCLUYENDO PROPINA)
      // ==============================================
      for (const hotelPayment of hotelPayments) {
        if (!hotelPayment.roomNumber) {
          throw new Error(`❌ El pago ${hotelPayment.method.name} no tiene número de habitación.`);
        }
        
        // ✅ Incluir propina proporcional en el pago de hotel
        const montoPago = parseFloat(hotelPayment.amount) || 0;
        const propinaPago = parseFloat(hotelPayment.tip || hotelPayment.propinaAplicada || 0) || 0;
        const montoTotalConPropina = montoPago + propinaPago;
        
        // Construir payload para el endpoint CON PROPINA
        const cargoPayload = {
          chequeId: chequeSeleccionado,
          roomNumber: hotelPayment.roomNumber,
          property: property,
          propina: propinaPago,        // ✅ PROPINA INCLUIDA
          montoTotal: montoTotalConPropina
        };
        
        console.log("📤 Enviando cargo a habitación con propina:", cargoPayload);
        
        // URL del endpoint PHP
        const cargoUrl = `https://www.bts.ei-solutions.co/shopeis/app_menu/cargo-habitacion.php`;
        
        try {
          const cargoResponse = await fetch(cargoUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify(cargoPayload)
          });
          
          const cargoResponseText = await cargoResponse.text();
          console.log('📥 Respuesta cargo-habitacion:', cargoResponseText);
          
          let cargoData;
          try {
            cargoData = JSON.parse(cargoResponseText);
          } catch (jsonError) {
            cargoData = { success: true, message: "Cargo procesado", raw_response: cargoResponseText };
          }
          
          // Verificar éxito
          const isSuccess = cargoData.success === true || 
                           cargoData.success === "true" || 
                           cargoData.estado === "exitoso" ||
                           cargoData.status === "ok";
          
          if (!isSuccess && cargoData.message) {
            throw new Error(cargoData.message);
          }
          
          // Guardar datos del cargo en el pago
          hotelPayment.hotelData = {
            success: true,
            ...cargoData,
            referencia: cargoData.referencia || `CHK-${chequeSeleccionado}-${Date.now()}`,
            monto: montoPago,
            propina: propinaPago,
            montoTotal: montoTotalConPropina,
            roomNumber: hotelPayment.roomNumber
          };
          
          mostrarMensaje(`✅ Cargo a habitación ${hotelPayment.roomNumber}: $${montoPago.toFixed(2)} + Propina: $${propinaPago.toFixed(2)} = $${montoTotalConPropina.toFixed(2)}`);
          
        } catch (cargoError) {
          console.error("❌ Error en cargo:", cargoError);
          throw new Error(`Error al procesar cargo a habitación ${hotelPayment.roomNumber}: ${cargoError.message}`);
        }
      }
      
      // Verificar que TODOS los pagos de hotel se procesaron
      for (const hotelPayment of hotelPayments) {
        if (!hotelPayment.hotelData?.success) {
          throw new Error(`❌ Pago a habitación ${hotelPayment.roomNumber} no fue procesado correctamente.`);
        }
      }
    }

    // ==============================================
    // 5. CONTINUAR CON EL PAGO NORMAL (pagos.php)
    // ==============================================
    const numeroCheque = Number(chequeSeleccionado);
    if (isNaN(numeroCheque)) {
      throw new Error("Número de cheque inválido.");
    }

    const totalPedido = parseFloat(calculatedTotals.totalCalculado.toFixed(2));
    
    // Requerimiento: cuenta que cierra en $0.00 (después de descuentos,
    // cancelaciones de línea, etc.). Antes se enviaba con nombre_pago
    // "Cancelación", lo que en reportes/auditoría se confundía con un
    // cheque cancelado. Se mantiene el mismo id_pago sentinel (999, no
    // corresponde a ningún método real del catálogo de payments) para no
    // arriesgar nada del lado del backend — solo cambia la etiqueta que
    // identifica el concepto en pagos.php y en los reportes que agrupan
    // por nombre_pago (ej. RESUMEN DE COBROS del cierre de día).
    if (Math.abs(totalPedido) < 0.01) {
      var requestData = {
        numero_cheque: numeroCheque,
        total_pedido: 0,
        total_pagado: 0,
        propina_total: totalPropina,
        pagos: [{
          id_pago: 999,
          nombre_pago: "Pago en Ceros",
          monto_pagado: 0.00,
          propina: 0.00,
          cambio: 0.00,
          folio: "",
        }],
      };
    } else {
      const totalPagado = parseFloat(
        appliedPayments.reduce((sum, p) => sum + parseFloat(p.amount.toFixed(2)), 0).toFixed(2)
      );

      if (appliedPayments.length === 0) {
        throw new Error("No se han aplicado pagos.");
      }

      if (totalPagado < totalPedido) {
        throw new Error(`Pago incompleto. Faltan $${(totalPedido - totalPagado).toFixed(2)}`);
      }

      // Preparar pagos incluyendo datos de hotel Y PROPINA
      var requestData = {
        numero_cheque: numeroCheque,
        total_pedido: totalPedido,
        total_pagado: totalPagado,
        propina_total: totalPropina,  // ✅ PROPINA TOTAL
        pagos: appliedPayments.map((pago, index) => {
          // ✅ OBTENER PROPINA DEL PAGO (de diferentes fuentes posibles)
          const propinaPago = parseFloat(pago.tip || pago.propinaAplicada || 0) || 0;
          
          const basePago = {
            id_pago: pago.method.id || 0,
            nombre_pago: pago.method.name || "Método desconocido",
            monto_pagado: parseFloat(pago.amount.toFixed(2)),
            propina: propinaPago,  // ✅ PROPINA POR PAGO
            cambio: parseFloat((pago.change || 0).toFixed(2)),
            folio: pago.folio || "",
          };
          
          if (pago.isHotelPayment && pago.hotelData) {
            return {
              ...basePago,
              es_pago_hotel: true,
              numero_habitacion: pago.roomNumber,
              referencia_hotel: pago.hotelData.referencia || "",
              respuesta_hotel: JSON.stringify(pago.hotelData),
              cargo_exitoso: true,
              propina_hotel: propinaPago  // ✅ PROPINA EN PAGO HOTEL
            };
          }
          
          return basePago;
        }),
        contiene_pagos_hotel: hotelPayments.length > 0,
        total_cargos_hotel: hotelPayments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0),
        propina_hotel_total: hotelPayments.reduce((sum, p) => sum + (parseFloat(p.tip || p.propinaAplicada || 0) || 0), 0)
      };
    }

    console.log("📤 Enviando confirmación de pago CON PROPINA:", JSON.stringify(requestData, null, 2));

    // Enviar a pagos.php
    const urls = [
      API_PAGO,
      `${LOCAL_API_URL}/pagos.php?property=${property}`,
    ];

    let response;
    let responseText;
    let lastError;

    for (const url of urls) {
      try {
        response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-Property": property,
            "X-Vendedor-ID": currentUser?.id?.toString() || "",
            "X-Vendedor-Nombre": currentUser?.nombre || ""
          },
          body: JSON.stringify(requestData),
          credentials: "omit",
        });

        responseText = await response.text();

        if (response.ok) {
          break;
        } else {
          lastError = new Error(`Error ${response.status}: ${responseText}`);
        }
      } catch (fetchError) {
        lastError = new Error(`Error de red: ${fetchError.message}`);
      }
    }

    if (!response || !response.ok) {
      throw lastError || new Error("No se pudo conectar con ninguna URL.");
    }

    let data;
    try {
      data = JSON.parse(responseText);
    } catch (jsonError) {
      throw new Error("La respuesta del servidor no es un JSON válido.");
    }

    if (!data.success) {
      throw new Error(data.message || "Error en la respuesta del servidor");
    }

    console.log("✅ Pago confirmado con propina:", data);

    // ==============================================
    // 6. GENERAR ARCHIVO DE FACTURA (NUEVA FUNCIONALIDAD)
    // ==============================================
    // Generar número de ticket único
    const ticketNumber = `TKT-${chequeSeleccionado}-${Date.now()}`;
    
    // Preparar datos del pedido para factura
    const orderDataForInvoice = order.map(item => ({
      id: item.id,
      nombre: item.name,
      cantidad: item.quantity,
      precio_unitario: item.price,
      tax: item.tax || 16,
      categoria: item.category || 'Otros',
      modifiers: item.modifiers || []
    }));
    
    // Construir payload para facturación
    const facturaPayload = {
      chequeId: chequeSeleccionado,
      property: property,
      ticket: ticketNumber,
      roomNumber: hotelPayments.length > 0 ? hotelPayments[0].roomNumber : null,
      total: totalPedido + totalPropina,
      propina: totalPropina,
      order: orderDataForInvoice,
      pagos: requestData.pagos,
      vendedor: currentUser?.nombre || 'SISTEMA',
      revenue: selectedRevenue?.nombre || 'Restaurante'
    };
    
    // URL del endpoint de facturación
    const FACTURACION_URL = 'https://www.bts.ei-solutions.co/shopeis/app_menu/facturacion_tav.php';
    
    // Enviar a facturación (sin afectar el flujo principal si falla)
    try {
      console.log("📄 Generando factura para ticket:", ticketNumber);
      const facturaResponse = await fetch(FACTURACION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(facturaPayload)
      });
      
      const facturaResult = await facturaResponse.json();
      
      if (facturaResult.success) {
        console.log("✅ Factura generada:", facturaResult.file);
        mostrarMensaje(`📄 Factura generada: ${facturaResult.file}`, "info");
        
        // Agregar información de factura al resultado
        data.factura = facturaResult;
      } else {
        console.warn("⚠️ Error al generar factura:", facturaResult.message);
        // No mostramos mensaje de error al usuario para no interrumpir
      }
    } catch (facturaError) {
      console.error("❌ Error en generación de factura:", facturaError);
      // No interrumpimos el flujo principal si falla la factura
    }

    // Imprimir ticket (con propina)
    if (allowedActions.includes("Imprimir") && order.length > 0) {
      try {
        // Pasar la propina total al ticket
        const ticketOptions = {
          propinaTotal: totalPropina,
          ticketNumber: ticketNumber,
          pagosConPropina: appliedPayments.map(p => ({
            metodo: p.method.name,
            monto: p.amount,
            propina: p.tip || p.propinaAplicada || 0
          }))
        };
        await handlePrintTicket(ticketOptions);
      } catch (printError) {
        console.error("Error al imprimir:", printError);
      }
    }

    // Mensaje de éxito con propina
    let mensajeExito = `✅ Cheque #${numeroCheque} cerrado.`;
    if (totalPropina > 0) {
      mensajeExito += ` 💝 Propina total: $${totalPropina.toFixed(2)}`;
    }
    if (hotelPayments.length > 0) {
      const resumenHotel = hotelPayments.map(p => 
        `${p.method.name}: $${parseFloat(p.amount).toFixed(2)} (Hab. ${p.roomNumber})${(p.tip || p.propinaAplicada) > 0 ? ` + Propina $${(p.tip || p.propinaAplicada).toFixed(2)}` : ''}`
      ).join(', ');
      mensajeExito += ` Pagos: ${resumenHotel}`;
    }
    mensajeExito += ` 📄 Ticket: ${ticketNumber}`;
    mostrarMensaje(mensajeExito);

    // Limpiar estados
    setOrder([]);
    setAppliedPayments([]);
    setChequeSeleccionado(null);
    setTotalPedido(0);
    setCurrentGroup(null);
    setCurrentCategory(null);
    setSelectedOrderIndex(null);

    if (selectedRevenue.Tipo !== "Retail") {
      handleChangeRevenue();
    }

  } catch (error) {
    console.error("❌ Error en confirmOrder:", error);
    mostrarMensaje(`❌ ${error.message}`);
  } finally {
    setIsConfirmingPayment(false);
  }
};

  const handleSelectGroup = (group) => {
    setCurrentGroup(group);
    setCurrentCategory(null);
  };

  const handleSelectCategory = (category) => {
    setCurrentCategory(category);
  };

  const handleBackToGroups = () => {
    setCurrentGroup(null);
    setCurrentCategory(null);
    setSelectedOrderIndex(null);
    setResetGroupList(prev => prev + 1); // Forzar reset de GroupList
  };

  const handleBackToCategories = () => {
    setCurrentCategory(null);
  };

const handleSelectRevenue = (data) => {
  if (!allowedActions.includes(data.revenue.name)) {
    mostrarMensaje(`No tienes permiso para acceder al centro de consumo "${data.revenue.name}".`);
    return;
  }

  setShowChequeList(false);
  setChequeSeleccionado(null);

  if (!data.revenue || !data.revenue.id) {
    console.error("El revenue seleccionado no tiene un ID válido.");
    return;
  }

  // ===== GUARDAR DATOS DE HABITACIÓN SI VIENEN DE ROOM =====
  if (data.roomNumber) {
    setRoomNumber(data.roomNumber);
    setRoomInfo(data.roomInfo);
    console.log('🏨 Datos de habitación recibidos:', { roomNumber: data.roomNumber, roomInfo: data.roomInfo });
  } else {
    setRoomNumber(null);
    setRoomInfo(null);
  }

  const clientes = data.revenue.Tipo === "Eventos"
    ? parseInt(data.clientCount, 10) || 1
    : data.clientCount || 1;

  setSelectedRevenue(data.revenue);
  setRevenueInfo({
    id: data.revenue.id,
    name: data.revenue.name,
  });

  // ===== OBTENER EL TIPO EN MAYÚSCULAS PARA COMPARACIÓN INSENSIBLE =====
  const tipoRevenue = (data.revenue.Tipo || "").toUpperCase();
  
  // ===== MANEJO SEGÚN EL TIPO DE REVENUE =====
  if (tipoRevenue === "RETAIL") {
    // Retail: espacio fijo, 1 cliente
    setSelectedSpace({ name: "Retail 1" });
    setClientCount(1);
    
  } else if (tipoRevenue === "ROOM") {
    // ROOM: mostrar habitación en lugar de mesa
    const nombreEspacio = data.roomNumber ? `Hab. ${data.roomNumber}` : data.revenue.name;
    console.log('🏨 Configurando espacio ROOM:', { nombreEspacio, roomNumber: data.roomNumber });
    
    setSelectedSpace({ 
      name: nombreEspacio,
      isRoomService: true,
      roomNumber: data.roomNumber,
      roomInfo: data.roomInfo,
      tipo: "ROOM"
    });
    setClientCount(clientes);
    
    // Mostrar mensaje de confirmación con la habitación
    if (data.roomNumber) {
      mostrarMensaje(`✅ Habitación ${data.roomNumber} seleccionada - ${clientes} ${clientes === 1 ? 'cliente' : 'clientes'}`);
    } else {
      mostrarMensaje(`✅ ${data.revenue.name} - ${clientes} ${clientes === 1 ? 'cliente' : 'clientes'}`);
    }
    
  } else if (tipoRevenue === "TABLE") {
    // Table: usar mesa seleccionada
    setSelectedSpace(data.space);
    setClientCount(clientes);
    setCustomerNamePending(data.customerName || null);
    
  } else {
    // Otros tipos (Ecommerce, etc.): sin espacio específico
    setSelectedSpace(null);
    setClientCount(clientes);
  }

  setShowRevenueSelector(false);
  setShowPOS(true);
};

  const handleChangeRevenue = () => {
    setShowRevenueSelector(true);
    setSelectedRevenue(null);
    setSelectedSpace(null);
    setClientCount(null);
    setOrder([]);
    setCurrentGroup(null);
    setCurrentCategory(null);
    setFilteredProducts([]);
    setChequeSeleccionado(null);
    setSelectedOrderIndex(null);
    setAppliedPayments([]);
    setPaymentMethod(null);
    setSelectedPaymentIndex(null);
    setSelectedClient(0);
    setShowChequeList(false);
    setShowPOS(false);
  };

  const handleProductSelection = (product) => {
    if (!allowedActions.includes("Ordenar")) {
      mostrarMensaje("No tienes permiso para agregar productos.");
      return;
    }

    // Verificar si el producto es de nota abierta
    const isOpenNote = propertyConfig?.opennote?.some(item => item.Itemprecio === product.id);
    if (isOpenNote) {
      // Mismo punto de entrada validado que el botón "Nota" y el ícono por
      // producto: si el producto ya fue enviado, no abre el panel.
      openNoteFor(selectedOrderIndex !== null ? [selectedOrderIndex] : []);
      return;
    }

    // Verificar si el producto es de precio abierto
    const isOpenItem = propertyConfig?.openitem?.some(item => item.Itemprecio === product.id);
    if (isOpenItem && allowedActions.includes("Price Open Item")) {
      setSelectedProduct(product);
      setShowOpenPriceModal(true);
      return;
    }

    // Manejo de modificadores
    if (product.modifiers?.length > 0) {
      setSelectedProduct(product);
      setShowModifiersModal(true);
      return;
    }

    // Agregar producto directamente
    handleAddProductToClient(product);
  };

const handleAddProductToClient = (product, modifiers) => {
  // Requerimiento: si no hay cliente seleccionado (o el asiento seleccionado
  // ya no es válido, ej. quedó fuera de rango), el producto se agrega de
  // todas formas — siempre al Cliente 1 (asiento índice 0) — en vez de
  // bloquear el alta pidiendo seleccionar cliente primero.
  let client = selectedClient;
  if (selectedRevenue?.Tipo === "Table") {
    const clienteInvalido =
      client === null || client === undefined || isNaN(client) || client < 0 || client >= clientCount;
    if (clienteInvalido) {
      client = 0;
      if (selectedClient !== 0) {
        setSelectedClient(0);
      }
    }
  }

  // ✅ OBTENER CARGO DEL PRODUCTO
  let cargoValue = parseFloat(product.cargo) || 0;
  
  // Si el producto no tiene cargo, intentar obtener de propertyConfig
  if (cargoValue === 0 && propertyConfig && propertyConfig.cargo_servicio) {
    cargoValue = parseFloat(propertyConfig.cargo_servicio) || 0;
  }
  
  // Si aún es 0, intentar de la configuración del revenue
  if (cargoValue === 0 && selectedRevenue && selectedRevenue.cargo_servicio) {
    cargoValue = parseFloat(selectedRevenue.cargo_servicio) || 0;
  }
  
  // ✅ APLICAR CARGO A MODIFICADORES SI NO TIENEN
  const modifiersWithCargo = (modifiers || []).map(mod => ({
    ...mod,
    cargo: parseFloat(mod.cargo) || cargoValue,
  }));
  
  const newProduct = {
    ...product,
    modifiers: modifiersWithCargo,
    client,
    quantity: product.quantity || 1,
    // ✅ INCLUIR CARGO EN EL PRODUCTO
    cargo: cargoValue,
    total: calculateItemTotal({ 
      ...product, 
      modifiers: modifiersWithCargo, 
      quantity: product.quantity || 1,
      cargo: cargoValue
    }),
    isFromDB: false,
    id: product.id,
  };

  // Requerimiento "Venta de Otro Revenue": varios productos de origen
  // distintos (ej. Tacos de Arrachera, Pastel) pueden compartir el mismo
  // producto LOCAL genérico configurado en revenuePriceOtros (mismo id) —
  // ese id local no los distingue. Cuando el producto viene de "Otro
  // Revenue" (trae ventaOtroRevenueOrigen), se compara por el id REAL del
  // producto de origen en su propio catálogo (único de verdad, a diferencia
  // del nombre que en teoría podría repetirse). Para una venta normal (sin
  // ese metadato) el comportamiento no cambia: sigue comparando por el id
  // local, como siempre.
  const mismoOrigen = (item) => {
    const origenNuevo = product.ventaOtroRevenueOrigen?.productId;
    const origenExistente = item.ventaOtroRevenueOrigen?.productId;
    if (origenNuevo === undefined && origenExistente === undefined) return true;
    return origenNuevo === origenExistente;
  };

  const existingIndexNow = order.findIndex(
    (item) =>
      item.id === product.id &&
      mismoOrigen(item) &&
      JSON.stringify(item.modifiers) === JSON.stringify(modifiersWithCargo) &&
      item.client === client
  );
  const targetIndex = existingIndexNow !== -1 ? existingIndexNow : order.length;

  setOrder((prevOrder) => {
    const existingIndex = prevOrder.findIndex(
      (item) =>
        item.id === product.id &&
        mismoOrigen(item) &&
        JSON.stringify(item.modifiers) === JSON.stringify(modifiersWithCargo) &&
        item.client === client
    );

    if (existingIndex !== -1) {
      return prevOrder.map((item, index) =>
        index === existingIndex
          ? {
              ...item,
              quantity: item.quantity + (product.quantity || 1),
              total: calculateItemTotal({
                ...item,
                quantity: item.quantity + (product.quantity || 1),
              }),
            }
          : item
      );
    } else {
      return [...prevOrder, newProduct];
    }
  });

  // Requerimiento "Order Summary": el resumen usa esta señal para
  // desplazarse automáticamente hasta el producto recién agregado (o cuya
  // cantidad se acaba de incrementar) y resaltarlo brevemente.
  setLastAddedSignal({ index: targetIndex, name: product.name, ts: Date.now() });
};

// ── Punto 2+3 — Venta de Otro Revenue (flujo integrado) ────────────────
// Carga el catálogo del Revenue de ORIGEN elegido en VentaOtroRevenueSelector
// (solo para mostrar el menú y tomar el precio/tax/cargo base — sección 5.1).
const fetchOriginProducts = (revenueId) =>
  fetchData(
    BASE_API_JSON,
    property,
    `products_${revenueId}.json`,
    "Error cargando productos",
    revenueId
  );

// originProduct: producto elegido del catálogo del Revenue de origen.
// originRevenue: Revenue Center del que viene ese producto.
const handleAddProductFromOtherRevenue = (originProduct, originRevenue) => {
  if (!allowedActions.includes("Venta de Otro Revenue")) {
    mostrarMensaje("No tienes permiso para vender productos de otro Revenue.");
    return;
  }
  if (!selectedRevenue) return;

  // 5.1 — precio, tax y cargo se calculan a partir del producto de ORIGEN,
  // según la regla configurada para el Revenue que está VENDIENDO (destino):
  // propertyConfig.revenuePriceOtros -> depto_<selectedRevenue.id>.
  let precioFinal = parseFloat(originProduct.price) || 0;
  let taxFinal = parseFloat(originProduct.tax) || 0;
  let cargoFinal = parseFloat(originProduct.cargo) || 0;
  let porcentaje = 0;
  let productoLocal = null;

  const regla = propertyConfig?.revenuePriceOtros?.find((r) =>
    Object.keys(r).includes(`depto_${selectedRevenue.id}`)
  );

  if (regla) {
    porcentaje = parseFloat(regla[`depto_${selectedRevenue.id}`]) || 0;
    if (porcentaje > 0) {
      // Redondeo a 2 decimales (moneda), no a entero.
      precioFinal = Math.round(precioFinal * (1 + porcentaje / 100) * 100) / 100;
    }
    // SC/TAX: valores de reemplazo directo, opcionales. "" = no tocar ese campo.
    if (regla.SC !== undefined && regla.SC !== "") {
      cargoFinal = parseFloat(regla.SC);
    }
    if (regla.TAX !== undefined && regla.TAX !== "") {
      taxFinal = parseFloat(regla.TAX);
    }
    // 5.2 — KDS, inventario, clasificaciones y demás configuración vienen del
    // producto LOCAL (id) ya existente en el catálogo del Revenue que vende.
    // filteredProducts ya es el catálogo cargado para selectedRevenue — no
    // hace falta pedirlo de nuevo al backend.
    if (regla.id !== undefined && regla.id !== "") {
      productoLocal =
        filteredProducts.find((p) => String(p.id) === String(regla.id)) || null;
    }
  }

  if (!productoLocal) {
    // Sin regla o sin producto local configurado: no se permite la venta
    // cruzada, en vez de agregar el producto de origen "tal cual" de forma
    // silenciosa (comportamiento pendiente de confirmar, sección 5.3).
    mostrarMensaje(
      `No hay un producto local configurado en ${selectedRevenue.name} para vender productos de ${originRevenue?.name || "otro Revenue"}. Contacta al administrador (revenuePriceOtros en config.json).`,
      6000
    );
    return;
  }

  // Los modificadores con costo también llevan el mismo porcentaje de
  // revenuePriceOtros que el precio base, para que el pedido quede
  // consistente (ej. si el platillo sube 10%, un modificador con costo
  // también sube 10%; uno sin costo, o si no hay porcentaje, no cambia).
  // ⚠️ Los modificadores agrupados (con "items", ej. Tamaño/Extras) llevan
  // el precio DENTRO de cada item.price, no en el objeto del grupo — hay
  // que ajustar ahí, o el aumento nunca se aplica (ModifiersModal.js filtra
  // por Array.isArray(mod.items) para saber si es un grupo).
  const ajustarPrecioModificador = (price) => {
    const p = parseFloat(price) || 0;
    if (porcentaje > 0 && p !== 0) {
      return Math.round(p * (1 + porcentaje / 100) * 100) / 100;
    }
    return p;
  };

  const modifiersOrigen =
    originProduct.modifiers?.length > 0 ? originProduct.modifiers : (productoLocal.modifiers || []);
  const modifiersAjustados = modifiersOrigen.map((mod) => {
    if (Array.isArray(mod.items) && mod.items.length > 0) {
      // Grupo de modificadores (ej. Tamaño, Extras): cada opción trae su
      // propio price dentro de items.
      return {
        ...mod,
        items: mod.items.map((item) => ({
          ...item,
          price: ajustarPrecioModificador(item.price),
        })),
      };
    }
    // Modificador plano (opcional, sin grupo).
    return { ...mod, price: ajustarPrecioModificador(mod.price) };
  });

  // El producto que se agrega al pedido es el producto LOCAL (KDS, inventario,
  // clasificaciones, resto de configuración) — salvo el nombre, que se
  // conserva del producto de ORIGEN para que la cocina sepa qué preparar
  // realmente (confirmado: el nombre es fundamental, no debe ser genérico).
  // Los modificadores también se respetan del producto de ORIGEN cuando los
  // tenga (ej. término de cocción, guarnición) — son propios del platillo
  // real que se está vendiendo, no del producto local genérico que lo recibe.
  const productoParaPedido = {
    ...productoLocal,
    price: precioFinal,
    tax: taxFinal,
    cargo: cargoFinal,
    name: originProduct.name,
    modifiers: modifiersAjustados,
    // Metadato informativo para trazabilidad (no interviene en el guardado
    // del pedido, que ya usa el flujo normal con el id del producto local).
    ventaOtroRevenueOrigen: {
      revenueId: originRevenue?.id,
      revenueName: originRevenue?.name,
      productId: originProduct.id,
      productName: originProduct.name,
    },
  };

  if (productoParaPedido.modifiers?.length > 0) {
    setSelectedProduct(productoParaPedido);
    setShowModifiersModal(true);
    return;
  }

  handleAddProductToClient(productoParaPedido);
};

  const isDiscountApplicable = (discount) => {
    if (selectedOrderIndex === null) return false;
    const selectedItem = order[selectedOrderIndex];
    return selectedItem.isFromDB && discount.products.some((product) => product.id === selectedItem.id);
  };

  const applicableDiscounts = promos.filter((x) =>
    x.products.some((product) => Number(product.id) === Number(order[selectedOrderIndex]?.item))
  );

  const handleApplyDiscount = (promo) => {
    if (!allowedActions.includes(promo.name)) {
      mostrarMensaje(`No tienes permiso para aplicar el descuento "${promo.name}".`);
      return;
    }

    if (selectedOrderIndex === null) return;

    const selectedItem = order[selectedOrderIndex];
    if (!selectedItem) return;

    if (!selectedItem.id && !selectedItem.id_producto) {
      console.error("El producto no tiene identificador válido", selectedItem);
      mostrarMensaje("Error: Producto sin ID válido");
      return;
    }

    const mainProductId = selectedItem.id_producto || selectedItem.id;
    const mainItemId = selectedItem.item;

    const updatedItem = {
      ...selectedItem,
      discount: promo,
      total: calculateItemTotal({ ...selectedItem, discount: promo }),
    };

    setOrder((prev) =>
      prev.map((item, index) =>
        index === selectedOrderIndex ? updatedItem : item
      )
    );

    setPendingDiscounts((prev) => {
      const discountsToApply = [];

      discountsToApply.push({
        id: mainProductId,
        itemId: mainItemId,
        discount: promo,
        originalPrice: selectedItem.price,
        finalPrice: updatedItem.total / updatedItem.quantity,
        modifiers: [],
      });

      if (selectedItem.modifiers && selectedItem.modifiers.length > 0) {
        selectedItem.modifiers.forEach((modifier) => {
          const modifierId = modifier.id_producto || modifier.id;
          if (!modifierId) {
            console.error("Modificador sin ID válido", modifier);
            return;
          }

          discountsToApply.push({
            id: modifierId,
            itemId: modifier.item || modifierId,
            discount: promo,
            originalPrice: modifier.price,
            finalPrice: modifier.price * (1 - promo.price / 100),
            parentItem: mainProductId,
            isModifier: true,
          });
        });
      }

      return [...prev, ...discountsToApply];
    });

    setShowDiscountModal(false);
    mostrarMensaje(
      `Descuento "${promo.name}" aplicado a: ${selectedItem.name}${selectedItem.modifiers?.length > 0 ? ` y ${selectedItem.modifiers.length} modificadores` : ''}`
    );
  };

  // Flujo rápido: seleccionar descuento → aplicar + procesar en un solo paso (sin pasos adicionales)
  const handleSelectAndProcessDiscount = async (promo) => {
    if (!allowedActions.includes(promo.name)) {
      mostrarMensaje(`No tienes permiso para aplicar el descuento "${promo.name}".`);
      return;
    }
    if (!allowedActions.includes("Descuentos")) {
      mostrarMensaje("No tienes permiso para procesar descuentos.");
      return;
    }
    if (!currentUser?.nombre) {
      mostrarMensaje("No se ha identificado al usuario.");
      return;
    }
    if (!chequeSeleccionado) {
      mostrarMensaje("No hay un cheque seleccionado.");
      return;
    }
    if (selectedOrderIndex === null) return;

    const selectedItem = order[selectedOrderIndex];
    if (!selectedItem) return;
    if (!selectedItem.id && !selectedItem.id_producto) {
      mostrarMensaje("Error: Producto sin ID válido");
      return;
    }

    const mainProductId = selectedItem.id_producto || selectedItem.id;
    const mainItemId = selectedItem.item;

    const updatedItem = {
      ...selectedItem,
      discount: promo,
      total: calculateItemTotal({ ...selectedItem, discount: promo }),
    };

    // Construir lista de descuentos a procesar localmente (sin depender del estado)
    const discountsToApply = [];
    discountsToApply.push({
      id: mainProductId,
      itemId: mainItemId,
      discount: promo,
      originalPrice: selectedItem.price,
      finalPrice: updatedItem.total / updatedItem.quantity,
      modifiers: [],
    });
    if (selectedItem.modifiers && selectedItem.modifiers.length > 0) {
      selectedItem.modifiers.forEach((modifier) => {
        const modifierId = modifier.id_producto || modifier.id;
        if (!modifierId) return;
        discountsToApply.push({
          id: modifierId,
          itemId: modifier.item || modifierId,
          discount: promo,
          originalPrice: modifier.price,
          finalPrice: modifier.price * (1 - promo.price / 100),
          parentItem: mainProductId,
          isModifier: true,
        });
      });
    }

    // Actualizar orden y cerrar modal de inmediato
    setOrder((prev) =>
      prev.map((item, index) =>
        index === selectedOrderIndex ? updatedItem : item
      )
    );
    setPendingDiscounts((prev) => [...prev, ...discountsToApply]);
    setShowDiscountModal(false);

    // Procesar contra la API usando los datos locales (sin esperar actualización de estado)
    try {
      const uniqueDiscounts = [...new Map(
        discountsToApply.map((item) => [`${item.id}-${item.discount.id}`, item])
      ).values()];

      const results = await Promise.allSettled(
        uniqueDiscounts.map(async (pending) => {
          try {
            if (!pending.discount || !pending.id) {
              throw new Error("Datos de descuento incompletos");
            }

            const body = {
              mesa: selectedRevenue.Tipo === "Retail" ? "Retail 1" : selectedSpace?.name || "Sin especificar",
              articuloId: pending.id,
              numeroCheque: chequeSeleccionado,
              descuentoId: pending.discount.id,
              porcentaje: pending.discount.price,
              nombreDescuento: pending.discount.name,
              empleado: currentUser.nombre,
            };

            const urls = [
              API_DESCUENTO,
              `${LOCAL_API_URL}/aplicar_descuento.php?property=${property}`,
            ];

            let response;
            let responseText;
            let lastError;

            for (const url of urls) {
              try {
                response = await fetch(url, {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                    "X-Property": property,
                  },
                  body: JSON.stringify(body),
                  credentials: 'omit',
                });

                responseText = await response.text();

                if (response.ok) {
                  break;
                } else {
                  lastError = new Error(`Error ${response.status}: ${responseText}`);
                }
              } catch (fetchError) {
                lastError = new Error(`Error de red: ${fetchError.message}`);
              }
            }

            if (!response || !response.ok) {
              throw lastError || new Error("No se pudo conectar con ninguna URL.");
            }

            const responseData = JSON.parse(responseText);
            if (!responseData.success) {
              throw new Error(responseData.message || "Error en la respuesta del servidor");
            }

            return responseData;
          } catch (error) {
            console.error("Error en descuento:", pending.id, error);
            throw error;
          }
        })
      );

      const errores = results.filter((r) => r.status === "rejected");
      if (errores.length > 0) {
        throw new Error(`${errores.length} descuentos fallaron`);
      }

      setPendingDiscounts([]);
      mostrarMensaje(`✓ Descuento "${promo.name}" aplicado exitosamente`);
    } catch (error) {
      console.error("Error al procesar descuento:", error);
      mostrarMensaje(`Error: ${error.message}`);
    }
  };

  const handleProcessDiscounts = async () => {
    if (!allowedActions.includes("Descuentos")) {
      mostrarMensaje("No tienes permiso para procesar descuentos.");
      return;
    }
    try {
      if (!currentUser?.nombre) {
        throw new Error("No se ha identificado al usuario");
      }

      if (!chequeSeleccionado) {
        throw new Error("No hay un cheque seleccionado");
      }

      const uniqueDiscounts = [...new Map(
        pendingDiscounts.map((item) => [`${item.id}-${item.discount.id}`, item])
      ).values()];

      const results = await Promise.allSettled(
        uniqueDiscounts.map(async (pending) => {
          try {
            if (!pending.discount || !pending.id) {
              throw new Error("Datos de descuento incompletos");
            }

            const body = {
              mesa: selectedRevenue.Tipo === "Retail" ? "Retail 1" : selectedSpace?.name || "Sin especificar",
              articuloId: pending.id,
              numeroCheque: chequeSeleccionado,
              descuentoId: pending.discount.id,
              porcentaje: pending.discount.price,
              nombreDescuento: pending.discount.name,
              empleado: currentUser.nombre,
            };

            const urls = [
              API_DESCUENTO,
              `${LOCAL_API_URL}/aplicar_descuento.php?property=${property}`,
            ];

            let response;
            let responseText;
            let lastError;

            for (const url of urls) {
              try {
                response = await fetch(url, {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                    "X-Property": property,
                  },
                  body: JSON.stringify(body),
                  credentials: 'omit',
                });

                responseText = await response.text();

                if (response.ok) {
                  break;
                } else {
                  lastError = new Error(`Error ${response.status}: ${responseText}`);
                }
              } catch (fetchError) {
                lastError = new Error(`Error de red: ${fetchError.message}`);
              }
            }

            if (!response || !response.ok) {
              throw lastError || new Error("No se pudo conectar con ninguna URL.");
            }

            const responseData = JSON.parse(responseText);
            if (!responseData.success) {
              throw new Error(responseData.message || "Error en la respuesta del servidor");
            }

            return responseData;
          } catch (error) {
            console.error("Error en descuento:", pending.id, error);
            throw error;
          }
        })
      );

      const errores = results.filter((r) => r.status === "rejected");
      if (errores.length > 0) {
        throw new Error(`${errores.length} descuentos fallaron`);
      }

      setPendingDiscounts([]);
      setShowDiscountModal(false);
      mostrarMensaje("Descuentos aplicados exitosamente");
    } catch (error) {
      mostrarMensaje(`Error: ${error.message}`);
      setPendingDiscounts((prev) => [...prev]);
    }
  };

  const handleProductScanned = (scannedProduct) => {
    setOrder((prevOrder) => {
      const existingIndex = prevOrder.findIndex(
        (item) => item.id === scannedProduct.id && item.client === selectedClient
      );

      if (existingIndex !== -1) {
        return prevOrder.map((item, index) =>
          index === existingIndex
            ? { 
                ...item, 
                quantity: item.quantity + 1,
                total: calculateItemTotal({ ...item, quantity: item.quantity + 1 })
              }
            : item
        );
      } else {
        const newProduct = {
          ...scannedProduct,
          client: selectedClient,
          quantity: 1,
          modifiers: [],
          total: calculateItemTotal({ ...scannedProduct, quantity: 1, modifiers: [] }),
          selectedModifiers: []
        };
        return [...prevOrder, newProduct];
      }
    });
    
    mostrarMensaje(`✓ ${scannedProduct.name} añadido al pedido`);
  };

  const handleRemoveDiscount = () => {
    if (!allowedActions.includes("Quitar descuento")) {
      mostrarMensaje("No tienes permiso para quitar descuentos.");
      return;
    }
    if (selectedOrderIndex === null) return;

    const selectedItem = order[selectedOrderIndex];
    if (!selectedItem) return;

    // Requerimiento: este botón debe quitar tanto un descuento manual
    // (Descuentos / Descuento Abierto) como una promoción aplicada al
    // producto seleccionado. Ambos casos terminan guardados en los mismos
    // campos del ítem, así que limpiar "discount" (anidado) junto con los
    // campos planos monto_descuento/nombre_descuento/porcentaje_descuento
    // cubre los dos orígenes con el mismo código.
    // Antes solo se limpiaba "discount"; si el descuento venía de
    // "Descuento Abierto" (que también escribe monto_descuento /
    // nombre_descuento a nivel raíz del ítem), esos campos quedaban
    // intactos y el ticket seguía mostrando el descuento — este era el
    // bug reportado ("no fue posible eliminar el descuento aplicado").
    const updatedOrder = order.map((item, index) => {
      if (index !== selectedOrderIndex) return item;
      return {
        ...item,
        discount: null,
        monto_descuento: 0,
        nombre_descuento: "",
        porcentaje_descuento: 0,
        total: calculateItemTotal({ ...item, discount: null }),
      };
    });

    setOrder(updatedOrder);

    // Si había un descuento de catálogo (Descuentos) en cola sin procesar
    // para este mismo producto, se descarta también para que no se vuelva
    // a aplicar al pulsar "Aplicar" en el panel express.
    const productoId = selectedItem.id_producto || selectedItem.id;
    if (productoId != null) {
      setPendingDiscounts((prev) => prev.filter((p) => p.id !== productoId));
    }

    mostrarMensaje("Descuento/Promoción eliminado del producto seleccionado.");

    // Intento best-effort de confirmar la eliminación en el backend.
    //
    // Antes esto llamaba a actualizar-descuento.php (el endpoint de
    // "Descuento Abierto"), pero se confirmó en el sistema real que ese
    // endpoint no acota nada por producto — aplica a nivel de cuenta sin
    // importar qué se le mande. En cambio, se comprobó contra datos
    // reales (lectura_cheques.php) que aplicar_descuento.php (el de
    // "Descuentos" / catálogo) sí escribe y por lo tanto probablemente
    // también sepa limpiar el descuento de UN producto específico sin
    // afectar a los demás: se manda porcentaje 0 para ese articuloId, el
    // mismo endpoint/forma que ya usa processExpressDiscounts para
    // aplicar. Sigue sin ser bloqueante: si el backend no limpia el
    // valor igual, el ticket ya se ve sin descuento en ese producto de
    // todas formas (la parte de arriba, 100% local, no depende de esto).
    if (chequeSeleccionado && productoId != null) {
      fetch(`${LOCAL_API_URL}/aplicar_descuento.php?property=${encodeURIComponent(property)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({
          mesa: selectedRevenue?.Tipo === "Retail" ? "Retail 1" : selectedSpace?.name,
          articuloId: productoId,
          numeroCheque: chequeSeleccionado,
          descuentoId: 0,
          porcentaje: 0,
          nombreDescuento: "Sin descuento",
          empleado: currentUser?.nombre || "Sistema",
        }),
      }).catch((err) => {
        console.warn("[handleRemoveDiscount] No se pudo confirmar la eliminación en el servidor:", err);
      });
    }
  };

  const handlePrintTicket = async () => {
    if (order.length === 0) {
      mostrarMensaje("No hay productos en el pedido para imprimir.");
      return;
    }

    console.log(`Imprimiendo desde ${deviceConfig.name} (PRNTT: ${deviceConfig.PRNTT || "No definido"})`);
    console.log("Impresoras disponibles:", devices
      .filter(d => d.Rol === "PRNT")
      .map(d => ({ name: d.name, tipo: d.Config?.[0]?.Tipo || "No definido" }))
    );

    try {
      const result = await printTicket(
        order,
        total,
        appliedPayments,
        property,
        deviceConfig,
        selectedRevenue,
        selectedSpace,
        chequeSeleccionado,
        currentUser,
        devices
      );
      mostrarMensaje(result.message, 5000);
    } catch (error) {
      console.error("Error al imprimir el ticket:", error);
      mostrarMensaje(`Error al imprimir: ${error.message}`, 5000);
    }
  };

  // ── handlePrintTicketWithSegmento ─────────────────────────────────────────
  // Wrapper del botón "Imprimir Ticket". Si config.json tiene "segmento":"Y",
  // muestra el selector de segmento antes de imprimir.
  // Si "segmento":"N" (o no definido), llama handlePrintTicket directamente.
  // handlePrintTicket NO se modifica.
  const handlePrintTicketWithSegmento = async () => {
    if (order.length === 0) {
      mostrarMensaje("No hay productos en el pedido para imprimir.");
      return;
    }
    if (propertyConfig?.segmento === 'Y') {
      // Cargar segmentos desde segmento.json si aún no están en memoria
      if (segmentos.length === 0) {
        try {
          const data = await fetchData(BASE_API_JSON, property, 'segmento.json', 'Error cargando segmentos');
          const lista = Array.isArray(data)
            ? data
            : (data?.segmentacion_mercado || []);
          setSegmentos(lista);
        } catch {
          mostrarMensaje('Error al cargar el listado de segmentos. Intente nuevamente.');
          return;
        }
      }
      setSegmentoError(null);
      setShowSegmentoModal(true);
      return; // el modal toma el control a partir de aquí
    }
    // segmento = "N" o no definido → flujo normal sin ningún cambio
    handlePrintTicket();
  };

  // ── handleSegmentoConfirm ─────────────────────────────────────────────────
  // Llamado por SegmentoModal al presionar Continuar.
  // 1. Guarda el segmento en t_cheques.Referencia_Hotel vía nuevo endpoint.
  // 2. Si el guardado es exitoso, cierra el modal y llama handlePrintTicket.
  // 3. Si falla, muestra error en el modal sin imprimir.
  const handleSegmentoConfirm = async (segmentoNombre) => {
    setSegmentoLoading(true);
    setSegmentoError(null);
    try {
      const res = await fetch(
        `${LOCAL_API_URL}/actualizar_segmento.php?property=${encodeURIComponent(property)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ numero_cheque: chequeSeleccionado, segmento: segmentoNombre }),
        }
      );
      if (!res.ok) throw new Error(`Error HTTP ${res.status}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'No se pudo guardar el segmento');
      // Guardado correcto: cerrar modal y continuar con el flujo de impresión existente
      setShowSegmentoModal(false);
      setSegmentoLoading(false);
      handlePrintTicket(); // flujo de impresión sin modificar
    } catch (err) {
      setSegmentoError(err.message || 'Error al guardar el segmento. Intente nuevamente.');
      setSegmentoLoading(false);
      // No imprime, no cierra el modal
    }
  };

  if (!isConfigLoaded) {
    return (
      <div className="property-selector">
        <h2>Registrar Dispositivo</h2>

        {/* Toggle Manual / Automático */}
        <div className="registro-mode-toggle">
          <button
            className={`registro-toggle-btn${registroMode === 'manual' ? ' active' : ''}`}
            onClick={() => { setRegistroMode('manual'); setPropertySelectorError(null); setAutoConfigFound(null); }}
          >
            <i className="bi bi-keyboard" /> Manual
          </button>
          <button
            className={`registro-toggle-btn${registroMode === 'auto' ? ' active' : ''}`}
            onClick={() => { setRegistroMode('auto'); setPropertySelectorError(null); setAutoConfigFound(null); }}
          >
            <i className="bi bi-magic" /> Automático
          </button>
        </div>

        {propertySelectorError && (
          <p className="registro-error">{propertySelectorError}</p>
        )}

        {/* Input file siempre en el DOM para que el ref esté disponible */}
        <input
          ref={autoFileInputRef}
          type="file"
          accept=".json"
          style={{ display: 'none' }}
          onChange={handleAutoFileSelect}
        />

        {registroMode === 'manual' ? (
          <>
            <div className="registro-field">
              <label>Propiedad</label>
              <input
                type="text"
                value={selectedProperty}
                onChange={(e) => {
                  setSelectedProperty(e.target.value);
                  setSelectedDevice("");
                  setPropertySelectorError(null);
                }}
                placeholder="NOMBRE DE TU PROPIEDAD"
                autoCapitalize="none"
                spellCheck={false}
              />
            </div>
            <div className="registro-field">
              <label>Dispositivo</label>
              {isLoadingDevices ? (
                <p className="registro-loading"><i className="bi bi-arrow-repeat spin-icon" /> Buscando dispositivos...</p>
              ) : (
                <select
                  value={selectedDevice}
                  onChange={(e) => setSelectedDevice(e.target.value)}
                  disabled={!selectedProperty || devices.length === 0}
                >
                  <option value="">-- Seleccionar --</option>
                  {devices
                    .filter(d => d.Rol !== "PRNT")
                    .map(d => (
                      <option key={d.name} value={d.name}>{d.name} ({d.Rol})</option>
                    ))}
                </select>
              )}
            </div>
            <button
              className="registro-confirm-btn"
              onClick={handleSelectPropertyAndDevice}
              disabled={!selectedProperty || !selectedDevice || isLoadingDevices}
            >
              <i className="bi bi-check-circle" /> Confirmar
            </button>
          </>
        ) : (
          /* Modo Automático */
          <div className="registro-auto">
            {autoConfigLoading ? (
              <p className="registro-loading">
                <i className="bi bi-arrow-repeat spin-icon" /> Leyendo archivo...
              </p>
            ) : autoConfigFound ? (
              <>
                <div className="registro-auto-preview">
                  <div className="registro-auto-row">
                    <i className="bi bi-building" />
                    <span>Propiedad</span>
                    <strong>{autoConfigFound.Propiedad}</strong>
                  </div>
                  <div className="registro-auto-row">
                    <i className="bi bi-display" />
                    <span>Dispositivo</span>
                    <strong>{autoConfigFound.name}</strong>
                  </div>
                </div>
                <button
                  className="registro-confirm-btn"
                  onClick={() => applyAutoConfig(autoConfigFound)}
                  disabled={autoConfigLoading}
                >
                  <i className="bi bi-check-circle" /> Confirmar
                </button>
                <button
                  className="registro-retry-btn"
                  onClick={loadAutoConfig}
                >
                  <i className="bi bi-folder2-open" /> Seleccionar otro archivo
                </button>
              </>
            ) : (
              <button className="registro-confirm-btn" onClick={loadAutoConfig}>
                <i className="bi bi-folder2-open" /> Seleccionar archivo de configuración
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  if (!deviceConfig) {
    return (
      <Registro
        onConfigSave={(config) => {
          setDeviceConfig(config);
          setProperty(config.Propiedad);
          setCurrentProperty(config.Propiedad);
          setIsConfigLoaded(true);
        }}
        property={currentProperty}
        setIsConfigLoaded={setIsConfigLoaded}
      />
    );
  }

  if (!isLoggedIn) {
    return (
      <Login
        onLoginSuccess={handleLoginSuccess}
        property={currentProperty}
      />
    );
  }

  const generarQR = async () => {
    console.log("[generarQR] Generando QR para cheque:", chequeSeleccionado);

    if (!chequeSeleccionado) {
      mostrarMensaje("No hay cheque seleccionado para generar QR.", 5000);
      return;
    }

    if (order.length === 0) {
      mostrarMensaje("No hay productos en el pedido para generar QR.", 5000);
      return;
    }

    console.log(`Imprimiendo desde ${deviceConfig.name} (PRNTT: ${deviceConfig.PRNTT || "No definido"})`);
    console.log("Impresoras disponibles:", devices
      .filter(d => d.Rol === "PRNT")
      .map(d => ({ name: d.name, tipo: d.Config?.[0]?.Tipo || "No definido" }))
    );

    try {
      const ticketData = {
        productos: order,
        total: calculateTotal(order),
        mesa: selectedRevenue?.Tipo === "Retail" ? "Retail" : selectedSpace?.name || "Mesa",
        revenue: selectedRevenue?.name || "Revenue",
        usuario: currentUser.nombre,
        chequeId: chequeSeleccionado,
        esQR: true
      };

      const result = await printTicket(
        ticketData.productos,
        ticketData.total,
        [],
        property,
        deviceConfig,
        selectedRevenue,
        selectedSpace,
        chequeSeleccionado,
        currentUser,
        devices,
        {
          esQR: true,
          DeviceID: deviceConfig?.PRNTT || 'tavpos',
          USB: { Tipo: 'USB' },
          cliente: currentUser?.id || '1' // Replace '1' with actual client ID source if available
        }
      );

      if (result.success) {
        mostrarMensaje("✅ QR generado e impreso correctamente", 5000);
      } else {
        throw new Error(result.message || "Error al imprimir el QR");
      }
    } catch (error) {
      console.error("[generarQR] Error al imprimir el QR:", error);
      mostrarMensaje(`⚠️ Error al generar QR: ${error.message}`, 5000);
    }
  };

  // En App.js, simplificar el handler de transferencia completa
  const handleTransferPedidosComplete = async (result) => {
    if (result.success) {
      // 1. Cerrar modal inmediatamente
      setShowTransferPedidoSelector(false);
      
      // 2. Mostrar mensaje de confirmación
      mostrarMensaje(result.message);
      
      // 3. Recargar usando función existente
      const chequesActualizados = await fetchCheques();
      await cargarChequeActualizado(chequeSeleccionado, chequesActualizados);
      
      // 4. Forzar update del OrderSummary
      setOrderSummaryKey(prev => prev + 1);
    }
  };


  return (
    <div className="app">
      {deviceConfig.Rol === "POS" ? (
        <Routes>
          <Route
            path="/"
            element={
<div className="pos-layout">
  <div className="pos-layout-tav">
    {/* ================= HEADER ================= */}
    <header className="fixed-header">
      <div className="header-container">
        <div className="user-info">
          <p>Bienvenido, {currentUser.nombre}</p>
          <h1>
            {deviceConfig.Propiedad} - {deviceConfig.name}
          </h1>
          <button onClick={handleLogout} className="logout-button">
            <FaSignOutAlt /> {t('logout')}
          </button>
        </div>
        
        <div className="header-buttons">
          {allowedActions.includes("Cambiar Centro de Consumo") && (
            <button onClick={handleChangeRevenue} className="header-button">
              {t('change_revenue')}
            </button>
          )}
          
          {allowedActions.includes("Mostrar Cheques") && (
            <button
              onClick={() => setShowChequeList(!showChequeList)}
              className="header-button"
            >
              {showChequeList ? t('hide_checks') : t('show_checks')}
            </button>
          )}
          
          {allowedActions.includes("Pedidos") && (
            <HeaderButton
              action="pedidos"
              label={t('orders')}
              currentActive={activeButton}
              onClick={handleHeaderButtonClick}
              isSectionVisible={showOrderSection}
            />
          )}
          
          {allowedActions.includes("Pagos") && (
            <HeaderButton
              action="pagos"
              label={t('payments')}
              currentActive={activeButton}
              onClick={handleHeaderButtonClick}
              isSectionVisible={showPaymentsSection}
            />
          )}
          
          {allowedActions.includes("Express") && (
            <HeaderButton
              action="express"
              label={t('express')}
              currentActive={activeButton}
              onClick={handleHeaderButtonClick}
              isSectionVisible={showExpressSection}
            />
          )}
          
          {allowedActions.includes("Admin") && (
            <HeaderButton
              action="admin"
              label={t('admin')}
              currentActive={activeButton}
              onClick={handleHeaderButtonClick}
              isSectionVisible={showAdminSection}
            />
          )}
          
          {/* Reportes movido a sección Admin */}
        </div>
        
        {selectedRevenue && (
          <RevenueInfo
            selectedRevenue={selectedRevenue}
            selectedSpace={selectedSpace}
            clientCount={clientCount}
            selectedClient={selectedClient}
            setSelectedClient={setSelectedClient}
            chequeSeleccionado={chequeSeleccionado}
            property={property}
            currentUser={currentUser}
            setOrder={setOrder}
            mostrarMensaje={mostrarMensaje}
            setPaymentMethod={setPaymentMethod}
            order={order}
            deviceConfig={deviceConfig}
            devices={devices}
            printTicket={printTicket}
            generarQR={generarQR}
            customerNamePending={customerNamePending}
            nombreVendedor={nombreVendedor}
          />
        )}
      </div>
    </header>

    {/* ================= MAIN ================= */}
    <main className="main-content">
      <Mensaje mensaje={mensaje} />

      {/* TODOS LOS MODALES ORIGINALES */}
      {/* ── Modal de Segmento (solo cuando config.json "segmento":"Y") ── */}
      {showSegmentoModal && (
        <SegmentoModal
          segmentos={segmentos}
          loading={segmentoLoading}
          error={segmentoError}
          onCancel={() => {
            setShowSegmentoModal(false);
            setSegmentoError(null);
          }}
          onConfirm={handleSegmentoConfirm}
        />
      )}

      {showVoidReasonsModal && (
        <VoidReasonsModal
          reasons={voidsReasons}
          onClose={() => {
            setShowVoidReasonsModal(false);
            setSelectedVoidReason(null);
          }}
          onConfirm={(reason) => confirmVoid(reason)}
          numeroCheque={chequeSeleccionado || null}
          mesa={selectedSpace?.name || "No especificado"}
          articuloId={order[selectedOrderIndex]?.id || null}
        />
      )}

      {showDiscountModal && (
        <DiscountModal
          show={showDiscountModal}
          onClose={() => {
            console.log("Cerrando DiscountModal");
            setShowDiscountModal(false);
          }}
          onApplyDiscount={handleApplyDiscount}
          onProcessDiscounts={handleProcessDiscounts}
          onSelectAndProcess={handleSelectAndProcessDiscount}
          applicableDiscounts={applicableDiscounts}
        />
      )}

      {showClientCountModal && (
        <ClientCountModal
          show={showClientCountModal}
          onClose={() => {
            setShowClientCountModal(false);
            setTempClientCount('');
          }}
          onConfirm={confirmClientCount}
          currentClientCount={clientCount}
        />
      )}

      {showSepararCuentasModal && (
        <SepararCuentasModal
          clientCount={clientCount}
          order={order}
          chequeSeleccionado={chequeSeleccionado}
          revenueId={selectedRevenue?.id}
          property={property}
          currentUser={currentUser}
          setOrder={setOrder}
          mostrarMensaje={mostrarMensaje}
          onClose={() => setShowSepararCuentasModal(false)}
          onSuccess={async () => {
            try {
              const updatedCheques = await fetchCheques();
              if (updatedCheques && chequeSeleccionado) {
                const chequeActual = updatedCheques.find(c => c.numero_cheque == chequeSeleccionado);
                if (chequeActual) {
                  await cargarChequeActualizado(chequeSeleccionado, updatedCheques);
                }
              }
              // Igual que en el resto de flujos que modifican el cheque
              // (promo, transferencias): forzar el re-render del
              // OrderSummary para que el cambio se vea de inmediato.
              setOrderSummaryKey((prev) => prev + 1);
            } catch (e) {
              console.error('[SepararCuentas onSuccess]', e);
            }
          }}
        />
      )}

      {showTransferClientModal && (
        <TransferClientNumericModal
          show={showTransferClientModal}
          onClose={() => {
            console.log("Closing TransferClientNumericModal");
            setShowTransferClientModal(false);
          }}
          transferClientData={transferClientData}
          validClients={transferClientData?.validClients || []}
          onConfirm={confirmTransferClient}
        />
      )}

      {showTransferTableSelector && (
        <>
          <div className="transfer-overlay-backdrop" onClick={() => setShowTransferTableSelector(false)} />
          <TransferTableSelector
            revenues={filteredRevenues}
            currentUser={currentUser}
            onTransferComplete={(result) => {
              setShowTransferTableSelector(false);
              if (result.success) {
                mostrarMensaje(result.message);
                fetchCheques();
                if (result.data && result.data.mesa_nueva) {
                  setSelectedSpace({ name: result.data.mesa_nueva });
                }
              }
            }}
            onClose={() => setShowTransferTableSelector(false)}
            currentCheque={{
              numero_cheque: chequeSeleccionado,
              mesa: selectedSpace?.name,
              revenue: selectedRevenue?.name,
              revenue_id: selectedRevenue?.id
            }}
          />
        </>
      )}

      {showTransferMeseroSelector && (
        <>
          <div className="transfer-overlay-backdrop" onClick={() => setShowTransferMeseroSelector(false)} />
          <TransferMeseroSelector
            currentUser={currentUser}
            onTransferComplete={(result) => {
              setShowTransferMeseroSelector(false);
              if (result.success) {
                mostrarMensaje(result.message);
                if (result.data?.nombre_vendedor_nuevo) {
                  setNombreVendedor(result.data.nombre_vendedor_nuevo);
                }
                fetchCheques();
              }
            }}
            onClose={() => setShowTransferMeseroSelector(false)}
            currentCheque={{
              numero_cheque: chequeSeleccionado,
              mesa: selectedSpace?.name,
              revenue: selectedRevenue?.name,
              nombre_vendedor: nombreVendedor,
            }}
          />
        </>
      )}

      {showPromoSelector && (
        <>
          <div className="promo-overlay-backdrop" onClick={() => setShowPromoSelector(false)} />
          <PromoSelector
            property={property}
            promos={listaPromos}
            order={order}
            selectedRevenue={selectedRevenue}
            selectedSpace={selectedSpace}
            chequeSeleccionado={chequeSeleccionado}
            currentUser={currentUser}
            // Requerimiento: la promo debe respetar la selección hecha en
            // el ticket. Misma fuente que ya usa "Eliminar": prioriza la
            // multi-selección (selectedOrderIndices) y si no hay, usa el
            // único índice seleccionado (selectedOrderIndex).
            selectedIndices={
              selectedOrderIndices.length > 0
                ? selectedOrderIndices
                : (selectedOrderIndex !== null ? [selectedOrderIndex] : [])
            }
            onApplyComplete={async (result) => {
              setShowPromoSelector(false);
              if (result.success) {
                mostrarMensaje(result.message);

                if (result.perProductoUpdates) {
                  // Se aplicó por producto (vía aplicar_descuento.php, el
                  // mismo endpoint que ya usa "Descuentos"). Se actualiza
                  // "order" localmente en cada índice afectado — mismo
                  // patrón que handleApplyDiscount — y a propósito NO se
                  // recarga el cheque desde el backend aquí: un refetch
                  // cheque-wide no debe pisar un cambio que ya sabemos que
                  // quedó bien aplicado por producto.
                  setOrder((prev) => {
                    const updates = new Map(result.perProductoUpdates.map((u) => [u.index, u.discount]));
                    return prev.map((item, index) => {
                      if (!updates.has(index)) return item;
                      const discount = updates.get(index);
                      return {
                        ...item,
                        discount,
                        total: calculateItemTotal({ ...item, discount }),
                      };
                    });
                  });
                  setOrderSummaryKey((prev) => prev + 1);
                } else {
                  // Promo aplicada a todo el pedido (sin selección): igual
                  // que en handleTransferPedidosComplete, recargar el
                  // cheque actualizado para reflejar el descuento a nivel
                  // de cuenta que aplicó aplicar_promo.php.
                  const chequesActualizados = await fetchCheques();
                  if (chequeSeleccionado) {
                    await cargarChequeActualizado(chequeSeleccionado, chequesActualizados);
                  }
                  setOrderSummaryKey((prev) => prev + 1);
                }

                setSelectedOrderIndex(null);
                setSelectedOrderIndices([]);
              }
            }}
            onClose={() => setShowPromoSelector(false)}
          />
        </>
      )}

      {showTransferPedidoSelector && (
        <>
          <div className="transfer-overlay-backdrop" onClick={() => setShowTransferPedidoSelector(false)} />
          <TransferPedidoSelector
            revenues={filteredRevenues}
            onTransferComplete={handleTransferPedidosComplete}
            onClose={() => setShowTransferPedidoSelector(false)}
            currentCheque={{
              numero_cheque: chequeSeleccionado,
              mesa: selectedSpace?.name,
              revenue: selectedRevenue?.name,
              revenue_id: selectedRevenue?.id
            }}
            currentOrder={order}
            propertyConfig={propertyConfig}
          />
        </>
      )}

      {showVentaOtroRevenueSelector && (
        <>
          <div className="transfer-overlay-backdrop" onClick={() => setShowVentaOtroRevenueSelector(false)} />
          <VentaOtroRevenueSelector
            revenues={filteredRevenues}
            currentRevenueId={selectedRevenue?.id}
            fetchOriginProducts={fetchOriginProducts}
            onSelectProduct={handleAddProductFromOtherRevenue}
            onClose={() => setShowVentaOtroRevenueSelector(false)}
            propertyConfig={propertyConfig}
          />
        </>
      )}

      {showReimprimirTicket && (
        <ReimprimirTicket
          onClose={() => setShowReimprimirTicket(false)}
          currentUser={currentUser}
          property={property}
          deviceConfig={deviceConfig}
          devices={devices}
          printTicket={printTicket}
          mostrarMensaje={mostrarMensaje}
        />
      )}
      
        {showCancelChequeModal && (
          <CancelChequeModal
            show={showCancelChequeModal}
            onClose={() => setShowCancelChequeModal(false)}
            onConfirm={handleCancelCheque}
            isLoading={isCancellingCheque}
          />
        )}

      {showFechaControl && (
        <ControlFecha
          show={showFechaControl}
          onClose={() => setShowFechaControl(false)}
          onChequeSeleccionado={handleChequeSeleccionado}
        />
      )}
      
      {showCorteDeCaja && (
        <CorteDeCaja
          show={showCorteDeCaja}
          onClose={() => setShowCorteDeCaja(false)}
          onChequeSeleccionado={handleChequeSeleccionado}
        />
      )}
      
      {showCorteAdmin && (
        <CorteAdmin
          show={showCorteAdmin}
          onClose={() => setShowCorteAdmin(false)}
          onChequeSeleccionado={handleChequeSeleccionado}
        />
      )}

      {showChequeList && (
        <ChequeList
          onChequeSeleccionado={handleChequeSeleccionado}
          revenueInicial={selectedRevenue?.name || "todos"}
        />
      )}

      {showRevenueSelector && (
        <RevenueSelector
          revenues={filteredRevenues}
          onSelectRevenue={handleSelectRevenue}
          onChequeClick={handleOccupiedTableClick}
          currentUser={currentUser}
          propertyConfig={propertyConfig}
        />
      )}

      
    
      
      {!showRevenueSelector && selectedRevenue && (
        <div className="pos-content">
          {/* CONTENEDOR PRINCIPAL DE 3 PANELES */}
          <div className="main-panels-container">
            {/* ✅ PANEL IZQUIERDO: MENÚ - CON CLASE CONDICIONAL */}
            <div className={`panel menu-panel ${activePanel === 'menu' ? 'active' : ''}`}>
              <div className="panel-header menu-panel-header">

                {/* Fila superior: botón back (móvil) + título de nivel actual */}
                <div className="menu-header-row">
                  {currentGroup && (
                    <button
                      className="mobile-back-btn"
                      onClick={currentCategory ? handleBackToCategories : handleBackToGroups}
                      aria-label="Volver"
                    >
                      <i className="bi bi-chevron-left"></i>
                    </button>
                  )}
                  <h3>
                    {!currentGroup
                      ? 'Menú'
                      : !currentCategory
                        ? (currentGroup.name || 'Categorías')
                        : (currentCategory.name || 'Productos')}
                  </h3>
                </div>

                {/* Pill de cliente quitado del header — ahora es FAB flotante */}

              </div>
              <div className="panel-content">
                {loadingProducts ? (
                  <p>Cargando productos...</p>
                ) : filteredProducts.length === 0 ? (
                  <p>No hay productos disponibles.</p>
                ) : (
                  <>
                    {!currentGroup && (
                      <GroupList
                        key={resetGroupList}
                        products={filteredProducts}
                        onSelectProduct={handleProductSelection}
                        propertyConfig={propertyConfig}
                        property={property}
                      />
                    )}
                    {currentGroup && !currentCategory && (
                      <CategoryList
                        group={currentGroup}
                        products={filteredProducts}
                        onSelectCategory={handleSelectCategory}
                        onBack={handleBackToGroups}
                      />
                    )}
                    {currentGroup && currentCategory && (
                      <ProductList
                        group={currentGroup}
                        category={currentCategory}
                        products={filteredProducts}
                        onSelectProduct={handleProductSelection}
                        onBack={handleBackToCategories}
                      />
                    )}
                    {selectedRevenue.Barcode === "Y" && (
                      <BarcodeInventoryScanner
                        filteredProducts={filteredProducts}
                        selectedRevenueId={selectedRevenue.id}
                        onProductScanned={handleProductScanned}
                      />
                    )}
                  </>
                )}
              </div>
            </div>
{/* ✅ PANEL CENTRAL: ASIENTOS DINÁMICOS - CON CLASE CONDICIONAL */}
<div className={`panel asientos-panel ${activePanel === 'clientes' ? 'active' : ''}`}>
  <div className="panel-header">
    <h3>Clientes</h3>
  </div>
  <div className="panel-content">
    {/* Renderizar RevenueInfo aquí, pero solo la parte de clientes */}
    {selectedRevenue && (
      <div className="asientos-simple">
        {clientCount > 0 && selectedRevenue?.Tipo !== 'Retail' && (
          <div className="clientes-seleccion-completa">
            <RevenueInfo
              selectedRevenue={selectedRevenue}
              selectedSpace={selectedSpace}
              clientCount={clientCount}
              selectedClient={selectedClient}
              setSelectedClient={setSelectedClient}
              chequeSeleccionado={chequeSeleccionado}
              property={property}
              currentUser={currentUser}
              setOrder={setOrder}
              mostrarMensaje={mostrarMensaje}
              setPaymentMethod={setPaymentMethod}
              order={order}
              deviceConfig={deviceConfig}
              devices={devices}
              printTicket={printTicket}
              generarQR={generarQR}
              customerNamePending={customerNamePending}
              // Props adicionales para el modo panel
              isPanelMode={true}
              allowedActions={allowedActions}
            />
          </div>
        )}

        {(!clientCount || clientCount === 0 || selectedRevenue?.Tipo === 'Retail') && (
          <div className="no-clientes-message">
            <p>No hay clientes disponibles</p>
            {selectedRevenue?.Tipo === 'Retail' && (
              <small>Los revenues tipo Retail no utilizan asientos</small>
            )}
          </div>
        )}
      </div>
    )}
  </div>
</div>

            {/* ✅ PANEL DERECHO: RESUMEN DEL PEDIDO - CON CLASE CONDICIONAL */}
            <div className={`panel resumen-panel ${activePanel === 'resumen' ? 'active' : ''}`}>
              <div className="panel-header resumen-panel-header">
                <h3>Resumen del Pedido</h3>
                {selectedSpace && (
                  <div className="space-info-mini">
                    <small>{selectedSpace.name}</small>
                  </div>
                )}
                {allowedActions.includes("Ordenar") && (
                  <button
                    type="button"
                    className="product-search-trigger-btn"
                    onClick={() => setShowProductSearchPanel(true)}
                    title="Buscar producto"
                    aria-label="Buscar producto"
                  >
                    <i className="bi bi-search"></i>
                  </button>
                )}
              </div>
              <div className="panel-content">
                <OrderSummary
                  key={orderSummaryKey}
                  order={order}
                  onSelectItem={(index) => setSelectedOrderIndex(index)}
                  onSelectMultiple={(indices) => setSelectedOrderIndices(indices)}
                  selectedIndex={selectedOrderIndex}
                  appliedPayments={appliedPayments}
                  onRemovePayment={(index) =>
                    handleRemovePayment(
                      appliedPayments,
                      setAppliedPayments,
                      index,
                      setSelectedPaymentIndex
                    )
                  }
                  selectedPaymentIndex={selectedPaymentIndex}
                  onSelectPayment={(index) => setSelectedPaymentIndex(index)}
                  onCalculateTotals={handleCalculateTotals}
                  selectedClient={selectedClient}
                  isLoading={isLoading}
                  property={property}
                  numeroCheque={chequeSeleccionado}
                  lastAddedSignal={lastAddedSignal}
                  onRequestNote={(index) => {
                    // Si el producto tocado forma parte de la selección
                    // múltiple activa, aplica a toda la selección; si no,
                    // solo a ese producto (mismo criterio que ya usan
                    // Cantidad/Descuentos con selectedOrderIndices).
                    const multi = selectedOrderIndices.length > 1 && selectedOrderIndices.includes(index);
                    openNoteFor(multi ? selectedOrderIndices : [index]);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODALES Y COMPONENTES FLOTANTES */}
      {showProductSearchPanel && (
        <ProductSearchPanel
          products={filteredProducts}
          onSelectProduct={(product) => handleProductSelection(product)}
          onClose={() => setShowProductSearchPanel(false)}
        />
      )}
      {showModifiersModal && selectedProduct && (
        <ModifiersModal
          modifiers={selectedProduct.modifiers || []}
          onClose={() => setShowModifiersModal(false)}
          onAddModifiers={(modifiers) => {
            handleAddProductToClient(selectedProduct, modifiers);
            setShowModifiersModal(false);
          }}
        />
      )}

      {showQuantityInput && (
  <QuantityInput
    currentQuantity={selectedOrderIndex !== null ? order[selectedOrderIndex]?.quantity : 1}
    item={selectedOrderIndex !== null ? order[selectedOrderIndex] : null}
    onConfirm={(quantity) => {
      if (!allowedActions.includes("Cantidad")) {
        mostrarMensaje("No tienes permiso para modificar cantidades.");
        return;
      }
      
      // Esta función debe actualizar TANTO el producto como sus modificadores
      setOrder(prevOrder => {
        const updatedOrder = [...prevOrder];
        const item = updatedOrder[selectedOrderIndex];
        
        if (item) {
          const oldQuantity = item.quantity || 1;
          const factor = quantity / oldQuantity;
          
          // Actualizar cantidad del producto
          item.quantity = quantity;
          
          // Actualizar cantidad de los modificadores
          if (item.modifiers && item.modifiers.length > 0) {
            item.modifiers = item.modifiers.map(mod => ({
              ...mod,
              quantity: Math.max(1, Math.round((mod.quantity || 1) * factor))
            }));
          }
          
          updatedOrder[selectedOrderIndex] = item;
        }
        
        return updatedOrder;
      });
      
      setShowQuantityInput(false);
    }}
    onClose={() => setShowQuantityInput(false)}
  />
)}

      {paymentMethod && paymentMethod.name && (
        <PaymentInputModal
          method={paymentMethod}
          total={calculatedTotals.totalCalculado || 0}
          restante={restante}
          isPagoCompleto={isPagoCompleto}
          appliedPayments={appliedPayments}
          onConfirm={(payment) => {
            const fullPayment = {
              method: paymentMethod,
              amount: payment.amount,
              tip: payment.tip || 0,
              folio: payment.folio || "",
              change: payment.change || 0,
              hotelData:payment.hotelData,
              roomNumber: payment.roomNumber,
              isHotelPayment: payment.isHotelPayment || false
            };
            handlePaymentConfirm(setAppliedPayments, fullPayment);
            setPaymentMethod(null);
          }}
          onClose={() => setPaymentMethod(null)}
          property={property}
          currentUser={currentUser}
          chequeSeleccionado={chequeSeleccionado}
          mostrarMensaje={mostrarMensaje}
          order={order}
          selectedRevenue={selectedRevenue}
          selectedSpace={selectedSpace}
          deviceConfig={deviceConfig}
          propertyConfig={propertyConfig}
          revenues={revenues}
        />
      )}
      
      {showOpenDiscountModal && (
        <OpenDiscountModal
          show={showOpenDiscountModal}
          onClose={() => setShowOpenDiscountModal(false)}
          onApplyDiscount={handleApplyOpenDiscount}
          product={order[selectedOrderIndex]}
          selectedItemIndex={selectedOrderIndex}
          order={order}
          currentUser={currentUser}
          currentProperty={property}
          setOrders={setOrder}
          setOpenDiscountModal={setShowOpenDiscountModal}
          orders={order}
          selectedOrderIndex={selectedOrderIndex}
          chequeId={chequeSeleccionado}
        />
      )}

      {showHotelModal && (
        <Hotel
          property={property}
          onClose={() => setShowHotelModal(false)}
          mostrarMensaje={mostrarMensaje}
          currentUser={currentUser}
        />
      )}

      {/* ================= PICKER DE CLIENTE MÓVIL ================= */}
      {showMobileClientPicker && clientCount > 0 && selectedRevenue?.Tipo !== 'Retail' && (
        <>
          {/* Backdrop — toca fuera para cerrar */}
          <div
            className="mobile-client-picker-backdrop"
            onClick={() => setShowMobileClientPicker(false)}
          />
          {/* Panel deslizable desde abajo */}
          <div className="mobile-client-picker">
            <div className="mobile-client-picker-handle" />
            <div className="mobile-client-picker-header">
              <span>Seleccionar Asiento</span>
              <button
                className="mobile-client-picker-close"
                onClick={() => setShowMobileClientPicker(false)}
                aria-label="Cerrar"
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <div className="mobile-client-picker-list">
              {Array.from({ length: clientCount }, (_, i) => {
                const itemsForClient = order.filter(item => item.client === i).length;
                return (
                  <button
                    key={i}
                    className={`mobile-client-picker-item ${selectedClient === i ? 'selected' : ''}`}
                    onClick={() => {
                      setSelectedClient(i);
                      setShowMobileClientPicker(false);
                      mostrarMensaje(`Asiento ${i + 1} seleccionado`);
                    }}
                  >
                    <div className="mobile-client-picker-item-info">
                      <span className="mobile-client-picker-item-name">
                        Asiento {i + 1}
                      </span>
                      {itemsForClient > 0 && (
                        <span className="mobile-client-picker-item-badge">
                          {itemsForClient} {itemsForClient === 1 ? 'producto' : 'productos'}
                        </span>
                      )}
                    </div>
                    {selectedClient === i && (
                      <i className="bi bi-check-circle-fill mobile-client-picker-check"></i>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </main>

    {/* ================= BARRA DE NAVEGACIÓN MÓVIL — MENÚ + RESUMEN ================= */}
    {/* Clientes ya no es una pestaña: se accede desde el pill selector en el header del menú */}
    {!showRevenueSelector && selectedRevenue && (
      <div className="mobile-nav-bar">
        <button
          className={`mobile-nav-btn ${(activePanel === 'menu' || activePanel === 'clientes') ? 'active' : ''}`}
          onClick={() => setActivePanel('menu')}
          aria-label="Menú"
        >
          <i className="bi bi-grid"></i> MENÚ
        </button>

        <button
          className={`mobile-nav-btn ${activePanel === 'resumen' ? 'active' : ''}`}
          onClick={() => setActivePanel('resumen')}
          aria-label="Resumen del pedido"
        >
          <i className="bi bi-receipt"></i> RESUMEN
        </button>
      </div>
    )}

    {/* ================= FOOTER CON TODAS LAS ACCIONES ================= */}
    {!showRevenueSelector && selectedRevenue && (
      <footer className="fixed-footer" ref={footerRef}>
        <div className="footer-content">
          {/* ✅ SECCIÓN PEDIDOS */}
          {showOrderSection && (
            <div className="footer-section pedidos-section active">
              <div className="footer-section-header">
                <h4>Acciones del Pedido</h4>
                <button 
                  className="close-section-btn"
                  onClick={() => setShowOrderSection(false)}
                >
                  ✕
                </button>
              </div>
              <div className="pedidos-buttons">
                {/* Manteniendo todos tus botones originales pero en el footer */}
                {allowedActions.includes("Inicio") && (
                  <button 
                    className="pedido-button"
                    onClick={handleBackToGroups}
                  >
                    Inicio
                  </button>
                )}
                {allowedActions.includes("Ordenar") && (
                  <button
                    className={`pedido-button${isOrdering ? ' ordering' : ''}`}
                    onClick={handleSaveOrder}
                    disabled={
                      !order.some((product) => !product.isFromDB) ||
                      !selectedRevenue ||
                      isOrdering
                    }
                  >
                    {isOrdering
                      ? <><i className="bi bi-arrow-repeat spin-icon" /> Enviando...</>
                      : 'Ordenar'
                    }
                  </button>
                )}
                {allowedActions.includes("Cantidad") && (
                  <button
                    className="pedido-button"
                    onClick={() =>
                      selectedOrderIndex !== null && setShowQuantityInput(true)
                    }
                    disabled={selectedOrderIndex === null}
                  >
                    Cantidad
                  </button>
                )}
                {allowedActions.includes("Clientes") && (
                  <button
                    className="pedido-button"
                    onClick={updateClientCount}
                    disabled={!selectedRevenue || selectedRevenue.Tipo === "Retail" || !chequeSeleccionado}
                  >
                    <i className="bi bi-people-fill"></i> Clientes
                  </button>
                )}
                {selectedRevenue?.Tipo !== "Retail" && allowedActions.includes("Transferir Cliente") && (
                  <button
                    className="pedido-button"
                    onClick={() => handleTransferClient(order[selectedOrderIndex]?.client)}
                    disabled={
                      !chequeSeleccionado ||
                      selectedOrderIndex === null
                    }
                  >
                    Transferir Cliente
                  </button>
                )}
                {allowedActions.includes("T Mesa") && (
                  <button
                    className="pedido-button"
                    onClick={() => setShowTransferTableSelector(true)}
                    disabled={!selectedRevenue || selectedRevenue.Tipo === "Retail" || !chequeSeleccionado}
                  >
                    Transferencia Mesa
                  </button>
                )}
                {allowedActions.includes("Transferir Mesero") && (
                  <button
                    className="pedido-button"
                    onClick={() => setShowTransferMeseroSelector(true)}
                    disabled={!selectedRevenue || selectedRevenue.Tipo === "Retail" || !chequeSeleccionado}
                  >
                    Transferencia Mesero
                  </button>
                )}
                {allowedActions.includes("Transferir Pedido") && (
                  <button
                    className="pedido-button"
                    onClick={() => setShowTransferPedidoSelector(true)}
                    disabled={!selectedRevenue || !chequeSeleccionado || order.length === 0}
                  >
                    📦 Transferir Pedido
                  </button>
                )}
                {allowedActions.includes("Eliminar") && (() => {
                  // Índices efectivos: prioriza multi-selección, si no usa el índice simple
                  const idxs = selectedOrderIndices.length > 0
                    ? selectedOrderIndices
                    : (selectedOrderIndex !== null ? [selectedOrderIndex] : []);
                  const canDelete = idxs.length > 0 && idxs.every(i => !order[i]?.isFromDB);
                  return (
                    <button
                      className="pedido-button"
                      disabled={!canDelete}
                      onClick={() => {
                        if (!canDelete) return;
                        const idxSet = new Set(idxs);
                        setOrder(prev => prev.filter((_, i) => !idxSet.has(i)));
                        setSelectedOrderIndex(null);
                        setSelectedOrderIndices([]);
                      }}
                    >
                      Eliminar{idxs.length > 1 ? ` (${idxs.length})` : ''}
                    </button>
                  );
                })()}
                {allowedActions.includes("Descuentos") && (
                  <button
                    className="pedido-button discount-button"
                    onClick={() => {
                      console.log("Botón Descuentos clicado");
                      setShowDiscountModal(true);
                    }}
                    disabled={
                      selectedOrderIndex === null ||
                      !order[selectedOrderIndex]?.isFromDB
                    }
                  >
                    Descuentos
                  </button>
                )}
                {allowedActions.includes("Quitar descuento") && (
                  <button
                    className="pedido-button"
                    onClick={handleRemoveDiscount}
                    disabled={
                      selectedOrderIndex === null ||
                      (!order[selectedOrderIndex]?.discount &&
                        !order[selectedOrderIndex]?.monto_descuento)
                    }
                  >
                    Quitar Desc./Promo
                  </button>
                )}
                {allowedActions.includes("CrearQR") && (
                  <button
                    className="pedido-button qr-button"
                    onClick={generarQR}
                    disabled={order.length === 0 || !chequeSeleccionado}
                  >
                    QR Pedido
                  </button>
                )}
                {allowedActions.includes("Venta de Otro Revenue") && (
                  <button
                    className="pedido-button"
                    onClick={() => setShowVentaOtroRevenueSelector(true)}
                    disabled={!selectedRevenue}
                  >
                    🔀 Venta de Otro Revenue
                  </button>
                )}
                {allowedActions.includes("Consulta Hotel") && (
                  <button
                    className="pedido-button"
                    onClick={() => setShowHotelModal(true)}
                  >
                    Consulta Hotel
                  </button>
                )}
                {allowedActions.includes("Descuentos") && (
                  <button
                    className="pedido-button"
                    onClick={() => {
                      console.log("Botón Descuento Abierto clicado");
                      setShowOpenDiscountModal(true);
                    }}
                    disabled={order.length === 0 || !chequeSeleccionado}
                  >
                    Descuento Abierto
                  </button>
                )}
                {/* Botón "Promo" — independiente de Descuentos/Descuento Abierto,
                    catálogo lista_promos.json (ver PromoSelector.js). */}
                {allowedActions.includes("Promo") && (
                  <button
                    className="pedido-button"
                    onClick={() => setShowPromoSelector(true)}
                    disabled={order.length === 0 || !chequeSeleccionado}
                  >
                    Promo
                  </button>
                )}
                {/* ── Tiempos de Servicio ─────────────────────────────── */}
                {allowedActions.includes("Tiempo") && (
                  <button
                    className="pedido-button"
                    onClick={() => setShowTiempoPanel(true)}
                    disabled={order.length === 0}
                  >
                    ⏱ Tiempo
                  </button>
                )}
                {allowedActions.includes("Imprimir") && (
                  <button
                    className="pedido-button destacado"
                    onClick={handlePrintTicketWithSegmento}
                    disabled={order.length === 0}
                  >
                    Imprimir Ticket
                  </button>
                )}
                {allowedActions.includes("Separar Cuentas") && chequeSeleccionado && clientCount >= 2 && (
                  <button
                    className="pedido-button separar-cuentas-btn"
                    onClick={() => setShowSepararCuentasModal(true)}
                  >
                    Separar Cuentas
                  </button>
                )}
              </div>
            </div>
          )}

          {/* ✅ SECCIÓN PAGOS */}
          {showPaymentsSection && allowedActions.includes("Pagos") && (
            <div className="footer-section pagos-section active">
              <div className="footer-section-header">
                <h4>Pagos</h4>
                <button 
                  className="close-section-btn"
                  onClick={() => setShowPaymentsSection(false)}
                >
                  ✕
                </button>
              </div>
              <div className="pagos-buttons">
                {payments.map((method) => (
                  allowedActions.includes(method.name) && (
                    <button
                      key={method.id}
                      className="pago-button"
                      onClick={() => setPaymentMethod(method)}
                      disabled={
                        !chequeSeleccionado || isPagoCompleto || restante <= 0
                      }
                    >
                      {method.name}
                    </button>
                  )
                ))}
                {allowedActions.includes("Confirmar Pago") && (
                  <button
                    className="pago-button confirm-pago"
                    onClick={confirmOrder}
                    disabled={
                      !chequeSeleccionado ||
                      (!isPagoCompleto && totalPedido > 0) ||
                      isConfirmingPayment
                    }
                  >
                    {isConfirmingPayment
                      ? <><i className="bi bi-arrow-repeat spin" /> Procesando pago...</>
                      : "Confirmar Pago"}
                  </button>
                )}
                {allowedActions.includes("Quitar Pago Seleccionado") && (
                  <button
                    className="pago-button remove-payment"
                    onClick={() =>
                      handleRemovePayment(
                        appliedPayments,
                        setAppliedPayments,
                        selectedPaymentIndex,
                        setSelectedPaymentIndex
                      )
                    }
                    disabled={selectedPaymentIndex === null}
                  >
                    Quitar Pago
                  </button>
                )}
              </div>
            </div>
          )}

          {/* ✅ SECCIÓN EXPRESS */}
{/* ✅ SECCIÓN EXPRESS - UN SOLO BLOQUE CON SCROLL HORIZONTAL */}
{showExpressSection && allowedActions.includes("Express") && (
  <div className="footer-section express-section active">
    <div className="footer-section-header">
      <h4>⚡ EXPRESS</h4>
      <button className="close-section-btn" onClick={() => setShowExpressSection(false)}>✕</button>
    </div>
    
    {/* CONTENEDOR CON SCROLL HORIZONTAL */}
    <div className="express-scroll-container">
      <div className="express-single-block">
        
        {/* ===== ORDEN ===== */}
        <div className="express-category">
          <div className="category-title">ORDEN</div>
          <div className="buttons-row">
            {allowedActions.includes("Inicio") && (
              <button className="express-btn" onClick={handleBackToGroups}><i className="bi bi-house"></i> Inicio</button>
            )}
            {allowedActions.includes("Ordenar") && (
              <button
                className={`express-btn success${isOrdering ? ' ordering' : ''}`}
                onClick={handleSaveOrder}
                disabled={!order.some(p => !p.isFromDB) || isOrdering}
              >
                {isOrdering
                  ? <><i className="bi bi-arrow-repeat spin-icon" /> Enviando...</>
                  : <><i className="bi bi-floppy" /> Ordenar</>
                }
              </button>
            )}
            {allowedActions.includes("Cantidad") && (
              <button className="express-btn" onClick={() => selectedOrderIndex !== null && setShowQuantityInput(true)} disabled={selectedOrderIndex === null}><i className="bi bi-123"></i> Cantidad</button>
            )}
            {/* Requerimiento "Notas": acceso siempre visible en la barra de
                Acciones del Pedido (no hay que navegar al menú ni desplazarse
                hasta el final del pedido). Aplica a la selección múltiple si
                existe, si no al producto único seleccionado. */}
            {allowedActions.includes("Ordenar") && (() => {
              const noteTargetIdxs = selectedOrderIndices.length > 0
                ? selectedOrderIndices
                : (selectedOrderIndex !== null ? [selectedOrderIndex] : []);
              // Bloqueado (deshabilitado de verdad, no solo con aviso) si no
              // hay nada seleccionado o si algo de lo seleccionado ya fue
              // enviado/cancelado — mismo criterio que el ícono por producto.
              const noteBlocked = noteTargetIdxs.length === 0 || noteTargetIdxs.some(
                (i) => isOrderItemSent(order[i]) || isOrderItemVoidedOrCancelled(order[i])
              );
              return (
                <button
                  className="express-btn"
                  onClick={() => openNoteFor(noteTargetIdxs)}
                  disabled={noteBlocked}
                  title={noteBlocked && noteTargetIdxs.length > 0 ? "Uno o más productos seleccionados ya fueron enviados" : undefined}
                >
                  <i className="bi bi-sticky"></i> Nota
                </button>
              );
            })()}
            {allowedActions.includes("Eliminar") && (
              <button className="express-btn danger" onClick={() => removeFromOrder(order, setOrder, selectedOrderIndex, setSelectedOrderIndex)} disabled={selectedOrderIndex === null || order[selectedOrderIndex]?.isFromDB}><i className="bi bi-trash3"></i> Eliminar</button>
            )}
            {allowedActions.includes("Clientes") && selectedRevenue?.Tipo !== "Retail" && (
              <button className="express-btn" onClick={updateClientCount} disabled={!chequeSeleccionado}><i className="bi bi-people"></i> Clientes</button>
            )}
            {allowedActions.includes("Transferir Cliente") && selectedRevenue?.Tipo !== "Retail" && (
              <button className="express-btn" onClick={() => handleTransferClient(order[selectedOrderIndex]?.client)} disabled={!chequeSeleccionado || selectedOrderIndex === null}><i className="bi bi-person-arrow-right"></i> T. Cliente</button>
            )}
            {allowedActions.includes("T Mesa") && selectedRevenue?.Tipo !== "Retail" && (
              <button className="express-btn" onClick={() => setShowTransferTableSelector(true)} disabled={!chequeSeleccionado}><i className="bi bi-arrow-left-right"></i> T. Mesa</button>
            )}
            {allowedActions.includes("Transferir Mesero") && selectedRevenue?.Tipo !== "Retail" && (
              <button className="express-btn" onClick={() => setShowTransferMeseroSelector(true)} disabled={!chequeSeleccionado}><i className="bi bi-person-badge"></i> T. Mesero</button>
            )}
            {allowedActions.includes("Transferir Pedido") && (
              <button className="express-btn" onClick={() => setShowTransferPedidoSelector(true)} disabled={!chequeSeleccionado || order.length === 0}><i className="bi bi-box-arrow-right"></i> T. Pedido</button>
            )}
            {allowedActions.includes("Venta de Otro Revenue") && (
              <button className="express-btn" onClick={() => setShowVentaOtroRevenueSelector(true)} disabled={!selectedRevenue}><i className="bi bi-shuffle"></i> Otro Revenue</button>
            )}
            {allowedActions.includes("CrearQR") && (
              <button className="express-btn" onClick={generarQR} disabled={order.length === 0 || !chequeSeleccionado}><i className="bi bi-qr-code"></i> QR</button>
            )}
            {allowedActions.includes("Imprimir") && (
              <button className="express-btn success" onClick={handlePrintTicketWithSegmento} disabled={order.length === 0}><i className="bi bi-printer"></i> Imprimir</button>
            )}
          </div>
        </div>

        {/* ===== DESCUENTOS ===== */}
        {(allowedActions.some(action => ["Descuentos", "Quitar descuento", "Promo"].includes(action)) || promos.some(p => allowedActions.includes(p.name))) && (
          <div className="express-category">
            <div className="category-title">DESCUENTOS</div>
            <div className="buttons-row">
              {allowedActions.includes("Descuentos") && (
                <button className="express-btn warning" onClick={() => setShowDiscountModal(true)} disabled={selectedOrderIndex === null || !order[selectedOrderIndex]?.isFromDB}><i className="bi bi-tag"></i> Descuentos</button>
              )}
              {allowedActions.includes("Quitar descuento") && (
                <button
                  className="express-btn danger"
                  onClick={handleRemoveDiscount}
                  disabled={
                    selectedOrderIndex === null ||
                    (!order[selectedOrderIndex]?.discount &&
                      !order[selectedOrderIndex]?.monto_descuento)
                  }
                >
                  <i className="bi bi-x-circle"></i> Quitar Desc./Promo
                </button>
              )}
              {allowedActions.includes("Descuentos") && (
                <button className="express-btn warning" onClick={() => setShowOpenDiscountModal(true)} disabled={order.length === 0 || !chequeSeleccionado}><i className="bi bi-percent"></i> Desc. Abierto</button>
              )}
              {allowedActions.includes("Promo") && (
                <button className="express-btn warning" onClick={() => setShowPromoSelector(true)} disabled={order.length === 0 || !chequeSeleccionado}><i className="bi bi-gift"></i> Promo</button>
              )}
              {promos.map((promo) => (
                allowedActions.includes(promo.name) && (
                  <button
                    key={promo.id}
                    className={`express-btn ${order.some(item => item.discount?.id === promo.id) ? 'discount-active' : ''}`}
                    onClick={() => handleToggleExpressDiscount(promo)}
                    disabled={!order.length || !promo.products.some(p => order.some(item => Number(p.id) === Number(item.item)))}
                  >
                    {promo.name}
                    {promo.price && ` (${promo.price}%)`}
                  </button>
                )
              ))}
              {allowedActions.includes("Descuentos") && pendingDiscounts.length > 0 && (
                <button className="express-btn success" onClick={processExpressDiscounts}><i className="bi bi-check-circle"></i> Aplicar</button>
              )}
              {allowedActions.includes("Quitar descuento") && order.some(item => item.discount) && (
                <button className="express-btn danger" onClick={handleRemoveAllExpressDiscounts}><i className="bi bi-x-circle"></i> Quitar Todos</button>
              )}
            </div>
          </div>
        )}

        {/* ===== PAGOS ===== */}
        {payments.some(p => allowedActions.includes(p.name)) && (
          <div className="express-category">
            <div className="category-title">PAGOS</div>
            <div className="buttons-row">
              {payments.map((method) => (
                allowedActions.includes(method.name) && (
                  <button
                    key={method.id}
                    className="express-btn primary"
                    onClick={() => setPaymentMethod(method)}
                    disabled={!chequeSeleccionado || isPagoCompleto || restante <= 0}
                  >
                    {method.name}
                  </button>
                )
              ))}
              {allowedActions.includes("Confirmar Pago") && (
                <button
                  className={`express-btn success${isConfirmingPayment ? ' ordering' : ''}`}
                  onClick={confirmOrder}
                  disabled={!chequeSeleccionado || (!isPagoCompleto && totalPedido > 0) || isConfirmingPayment}
                >
                  {isConfirmingPayment
                    ? <><i className="bi bi-arrow-repeat spin" /> Procesando...</>
                    : <><i className="bi bi-check-circle"></i> Confirmar</>}
                </button>
              )}
              {allowedActions.includes("Quitar Pago Seleccionado") && (
                <button className="express-btn danger" onClick={() => handleRemovePayment(appliedPayments, setAppliedPayments, selectedPaymentIndex, setSelectedPaymentIndex)} disabled={selectedPaymentIndex === null}><i className="bi bi-x-circle"></i> Quitar Pago</button>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  </div>
)}

          {/* ✅ SECCIÓN ADMIN */}
          {showAdminSection && allowedActions.includes("Admin") && (
            <div className="footer-section admin-section active">
              <div className="footer-section-header">
                <h4>Administración</h4>
                <button 
                  className="close-section-btn"
                  onClick={() => setShowAdminSection(false)}
                >
                  ✕
                </button>
              </div>
              <div className="admin-buttons">
                {allowedActions.includes("Void") && (
                  <button
                    className="admin-button void-btn"
                    onClick={handleVoid}
                    disabled={
                      selectedOrderIndex === null ||
                      !order[selectedOrderIndex]?.isFromDB
                    }
                  >
                    Void
                  </button>
                )}
                {allowedActions.includes("Corte de Caja") && (
                  <button
                    className="admin-button"
                    onClick={() => setShowCorteDeCaja(true)}
                  >
                    <i className="bi bi-calendar-check"></i> Corte de Caja
                  </button>
                )}
                {allowedActions.includes("Administra") && (
                  <button
                    className="admin-button"
                    onClick={() => setShowCorteAdmin(true)}
                  >
                    <i className="bi bi-calendar-check"></i> Administra
                  </button>
                )}
                {allowedActions.includes("Control de Fechas") && (
                  <button
                    className="admin-button"
                    onClick={() => setShowFechaControl(true)}
                  >
                    <i className="bi bi-calendar-check"></i> Control de Fechas
                  </button>
                )}
                {allowedActions.includes("Ver Logs") && (
                  <button className="admin-button">Ver Logs</button>
                )}
                {allowedActions.includes("Configuración del Sistema") && (
                  <ResetConfig
                    setDeviceConfig={setDeviceConfig}
                    setProperty={setProperty}
                    setCurrentProperty={setCurrentProperty}
                    setIsConfigLoaded={setIsConfigLoaded}
                  />
                )}
                {allowedActions.includes("Actualizar Datos") && (
                  <button
                    className="admin-button"
                    onClick={handleReloadData}
                  >
                    Actualizar Datos
                  </button>
                )}
                {allowedActions.includes("Reimprimir Ticket") && (
                  <button
                    className="admin-button"
                    onClick={() => setShowReimprimirTicket(true)}
                  >
                    Reimprimir Ticket
                  </button>
                )}
               {allowedActions.includes("Cancelar Cheque") && (
                  <button
                    className="admin-button cancel-cheque-btn"
                    onClick={() => setShowCancelChequeModal(true)}
                  >
                    <i className="bi bi-trash3"></i> Cancelar Cheque
                  </button>
                )}

                {/* ===== REPORTES — movido desde header, protegido por permiso "Reportes" ===== */}
                {allowedActions.includes("Reportes") && (
                  <button
                    className="admin-button reporte-button-admin"
                    onClick={() => setShowReportesSection(prev => !prev)}
                  >
                    <i className="bi bi-bar-chart-line"></i> Reportes
                  </button>
                )}

                {/* ===== SALIR PANTALLA COMPLETA — requiere permiso "Salir Pantalla Completa" ===== */}
                                {allowedActions.includes("Salir Pantalla Completa") && (
                  <button
                    className="admin-button"
                    onClick={() => {
                      const isAndroid = /android/i.test(navigator.userAgent || '');
                      if (isAndroid) {
                        // Android: cerrar la app o finalizar la Activity
                        // 1. JS bridge explícito (si el WebView lo expone)
                        if (window.Android && typeof window.Android.closeApp === 'function') {
                          window.Android.closeApp();
                        } else if (window.Android && typeof window.Android.finish === 'function') {
                          window.Android.finish();
                        } else {
                          // 2. window.close() dispara onCloseWindow en WebChromeClient → finish()
                          window.close();
                        }
                      } else {
                        // Web / Windows / otros: salir de pantalla completa (comportamiento original)
                        if (document.exitFullscreen) {
                          document.exitFullscreen();
                        } else if (document.webkitExitFullscreen) {
                          document.webkitExitFullscreen();
                        } else if (document.mozCancelFullScreen) {
                          document.mozCancelFullScreen();
                        }
                        setIsFullscreen(false);
                      }
                    }}
                  >
                    <i className="bi bi-fullscreen-exit"></i> Salir Pantalla
                  </button>
                )}
              </div>
            </div>
          )}

          {/* ✅ BOTONES DEL FOOTER CUANDO NO HAY SECCIÓN ACTIVA */}
         
        </div>
      </footer>
    )}

    {/* MODALES Y COMPONENTES ADICIONALES */}
    {showOpenPriceModal && selectedProduct && (
      <OpenPriceModal
        product={selectedProduct}
        onClose={() => setShowOpenPriceModal(false)}
        onConfirm={handleAddOpenPrice}
      />
    )}
    {showOpenNoteModal && (selectedProduct || noteTargetIndices.length > 0) && (
      <OpenNoteModal
        onClose={() => {
          setShowOpenNoteModal(false);
          setNoteTargetIndices([]);
        }}
        onConfirm={handleAddOpenNote}
        expressOptions={propertyConfig?.opennoteexpres || []}
        productName={
          noteTargetIndices.length === 1 && order[noteTargetIndices[0]]
            ? order[noteTargetIndices[0]].name
            : (selectedOrderIndex !== null && order[selectedOrderIndex]
                ? order[selectedOrderIndex].name
                : "")
        }
        quantity={
          noteTargetIndices.length === 1 && order[noteTargetIndices[0]]
            ? Math.abs(order[noteTargetIndices[0]].quantity) || 1
            : undefined
        }
        count={noteTargetIndices.length > 1 ? noteTargetIndices.length : undefined}
      />
    )}

    {/* ── Panel de Tiempos de Servicio ──────────────────────────────────── */}
    {showTiempoPanel && (
      <TiempoServicioPanel
        show={showTiempoPanel}
        onClose={() => setShowTiempoPanel(false)}
        order={order}
        tiempos={
          propertyConfig?.tiempos && propertyConfig.tiempos.length > 0
            ? propertyConfig.tiempos
            : [
                { Tiempo: "Tiempo 1" },
                { Tiempo: "Tiempo 2" },
                { Tiempo: "Tiempo 3" },
                { Tiempo: "Tiempo 4" },
              ]
        }
        onAssignTiempo={handleAssignTiempo}
        preselectedIndex={selectedOrderIndex}
      />
    )}

    {/* FAB DE CLIENTE — solo móvil, reemplaza flechas sin funcionalidad */}
    {!showRevenueSelector && selectedRevenue && selectedRevenue.Tipo !== 'Retail' && clientCount > 0 && (
      <button
        className="mobile-client-fab"
        onClick={() => setShowMobileClientPicker(true)}
        aria-label={`Asiento ${(selectedClient ?? 0) + 1} — cambiar`}
      >
        <i className="bi bi-people-fill"></i>
        <span className="mobile-client-fab-num">{(selectedClient ?? 0) + 1}</span>
      </button>
    )}
  </div>
</div>
            }
          />
        </Routes>
      ) : deviceConfig.Rol === "KIC" ? (
  (console.log("=== RENDERIZANDO KIOSCOS EN APP.JS ===", { 
    property, 
    deviceConfig, 
    depto: deviceConfig?.Depto 
  }),
  <Kioscos
    deviceConfig={deviceConfig}
    currentUser={currentUser}
    onLogout={handleLogout}
    property={property}
  />)
      ) : (
        <div>
          <div className="user-info">
            <p>Bienvenido, {currentUser.nombre}</p>
            <h1>
              {deviceConfig.Propiedad} - {deviceConfig.name}
            </h1>
            <button onClick={handleLogout} className="logout-button">
              <FaSignOutAlt /> Salir
            </button>
          </div>
          <KDS kdsId={deviceConfig.name} />
        </div>
      )}
    </div>
  );
};

// En AppWrapper:
const AppWrapper = () => {
  // Get the property and currentUser from localStorage
  const property = localStorage.getItem("currentProperty");
  const currentUserData = localStorage.getItem("currentUser");
  const currentUser = currentUserData ? JSON.parse(currentUserData) : null;

  // Usar IP importada directamente
  return (
    <PropertyProvider>
      <LanguageProvider 
        property={property} 
        currentUser={currentUser}
        IP={IP} // ✅ Ahora IP está definida
      >
        <App />
        <VirtualKeyboard />
        <KdsReadyNotifier />
      </LanguageProvider>
    </PropertyProvider>
  );
};

export default AppWrapper;
